import {
  CARD_BALANCE,
  CATOLE_BLAST_EFFECT,
  cardBeamWidthFor,
  cardSphereRadiusFor,
  FIREBALL_BLAST_EFFECT,
  SUPER_CANNONS_BACK_BPS as PVP_SUPER_CANNONS_BACK_BPS,
  SUPER_CANNONS_LATERAL_BPS as PVP_SUPER_CANNONS_LATERAL_BPS,
} from '@kenocyber/game-domain';

/**
 * O dobro da escala do canhão da formação, como na caça (gate V28H.J).
 *
 * O mesmo número que o dono aprovou a olhar para a arena da caça, e o mesmo
 * papel: um canhão temporário lê-se como armamento PESADO, e o tamanho é o que
 * o diz sem precisar de arte nova.
 */
const PVP_SUPER_CANNON_ART_SCALE = 2;

/**
 * **O tamanho dos dois dardos de gelo**, em raios de casco (gate V28H.W).
 *
 * Quase um casco de diâmetro. Começou a um terço disto e o dono não conseguiu
 * ver o que estava a voar: uma silhueta que tem de dizer "bola" ou "estrela" -
 * e são essas duas formas que separam as duas cartas - não sobrevive a ser
 * desenhada com dezena e meia de pixels a atravessar o ecrã.
 *
 * **Três vezes isso** (segunda decisão do dono): ele passou a ser maior do que o
 * casco que persegue. Foi pedido depois de ver o primeiro tamanho no ecrã, e o
 * argumento anterior - "não tapar aquilo que vai atingir" - cede à decisão de
 * quem está a olhar: um dardo que não se vê não diz nada, e este tem de dizer se
 * é uma bola ou uma estrela.
 */
const ICE_BOLT_HULLS = 2.85;

import {
  SAW_VECTOR_SCALE,
  sawBladeRadius,
  sawBladeStep,
  sawDirection,
  sawOrbitRadius,
} from '@kenocyber/game-domain';
import {
  ATOMIC_BLAST_EFFECT,
  CANNON_SHOT_PAYLOAD,
  CYCLONE_CUT_EFFECT,
  LIFESTEAL_HEAL_EFFECT,
  MISSILE_EFFECT,
  PVP_BOMB_BODY_RADIUS_MU,
  PVP_BOOSTERS,
  PVP_CATOLE_BODY_RADIUS_MU,
  PVP_DEFAULT_COMBAT_PROFILE,
  PVP_FIREBALL_REACH_BPS,
  PVP_LEVIATHAN_ENTRY_BPS,
  PVP_MIN_VISUAL_SCALE,
  PVP_MISSILE_BODY_RADIUS_MU,
  PVP_RUNE_PICKUP_RADIUS_MU,
  PVP_RUNE_SPEED_BPS,
  PVP_SHIP_RADIUS_MU,
  PVP_SOLAR_LANCE_REACH_BPS,
  PVP_SOLAR_LANCE_WIDTH_BPS,
  pvpEffectiveSpeedBps,
  pvpHullRadiusMu,
  pvpHullScale,
  VITAL_HEAL_EFFECT,
} from '@kenocyber/game-domain/pvp';
import {
  type AssetManifest,
  formatHealthMilli,
  PVP_FALLBACK_APPEARANCE,
  PVP_HEALTH_MILLI_PER_UNIT,
  type PvpAppearance,
  type SkinModality,
  type SkinRuntimeManifest,
  type SkinVisualPlan,
} from '@kenocyber/shared';
import Phaser from 'phaser';
import {
  clampPvpZoom,
  PVP_IDLE_INTENT,
  PVP_ZOOM_DEFAULT,
  PVP_ZOOM_MIN,
  PvpIntentCadence,
  PvpIntentSource,
  pvpAimVector,
  zoomAfterWheel,
} from '../pvp/controls';
import {
  pvpHullRotation,
  pvpHullTargetFromMotion,
  pvpHullTargetFromMovement,
  pvpTurnHull,
} from '../pvp/hull-heading';
import { type PvpPredictedPoint, PvpPrediction } from '../pvp/prediction';
import {
  PROJECTILE_TRAIL_ALPHA,
  PROJECTILE_TRAIL_EVERY_FRAMES,
  PROJECTILE_TRAIL_LIFESPAN_MS,
  PROJECTILE_TRAIL_MAX,
  PVP_CADENCE_SHOT_LENGTH,
  PVP_CADENCE_SHOT_THICKNESS,
  type PvpDashGauge,
  pvpDashGauge,
  pvpProjectileColour,
  pvpProjectileColourIsFallback,
  pvpStreakScale,
} from '../pvp/projectile-art';
import {
  PVP_BAR_ENEMY_TINT,
  PVP_BAR_FRIENDLY_TINT,
  PVP_BAR_HEIGHT,
  PVP_BAR_LIFT,
  PVP_BAR_WIDTH,
  PVP_MU_PER_UNIT,
  PVP_RUNE_PROJECTED_MAX,
  PVP_RUNE_PROJECTED_MIN,
  PVP_RUNE_TINT,
  PvpCorrectionWatch,
  type PvpIdentity,
  type PvpParticipantRole,
  type PvpRuneIndicatorKind,
  type PvpWorldArena,
  pvpActiveRuneEffect,
  pvpCameraBounds,
  pvpCameraHome,
  pvpCameraTarget,
  pvpHeading,
  pvpHealthFraction,
  pvpIdentityMark,
  pvpLocalIdentity,
  pvpMissileDiff,
  pvpProjectileDiff,
  pvpProjectileHeading,
  pvpRuneIndicator,
  pvpRuneIndicatorScale,
  pvpRunePulseAlpha,
  pvpRuneTurnsWithHeading,
  pvpShipRole,
  pvpVisualMargin,
  pvpWorldArena,
  pvpWorldUnits,
} from '../pvp/scene-model';
import {
  PVP_CANNON_WORLD_UNITS,
  PVP_HULL_WORLD_UNITS,
  pvpCannonArt,
  pvpCannonMounts,
  pvpCannonPivotAngle,
  pvpCannonSpriteRotation,
  pvpHullOf,
  pvpMountInWorld,
  pvpShipArt,
} from '../pvp/ship-art';
import {
  type PvpParticipantId,
  type PvpRenderFrame,
  type PvpRenderShip,
  PvpViewBuffer,
} from '../pvp/view';
import { assetUrl, findStaticAsset } from './assets';
import { AtomicBlastSystem } from './atomic-blast';
import {
  ATOMIC_BOMB_BODY_SPAN,
  atomicBombRollFrame,
  ensureAtomicBombTexture,
} from './atomic-bomb-art';
import { BastionFx } from './bastion-fx';
import { bleedAudioFrom } from './bleed-audio';
import {
  BLEED_BURST_POOL_CEILING,
  BleedBurstSystem,
  BleedMarkSystem,
  type BleedMarkTarget,
} from './bleed-mark';
import type { GameBridge } from './bridge';
import {
  CARD_FIREBALL_BODY_FRACTION,
  CATOLE_SHAKE_INTENSITY,
  CATOLE_SHAKE_MS,
  type CardBlastBody,
  CardBlastFx,
} from './card-blast-fx';
import { cardEffectTint } from './card-colour';
import { MagicCircleFx, SolarLanceFx } from './card-fx';
import {
  bladeOrbitRadius,
  cardVisualFlags,
  chargingSphereGeometry,
  LEVIATHAN_MIN_HULLS,
  summonDiameter,
} from './card-vfx';
import {
  COMBAT_TEXT_BLEED_STYLE,
  COMBAT_TEXT_FONT_PX,
  COMBAT_TEXT_HEAL_COLOR,
  COMBAT_TEXT_HEAL_OFFSET_PX,
  COMBAT_TEXT_LIFE_MS,
  COMBAT_TEXT_REFLECT_VALUE_PX,
  COMBAT_TEXT_RISE_PX,
  COMBAT_TEXT_STYLE,
} from './combat-text';
import { CycloneCutSystem } from './cyclone-cut';
import { type ConfirmedHeal, healNumbersFrom } from './heal-numbers';
import { HealTally } from './heal-tally';
import {
  ensureMissileBodyTexture,
  ensureMissileFlameTexture,
  ensureMissileTrailTexture,
  MISSILE_BLAST_RADIUS_FACTOR,
  MISSILE_BODY_SPAN,
  MISSILE_FLAME_ANCHOR,
  MISSILE_FLAME_SCALE,
  MISSILE_TRAIL_FADE_MS,
  MISSILE_TRAIL_INTERVAL_MS,
  MISSILE_TRAIL_SCALE,
  MissileBlastSystem,
  missileFlameFrame,
  missileRotation,
} from './missile-art';
import { CLOAK_HULL_ALPHA, MovementFx } from './movement-fx';
import {
  POISON_MARK_MIN_PX,
  POISON_MARK_TARGET_RATIO,
  PoisonMarkSystem,
  type PoisonMarkTarget,
  poisonBandHeight,
} from './poison-mark';
import {
  DASH_ORIGIN_X,
  ensureDashTexture,
  ensureShockwaveTexture,
  ensureStreakTexture,
} from './projectile-art';
import { PvpArenaVfx } from './pvp-arena-vfx';
import { ReflectFlashSystem } from './reflect-flash';
import { ReflectNumberSystem } from './reflect-number';
import { PvpRuneRenderer } from './rune-renderer';
import { SkinRenderer } from './SkinRenderer';
import { SawBladeSystem, type SawBladeView } from './saw-blade';
import { requestSharedTexture } from './shared-texture-requests';
import { SKIN_VFX_BASE_URL, SKIN_VFX_TEXTURES } from './skin-art';
import { resolveSkinContent } from './skin-resolution';
import { fetchSkinManifest } from './skin-runtime';
import { MalwareCastFx, MarkCastFx, StatusFx } from './status-fx';
import {
  buildSummonBody,
  requestDroneTexture,
  requestLeviathanTexture,
  SUMMON_DRONE_TEXTURE,
  SUMMON_LEVIATHAN_BODY_TEXTURE,
  SUMMON_LEVIATHAN_SHOT_COLOUR,
  setSummonBoardingArrows,
  setSummonHealth,
  spinSummonRotor,
} from './summon-art';
import { VitalHealFlashSystem } from './vital-heal-flash';
import { vitalHealPresentation } from './vital-heal-presentation';

/**
 * A chave da folha do chefe do mapa 1, emprestada ao Drone (gate V28H.P).
 *
 * Um prefixo proprio e nao `boss`: a caca usa essa chave para o chefe a serio,
 * e as duas cenas partilham o registo de texturas de um `Phaser.Game`.
 */
const PVP_DRONE_TEXTURE = SUMMON_DRONE_TEXTURE;

/**
 * **O Leviatã Aéreo é um helicóptero 3D, e a hélice gira** (gate V28H.X).
 *
 * Duas tentativas antes desta falharam à vista do dono. A primeira era um
 * rectângulo com uma barra atravessada; a segunda emprestava a chapa do pet
 * Apache, que é um bom helicóptero mas é o de outra coisa - preto sobre uma
 * arena preta, com seis canos pintados por cima. A ordem foi: "tens de criar um
 * novo helicóptero, 3D, e pôr na cena, com a hélice a girar".
 *
 * O que se desenha agora é um render a sério: um modelo three.js próprio,
 * fotografado em NOVE poses pelo pipeline de recursos - o mesmo caminho pelo
 * qual os pets viram imagem - com o rotor um pouco mais adiante em cada uma. A
 * folha 3x3 que sai daí é o formato dos chefes, e a cena anima-a.
 *
 * Ver `packages/asset-pipeline/src/summons/leviathan-model.ts` para o porquê de
 * a fotografia ser a resposta certa e não um segundo motor 3D sobre a arena.
 */
const PVP_LEVIATHAN_BODY_TEXTURE = SUMMON_LEVIATHAN_BODY_TEXTURE;

/*
 * **Os ids dos recursos, a hélice e as medidas da chapa vivem agora em
 * `summon-art.ts`** (gate V28I.P).
 *
 * Com a arte que os usa, e uma vez: o mundo aberto desenha as mesmas duas
 * invocações, e dois sítios a nomear os mesmos ficheiros seriam dois sítios
 * para um deles ficar por renomear. As razões de cada número - porque a hélice
 * é uma imagem e não um efeito, porque a chapa vem sem rotor, e a que ritmo ele
 * gira - estão lá, ao lado do que as cumpre.
 */

/**
 * **O vermelho néon dos seis canhões** (decisão do dono, gate V28H.X).
 *
 * O mesmo das luzes da chapa: a salva tem de se ler como saída DAQUELA máquina,
 * e não da nave que está lá dentro. A textura é a mesma do tiro de qualquer
 * casco - o gerador da caça pinta-a na cor que lhe derem - portanto isto é uma
 * cor, e não uma arte nova.
 */
const PVP_LEVIATHAN_SHOT_COLOUR = SUMMON_LEVIATHAN_SHOT_COLOUR;

/**
 * **Quanto um tiro de CADÊNCIA é desenhado maior** (decisão do dono, V28H.X).
 *
 * **Quatro vezes ao comprido e quase o dobro de espessura**, em duas passagens:
 * o dono viu o primeiro aumento - dobro e metade - e pediu outro por cima,
 * "mais 100% para os lados e 30% para cima/baixo". Os números escritos aqui são
 * o resultado das duas contas, e não uma multiplicação escondida por trás de
 * uma variável: 2 x 2 e 1,5 x 1,3.
 *
 * Aqui e não no calibre - a função que o calcula serve os dois modos, e a caça
 * não pediu nada - portanto é a ARENA que magnifica, e o traço continua a ser o
 * mesmo desenho, com a mesma cor e a mesma origem.
 *
 * Só o DESENHO. O raio de colisão de um tiro é da sala, e nada nesta linha o
 * toca: um tiro maior continua a acertar exactamente onde acertava.
 */
/* Os dois factores vivem em `pvp/projectile-art.ts`, partilhados com o mundo aberto (gate V28I.CF.3). */

/**
 * **A proporção da chapa**: ela é mais alta do que larga (gate V28H.X).
 *
 * A arte tem 1024x1536, e desenhá-la num quadrado esmagava-a. O diâmetro que a
 * sala decide é o COMPRIMENTO - o eixo do nariz à cauda - e a largura sai desta
 * fracção, que é a da imagem.
 */

/** Quanto a hélice é maior do que o comprimento da chapa: ela cobre as asas. */

/**
 * **O nariz da arte aponta para CIMA**, e a mira aponta para a direita.
 *
 * O ângulo do contentor é `atan2(ay, ax)`, que é zero a apontar para leste; a
 * chapa do Apache tem os lemes em baixo e o nariz em cima. Sem este quarto de
 * volta o helicóptero voaria de lado - é a mesma correcção que a caça faz ao
 * sprite dela, com o mesmo número.
 */

/**
 * The duel, drawn.
 *
 * ## It is a third scene on the SAME game
 *
 * Handed to `Phaser.Game` at boot beside the hunt and the mining ring, active
 * from the first frame and drawing nothing until it is told to - the pattern
 * `MiningScene` already establishes and the reason there is exactly one canvas
 * and one WebGL context for the life of the tab. Opening a duel is a bridge
 * MESSAGE, never `scene.start`, never a second renderer.
 *
 * ## It decides nothing
 *
 * Positions, health, aim, projectiles and the result are the room's. This scene
 * samples an authoritative buffer and draws what is in it. The one thing it
 * produces is INTENT - which keys are down, where the pointer is, whether fire
 * is held - and even that is numbered by the connection rather than here.
 *
 * There is no pet, no drop and no reward anywhere in it, and it never reads an
 * inventory: the hull and the battery it draws come from the profile the SERVER
 * signed into the welcome, for BOTH seats. A client that dressed a ship from
 * its own idea of what somebody owns would be guessing at something the other
 * player can see.
 *
 * ## What it refuses to draw
 *
 * A projectile the newest snapshot did not carry, health it computed itself, a
 * shot pointed at the other ship rather than along its own velocity, and
 * anything at all between two worlds that a correction says were never
 * continuous.
 */

export interface PvpSceneOptions {
  readonly bridge: GameBridge;
  readonly manifest: AssetManifest;
}

/**
 * The hull drawn while the real one is still loading.
 *
 * `preload()` runs before the welcome exists, so which ship a seat flies is not
 * known yet. This is the placeholder that holds the frame until the profile's
 * own texture arrives - never a stand-in for a ship somebody actually owns.
 */
const HULL_ASSET_ID = 'ship1';
const HULL_TEXTURE = 'pvp-hull';

/**
 * World units. Presentation sizes; nothing here is a collision radius.
 *
 * They live in `scene-model.ts` now, beside the lift, because what the rune
 * marks may and may not reach is proved against this exact rectangle without a
 * canvas - and a second copy of 70x8 would eventually disagree with the one
 * being drawn.
 */
/**
 * Quanto a etiqueta do nome fica ACIMA da barra de vida (gate V28H.R).
 *
 * Em unidades de mundo, e nao escalado com o casco: a barra tambem nao e, e as
 * duas tem de continuar coladas uma a outra em qualquer tamanho de nave.
 */
const NAME_TAG_LIFT = 14;

/**
 * **Quanto o nickname OCUPA para cima** (gate V28H.W).
 *
 * Treze pixels de letra mais o contorno, e o texto tem origem em baixo - ele
 * cresce a partir de `NAME_TAG_LIFT` para cima. Quem quiser desenhar acima do
 * nome tem de somar isto, e é exactamente o que faltava ao selo do Malware: ele
 * saiu por cima das letras.
 *
 * Escrito ao lado do sítio que cria o texto, para os dois números mudarem
 * juntos.
 */
const NAME_TAG_HEIGHT = 17;
const BAR_WIDTH = PVP_BAR_WIDTH;
const BAR_HEIGHT = PVP_BAR_HEIGHT;
/**
 * How far a health bar floats above its ship, at 1x.
 *
 * MULTIPLIED by the visual scale wherever it is used. A fixed lift is legible
 * on a 52-unit hull and sits ON TOP of a 156-unit one - visible in the 3x
 * capture, where the bar was drawn across the ship rather than above it. The
 * bar itself does not grow: a ship twice the size does not deserve a health bar
 * twice as wide, it deserves the same bar further out of the way.
 *
 * It lives in `scene-model.ts` now, because the team mark has to be provably
 * clear of it and a second copy of the number would eventually disagree.
 */
const BAR_LIFT = PVP_BAR_LIFT;

const DEPTH = {
  floor: 0,
  booster: 5,
  pad: 6,
  obstacle: 10,
  /**
   * The wake, under everything it is a wake of.
   *
   * Residue: it says where a shot HAS been, so it must never be mistaken for
   * one, and it must never sit over the cue that says a ship is a copy. Under
   * the first.
   */
  projectileTrail: 18,
  /**
   * The cue that says a ship is a copy, UNDER the shots and under the copy.
   *
   * Below the projectiles on purpose: it is the only drawn thing in the arena
   * that annotates something without being part of it, so a shot crossing an
   * halo is drawn under everything it annotates. Below the
   * own hull and battery too, which is the second half of "the cue never covers
   * the art" - the first half being geometric, since the figure's radius is
   * outside the hull's half-diagonal at every drawn size.
   */
  /**
   * The shot itself: one sprite, the hunt's own dash texture.
   *
   * There is nothing between this and {@link DEPTH.ship} any more. The shot used
   * to be a pair - a tinted halo rectangle with a white core rectangle drawn one
   * step over it - because a tint MULTIPLIES and one rectangle could not be both
   * saturated and white. A baked texture has no such problem: the near-white
   * core, the coloured body and the hot tip are painted into it, so the whole
   * bolt is one object and the layering survives.
   */
  projectile: 20,
  /**
   * Below the ships.
   *
   * Two steps rather than one, because a ship has a battery: its
   * barrels must sit above its own hull for the same reason a real ship's do,
   * and both must stay under every real hull in the arena.
   */
  ship: 30,
  cannon: 31,
  /**
   * The team mark, ABOVE the hull and its battery and below the bar.
   *
   * Above, because an outline under the art would be swallowed by the very ship
   * it identifies at 3x. Below the bar, because health is the one thing nothing
   * may cross.
   */
  identity: 35,
  /**
   * The rune indicator, one step above the team mark and still under the bar.
   *
   * Two marks, two bands, and they cannot collide: the indicator's geometry
   * starts outside the identity radius, so the ordering here decides nothing
   * about which is legible - it only states which is on top if a future size
   * ever brings them together. Both are above the art they annotate and neither
   * crosses health.
   */
  rune: 36,
  bar: 40,
} as const;

/**
 * The wake, in the four numbers that bound it.
 *
 * ## What it is
 *
 * The hunt's own `projectile-streak` lozenge, emitted behind a shot along the
 * heading it had BEFORE this frame, from one emitter shared by every projectile
 * in the arena. Never a sprite per shot, never a configuration built per frame,
 * and never anything round: the brief forbids a trail made of little white
 * balls.
 *
 * ## Why a cadence rather than every frame
 *
 * Ninety shots emitting on every frame is 5400 particles a second, and at the
 * hunt's lifespan that is well over a thousand alive at any instant for
 * something whose entire job is to be faint. One in three frames, at a shorter
 * life than the hunt's, holds it near two hundred - which is measured rather
 * than asserted, in `projectile-performance.spec.ts`.
 *
 * ## And why a hard ceiling on top of that
 *
 * Because a cadence bounds the STEADY state and not the worst one: a correction
 * that respawns ninety shots at once, or a future room that fires faster, would
 * both climb past it. {@link PROJECTILE_TRAIL_MAX} is the backstop. It can only
 * ever drop RESIDUE - the shots, their cores, the ships, the bars
 * and the indicators are authoritative and are never reduced for a frame budget.
 */
/* Os números vivem em `pvp/projectile-art.ts`, partilhados com o mundo aberto (gate V28I.CF.3). */

/*
 * What the renderer follows a ship BY is `PvpParticipantId`, imported above.
 *
 * A string rather than a seat, and an id rather than an array index. A frame is
 * a list and a list reorders; keying sprites by position means that the first
 * time the server sends its ships in another order - or one dies and is dropped
 * - every hull inherits somebody else's trail, heading and health bar.
 *
 * In a 1x1 the id IS the seat ('a' or 'b'), so nothing about the existing duel
 * changes: the same objects, the same colours, the same positions. It used to
 * be declared here; it lives in `pvp/view.ts` now because the client state and
 * the render frame both speak it, and two declarations of one idea drift.
 */

interface HealthBar {
  readonly back: Phaser.GameObjects.Rectangle;
  readonly fill: Phaser.GameObjects.Rectangle;
}

export class PvpScene extends Phaser.Scene {
  static readonly KEY = 'pvp';

  private readonly options: PvpSceneOptions;
  /** Handed back on shutdown, so a dead scene stops hearing the bridge. */
  private readonly unsubscribers: (() => void)[] = [];

  /** Everything below is dormant until `pvp:open`. */
  private opened = false;
  /**
   * Whether a frame has thrown. Latched, and cleared only by opening again.
   *
   * A scene that failed once will fail again on the next frame, so the flag is
   * what turns sixty reports a second into one. Reset by `pvp:open` rather than
   * by `pvp:close`, so a player who leaves and comes back gets a working arena
   * rather than a permanently dead one.
   */
  private failed = false;
  /** Which participant this browser is. The welcome's answer, never a choice. */
  private seat: PvpParticipantId | null = null;
  private arena: PvpWorldArena | null = null;

  private readonly view = new PvpViewBuffer();
  private readonly correction = new PvpCorrectionWatch();
  /**
   * O sistema do corte, partilhado com a caca e criado com esta cena.
   *
   * Nulo depois do `shutdown`. A cena so lhe entrega posicoes ja em pixeis:
   * ela nao sabe que item originou o efeito, e nao tem por onde saber.
   */
  private cycloneCuts: CycloneCutSystem | null = null;
  /**
   * O sistema das marcas corrosivas (gate V28G.M). Nulo fora de uma partida.
   *
   * Um objeto gráfico por CASCO envenenado, e nunca um por marca. A
   * profundidade é a das barras, porque as gotas ficam imediatamente acima
   * delas - e nunca sobre elas, sobre o nome ou sobre o casco.
   */
  private sawBlades: SawBladeSystem | null = null;
  private poisonMarks: PoisonMarkSystem | null = null;
  private bleedMarks: BleedMarkSystem | null = null;
  private bleedBursts: BleedBurstSystem | null = null;

  /** O sistema visual da explosão atômica. Nulo depois do `shutdown`. */
  /**
   * O brilho e o feixe de uma REFLEXÃO confirmada (gate V28G.O).
   *
   * Um sistema e não dois porque os dois nascem do mesmo facto e acabam juntos -
   * ver `reflect-flash.ts`. Reconstruído a cada abertura de arena, como todos os
   * outros: as texturas ficam no gestor da cena e não voltam a ser desenhadas.
   */
  private reflectFlash: ReflectFlashSystem | null = null;

  /**
   * O feixe e o brilho de uma CURA confirmada (gate V28G.U).
   *
   * Um sistema e não dois, pela mesma razão que a Reflexão tem um: as duas
   * formas nascem do mesmo facto - uma cura autoritativa - e a que sai depende
   * apenas de quem foi curado ter sido outra nave ou a própria.
   *
   * Reconstruído a cada abertura de arena, como todos os outros: a textura da
   * tira fica no gestor de texturas do jogo e não volta a ser desenhada.
   */
  private vitalHeals: VitalHealFlashSystem | null = null;

  /** A composição `REFLECT` + valor, com o seu próprio pool (gate V28G.O). */
  private reflectNumbers: ReflectNumberSystem | null = null;

  private atomicBlasts: AtomicBlastSystem | null = null;
  /**
   * **As duas bombas de CARTA: os corpos, as explosões e a esfera a carregar**
   * (gate V28H.U).
   *
   * A MESMA classe da caça, e é o ponto - a mesma que o dono aprovou lá. O
   * Turbilhão e a Lança já vieram inteiros por esta porta, e o argumento é o que
   * era: uma segunda arte escrita aqui seria uma segunda arte a divergir.
   *
   * Não é o sistema da Bomba Atômica e nunca lhe toca: a bomba da joia continua
   * exactamente como estava, com o corpo dela, a onda dela e o raio dela.
   */
  private cardBlasts: CardBlastFx | null = null;
  /**
   * As esferas a CARREGAR neste quadro, uma por casco no máximo (gate V28H.U).
   *
   * Uma lista reutilizada e esvaziada a cada quadro, e não uma alocação por
   * quadro: ela é preenchida enquanto os cascos são desenhados e entregue
   * inteira no fim, porque quem a recebe apaga as que já lá não vêm - entregá-la
   * casco a casco apagaria a do casco anterior.
   */
  private readonly chargingSpheres: { key: string; x: number; y: number; radius: number }[] = [];
  /** O fogo do míssil. Sem onda de choque, por contrato (gate V28G.L). */
  private missileBlasts: MissileBlastSystem | null = null;
  /**
   * Onde cada casco foi DESENHADO no último quadro (gate V28G.I).
   *
   * O `+VALOR` da cura nasce sobre a nave curada, e o evento não diz onde ela
   * está - ele diz onde o ALVO estava, que é outro sítio. Esta é a mesma fonte
   * por onde a cena já desenha o casco, registada quando o desenha.
   *
   * Um casco sem entrada é um casco que ainda não foi desenhado, e nesse caso
   * não nasce número nenhum: pô-lo num sítio adivinhado seria pior.
   */
  private readonly hullPositions = new Map<string, { x: number; y: number }>();
  /** O pool dos `+VALOR`, com o mesmo estilo dos números de combate da caça. */
  private readonly healPool: Phaser.GameObjects.Text[] = [];
  private readonly liveHealTexts = new Set<Phaser.GameObjects.Text>();
  /**
   * O pool dos números de DETONAÇÃO, separado do da cura (gate V28G.N).
   *
   * Separado porque o estilo é outro: 2,4× o tamanho, peso 900, halo néon. Um
   * texto emprestado ao pool da cura teria de trocar cinco propriedades à ida e
   * repô-las todas à volta, e a que alguém se esquecesse de repor ficava numa
   * cura qualquer, meia hora depois, sem ninguém saber porquê.
   */
  private readonly bleedTextPool: Phaser.GameObjects.Text[] = [];
  private readonly liveBleedTexts = new Set<Phaser.GameObjects.Text>();
  /**
   * **O `+TOTAL` de cada casco, e o total que ele mostra** (decisão final).
   *
   * Um texto por nave: enquanto o de um casco estiver vivo, as curas seguintes
   * dele somam-se ao número que já está no ecrã. Aqui isto pesa mais do que na
   * caça - um 3×3 tem seis cascos, e cada um pode estar a curar a toda a hora.
   *
   * Esvaziado em `clearHealTexts`, por onde passam as quatro fronteiras do
   * duelo: abertura da modalidade, fecho da arena, fecho da cena e correção do
   * mundo.
   */
  private readonly healTally = new HealTally<Phaser.GameObjects.Text>();

  /**
   * **A largura DESENHADA do casco de referência**, e a única definição dela.
   *
   * `PVP_HULL_WORLD_UNITS` sem escala: é o tamanho de uma nave com exatamente
   * {@link PVP_MAX_HEALTH} de vida máxima.
   *
   * ## Porque isto NÃO é o tamanho de um casco (gate V28G.R)
   *
   * Porque um casco tem o seu, e a pergunta "que tamanho tem esta nave" só se
   * responde com uma cadeira - {@link hullDiameterOf}. Este número serve as
   * poucas coisas que não pertencem a um casco nomeado: um corte que a sala
   * anuncia num PONTO sem dizer sobre quem, e o traço de um projétil, cujo
   * desenho o `CLAUDE.md` proíbe de mudar.
   *
   * Não é a vida atual de ninguém: um casco não encolhe ao ser atingido.
   */
  private referenceHullDiameter(): number {
    return PVP_HULL_WORLD_UNITS;
  }

  /**
   * O sistema da explosão, construído com o que ESTA cena tem (gate V28G.J).
   *
   * Uma função e não um literal repetido: ele é criado duas vezes - uma quando a
   * cena arranca e outra a cada abertura de arena - e duas cópias da mesma
   * configuração seriam duas coisas que podem divergir.
   *
   * A onda de choque reutiliza o anel partilhado de `projectile-art.ts`, que é o
   * mesmo que a caça usa. A profundidade fica entre o rasto e o projétil: uma
   * explosão pode cruzar as naves, nunca escondê-las.
   */
  private buildBlastSystem(): AtomicBlastSystem {
    return new AtomicBlastSystem(this as never, {
      depth: DEPTH.projectile - 1,
      waveTexture: ensureShockwaveTexture(this),
      blend: { add: Phaser.BlendModes.ADD, normal: Phaser.BlendModes.NORMAL },
    });
  }

  /**
   * O sistema de fogo do MÍSSIL, e ele não é o de cima.
   *
   * Não recebe textura de onda porque não tem camada de onda: o míssil não tem
   * área, e a defesa estrutural contra desenhar um círculo que pareça uma zona de
   * dano é não existir onda nenhuma para ligar. As quatro texturas de fogo são as
   * mesmas, reutilizadas do gestor de texturas.
   */
  private buildMissileBlastSystem(): MissileBlastSystem {
    return new MissileBlastSystem(this as never, {
      depth: DEPTH.projectile - 1,
      blend: { add: Phaser.BlendModes.ADD, normal: Phaser.BlendModes.NORMAL },
    });
  }

  /**
   * **Um clarão curto sobre um casco que a autoridade disse ter levado.**
   *
   * Sobre o SPRITE do casco, e é por isso que ele pode não existir: uma nave
   * cujo sprite ainda não foi construído - a aparência dela não chegou - não
   * pisca, e isso é melhor do que inventar um sprite para o efeito.
   *
   * Não é um dano nem um número. O número do golpe já viaja no seu próprio
   * caminho; isto diz apenas "esta nave estava dentro do círculo".
   */
  /**
   * Os dois sistemas da Reflexão, construídos juntos (gate V28G.O).
   *
   * ## As profundidades, e o que cada uma protege
   *
   * O brilho fica em `DEPTH.ship`: o sistema põe o núcleo meio degrau acima e o
   * halo meio degrau abaixo, portanto os dois ficam **por baixo do canhão (31),
   * do marcador de equipa (35) e da barra (40)**. É assim que o §7 - *«não cubra
   * barra, marcador de equipe ou canhão»* - fica cumprido pela ordenação em vez
   * de por uma condição.
   *
   * O feixe fica um degrau abaixo da barra: ele atravessa a arena e tem de se
   * ler por cima dos cascos, mas a vida é a única coisa que nada pode cruzar.
   *
   * ## E o deslocamento do número
   *
   * Meio casco mais a folga que o `+VALOR` já usa. É o que garante que a
   * composição nasce ACIMA do casco e não sobre ele. É o casco de REFERÊNCIA:
   * o sistema é construído uma vez para a arena inteira e o número flutua sobre
   * quem refletiu, que este construtor não sabe quem é. Um casco maior leva o
   * seu número um pouco mais perto, e nunca por cima da barra, que sobe com ele.
   */
  private buildReflectSystems(): void {
    this.reflectFlash?.destroy();
    this.reflectFlash = new ReflectFlashSystem(this as never, DEPTH.ship, DEPTH.bar - 1);
    /*
     * E o da CURA, às mesmas profundidades e pela mesma razão (gate V28G.U): o
     * brilho ocupa exatamente os pixels do casco, portanto tem de ficar por baixo
     * da barra, do marcador de equipa e dos canhões; e o feixe passa por cima dos
     * cascos e por baixo das barras, para nunca tapar uma leitura de vida.
     */
    this.vitalHeals?.destroy();
    this.vitalHeals = new VitalHealFlashSystem(this as never, DEPTH.ship, DEPTH.bar - 1);
    this.reflectNumbers?.destroy();
    this.reflectNumbers = new ReflectNumberSystem(
      this as never,
      DEPTH.bar,
      /*
       * **A folga acima do casco é a MESMA; o que cresceu foi o glifo**
       * (gate V28G.O.2).
       *
       * O valor tem origem no topo, portanto ele desce a partir daqui: dobrar a
       * letra sem mexer no deslocamento faria a composição descer quinze píxeis
       * sobre o casco, e o §7 pede que ela não cubra barra nem nave.
       *
       * Somar exatamente o que o glifo cresceu devolve a distância que havia -
       * dezanove píxeis entre o pé do número e a borda do casco de referência -
       * em vez de a afinar outra vez à mão.
       */
      this.referenceHullDiameter() / 2 +
        COMBAT_TEXT_HEAL_OFFSET_PX +
        (COMBAT_TEXT_REFLECT_VALUE_PX - COMBAT_TEXT_FONT_PX),
    );
  }

  /** Larga os dois. Chamado ao fechar a arena e ao desligar a cena. */
  private destroyReflectSystems(): void {
    this.reflectFlash?.destroy();
    this.reflectFlash = null;
    this.vitalHeals?.destroy();
    this.vitalHeals = null;
    this.reflectNumbers?.destroy();
    this.reflectNumbers = null;
  }

  /**
   * **Desenha uma cura que a autoridade confirmou** (gate V28G.U).
   *
   * ## Porque a geometria sai dos cascos DESENHADOS
   *
   * Porque o contrato manda o feixe começar e acabar nas *«bordas reais dos
   * cascos, respeitando a escala de cada nave»*, e as bordas reais são as que
   * este cliente está a desenhar neste quadro. Um casco de duelo é escalado pela
   * vida máxima desde o gate V28G.S, portanto não há um raio único que sirva os
   * seis - e as posições autoritativas do evento são de um tique que pode não ser
   * o que está no ecrã.
   *
   * A AUTORIDADE decide as identidades - quem curou, quem foi curado e quanto -
   * e nada aqui as infere. O que esta função faz com elas é geometria de quem
   * desenha.
   *
   * ## As duas formas, e nunca as duas ao mesmo tempo
   *
   * Uma autocura não tem duas pontas: desenhar um feixe de comprimento zero seria
   * desenhar nada, e inventar-lhe uma direção seria mentir. Por isso ela é um
   * brilho na silhueta, e uma cura de aliado é um feixe - e o `+TOTAL` verde
   * aparece nas duas, porque é ele que diz quanto.
   *
   * Um assento que este cliente nunca desenhou não tem casco, e aí não desenhar é
   * a única resposta honesta.
   */
  private drawVitalHeal(healer: string, target: string, appliedMilli: number): void {
    /*
     * A DECISÃO vive em `vital-heal-presentation.ts`, e não aqui.
     *
     * Quatro entradas, três saídas, e nenhuma delas precisa de um contexto WebGL
     * para ser verificada - portanto o que esta cena faz é obedecer. O que ela
     * acrescenta é a única coisa que só ela sabe: a GEOMETRIA dos cascos que
     * está a desenhar neste quadro.
     */
    const decision = vitalHealPresentation({
      healer,
      target,
      appliedMilli,
      drawn: new Set(this.hulls.keys()),
    });
    if (decision.kind === 'none') {
      return;
    }
    if (decision.kind === 'glow') {
      const hull = this.hulls.get(decision.hull as PvpParticipantId);
      if (!hull) {
        return;
      }
      this.vitalHeals?.glow({
        id: decision.hull,
        textureKey: hull.texture.key,
        x: hull.x,
        y: hull.y,
        rotation: hull.rotation,
        width: hull.displayWidth,
        height: hull.displayHeight,
      });
      return;
    }
    const from = this.hulls.get(decision.from as PvpParticipantId);
    const to = this.hulls.get(decision.to as PvpParticipantId);
    if (!from || !to) {
      return;
    }
    this.vitalHeals?.beam({
      id: `${decision.from}:${decision.to}`,
      fromX: from.x,
      fromY: from.y,
      /*
       * O raio DESENHADO de cada casco, e o menor dos dois eixos.
       *
       * Menor porque o corte tem de sair de dentro da silhueta em qualquer
       * direção: usar o maior deixaria o feixe a nascer fora de uma nave que não
       * é quadrada, com uma folga visível entre a ponta e o casco.
       */
      fromRadius: Math.min(from.displayWidth, from.displayHeight) / 2,
      toX: to.x,
      toY: to.y,
      toRadius: Math.min(to.displayWidth, to.displayHeight) / 2,
    });
  }

  private flashHull(seat: PvpParticipantId): void {
    const hull = this.hulls.get(seat);
    if (!hull) {
      return;
    }
    /*
     * Um tween que devolve o valor original no fim.
     *
     * `yoyo` e não dois tweens encadeados: dois tweens sobre o mesmo campo
     * podem sobrepor-se quando duas explosões apanham a mesma nave, e o segundo
     * a acabar deixaria o casco na cor errada para sempre.
     */
    this.tweens.add({
      targets: hull,
      alpha: 0.35,
      duration: 70,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => {
        hull.setAlpha(1);
      },
    });
  }
  private readonly intent = new PvpIntentSource();
  private readonly cadence = new PvpIntentCadence();
  /** False outside `running`, so nothing is said before ready or after ended. */
  private acceptingCommands = false;
  private zoom = PVP_ZOOM_DEFAULT;

  /**
   * The local ship's predicted position.
   *
   * Only ever the LOCAL one. The opponent is interpolated from frames the room
   * sent, and predicting it would be inventing a position for somebody whose
   * input this client cannot see.
   */
  private readonly prediction = new PvpPrediction();
  /** Real elapsed time between rendered frames, for time-based convergence. */
  private lastSampleAt: number | null = null;

  private floor: Phaser.GameObjects.Rectangle | null = null;
  private border: Phaser.GameObjects.Rectangle | null = null;
  /** The dark space outside the walls. Backdrop only: nothing lives there. */
  private outside: Phaser.GameObjects.Rectangle | null = null;
  private readonly obstacles: Phaser.GameObjects.Rectangle[] = [];
  /** O fundo e as estruturas de impulso (gate V28H.K). Dono das suas peças. */
  private readonly arenaVfx: PvpArenaVfx = new PvpArenaVfx(this, Phaser.BlendModes.ADD);
  private readonly hulls = new Map<PvpParticipantId, Phaser.GameObjects.Image>();
  /** O último valor publicado da oferta de embarque (gate V28H.X). */
  private boardOffered = false;
  /**
   * Which way each hull is pointing, in world radians.
   *
   * Presentation, and only here. The room has no hull angle and must not grow
   * one: nothing about it changes where a ship is, what it collides with,
   * where a shot is born or who wins.
   */
  private readonly hullHeading = new Map<PvpParticipantId, number>();
  /** Where each ship was drawn last, so an opponent motion can be read. */
  private readonly lastDrawnAt = new Map<PvpParticipantId, { x: number; y: number }>();
  /**
   * One sprite per mounted barrel, per seat.
   *
   * A list rather than the single rectangle this used to be, because a ship
   * draws every cannon its configuration mounts - the same formation the hunt
   * bolts to the same hull. Rebuilt only when the profile that produced it
   * changes, which is at most once per duel.
   */
  private readonly cannons = new Map<PvpParticipantId, Phaser.GameObjects.Image[]>();
  /**
   * **Os dois canhões temporários de cada casco** (gate V28H.J).
   *
   * Um pool de dois POR CADEIRA, criados na primeira vez que aquela nave os
   * monta e reutilizados para sempre: a habilidade liga e desliga várias vezes
   * numa partida, e criar-destruir dois `Image` a cada ciclo seria lixo por
   * minuto sem nada em troca.
   *
   * Desenhados para TODA a gente, e não só para o dono: eles disparam contra
   * quem está do outro lado, e um adversário que não os visse levaria tiros de
   * uma arma invisível.
   *
   * Sem arte nova: a MESMA textura do canhão que aquele casco já usa, ao dobro
   * da escala - exatamente como a caça faz.
   */
  private readonly superCannons = new Map<PvpParticipantId, Phaser.GameObjects.Image[]>();
  /** The profile each seat's sprites were last BUILT from, to notice a change. */
  private readonly builtFrom = new Map<PvpParticipantId, string>();
  /* ------------------------------------------------------------------ */
  /*  The skin adapter (ADR 0041)                                        */
  /* ------------------------------------------------------------------ */
  /**
   * What each seat is WEARING, and one renderer per seat that draws it.
   *
   * ## Keyed by seat, never by roster index
   *
   * `PvpParticipantId` is the authority's own name for a participant. The
   * roster is reordered by the room, participants are dropped when they die and
   * restored when a correction replaces the world, so an index is a different
   * person from one frame to the next - and a skin keyed by one would migrate
   * between ships mid-duel.
   *
   * ## Presentation, and nothing else
   *
   * A renderer here swaps the hull's TEXTURE and draws effect layers. It is
   * never consulted for a cannon, a mount, a muzzle, a projectile, a hitbox or
   * a damage number: `buildShipArt` builds the battery from `pvpCannonMounts`
   * and the frame loop places it from `pvpMountInWorld`, both of which run
   * exactly the same with a skin on and with a skin off.
   *
   * ## Two writers, and only one of them ships
   *
   * {@link PvpScene.applySkins} is the door, and it has exactly two callers.
   * The PRODUCT reaches it through {@link PvpScene.syncAuthoritativeSkins},
   * which reads `arena.appearance` - the server's answer, per participant - and
   * nothing else. The visual laboratory (`benchmark/`, an entry production does
   * not compile) calls it directly so it can hold a modality and a level of
   * detail still while it measures.
   *
   * They cannot fight over the map. The laboratory's fixture leaves every
   * `appearance.skin` null, and the authoritative sync only acts when the set
   * of ids the SERVER describes changes - which, for an all-null fixture, is
   * never. In the product there is no laboratory, so there is one writer and
   * one source: the wire.
   */
  private readonly skinAssignments = new Map<
    PvpParticipantId,
    { plan: SkinVisualPlan; textureKey: string }
  >();
  private readonly skins = new Map<PvpParticipantId, SkinRenderer>();
  /** Where each seat's hull was drawn last frame, for its trail's direction. */
  private readonly skinDrawnAt = new Map<PvpParticipantId, { x: number; y: number }>();
  /**
   * Which VFX profile this match's skins compile with. Set by `pvp:open`.
   *
   * `pvp1x1` until a team match says otherwise, because the duel is the shape
   * that existed first and a wrong guess there is the cheaper one: it draws the
   * full signature for two ships, which is what two ships get anyway.
   */
  private skinModality: SkinModality = 'pvp1x1';
  /**
   * The skin ids the AUTHORITY last described, by participant.
   *
   * Only the dressed ones: a participant wearing nothing has no entry, so the
   * empty map means "the server has not said anybody is wearing anything" -
   * which is also the state a duel with no skins stays in for its whole life,
   * and the state that keeps this dormant under the laboratory's fixture.
   *
   * It is what makes the per-frame check a comparison rather than work. Every
   * frame carries every profile; without this, resolving them would mean
   * compiling six plans sixty times a second for an answer that changes at most
   * once a match.
   */
  private readonly authoritativeSkins = new Map<PvpParticipantId, string>();
  /** The skin sidecar, fetched at most once per scene. */
  private skinManifestPromise: Promise<SkinRuntimeManifest | null> | null = null;
  /**
   * The sidecar once it has arrived, so a per-frame apply needs no await.
   *
   * Null until the fetch lands, and null forever in a build that ships no
   * skins - both of which resolve every id to nothing, which draws the ordinary
   * hull. There is no state here a caller has to distinguish.
   */
  private skinManifestValue: SkinRuntimeManifest | null = null;
  /** Skin PNGs reported missing, so a failure is said once and never per frame. */
  private readonly reportedMissingSkins = new Set<string>();
  /**
   * **A escala de CADA casco, por participante** (gate V28G.R).
   *
   * Era um número só para a arena inteira, e não podia continuar a sê-lo: a
   * vida máxima passou a ser uma propriedade de cada casco, e seis cadeiras
   * podem ter seis vidas diferentes. Uma escala partilhada desenharia todas
   * iguais e faria a hitbox de uma delas discordar do que está no ecrã.
   *
   * Preenchido quando a boas-vindas chega, do MESMO perfil por participante que
   * a sala declarou, e por `pvpHullScale` - a única curva que existe. Esta cena
   * não sabe que joia produziu a vida, não lê nível e não lê inventário: ela
   * desenha o resultado.
   */
  private readonly hullScales = new Map<string, number>();
  /**
   * A escala do casco desta cadeira, ou a da nave de referência.
   *
   * O recuo é `PVP_MIN_VISUAL_SCALE` e não um palpite: uma cadeira sem perfil é
   * uma cadeira sobre a qual a sala ainda não falou, e desenhá-la no tamanho da
   * arte é a única resposta que não inventa uma vida.
   */
  private hullScaleOf(seat: string): number {
    return this.hullScales.get(seat) ?? PVP_MIN_VISUAL_SCALE;
  }

  /** O diâmetro DESENHADO do casco desta cadeira, na única fonte que o define. */
  private hullDiameterOf(seat: string): number {
    return PVP_HULL_WORLD_UNITS * this.hullScaleOf(seat);
  }
  /** Textures asked for and not yet arrived, so nothing is requested twice. */
  private readonly pendingTextures = new Set<string>();
  private readonly bars = new Map<PvpParticipantId, HealthBar>();
  /**
   * **A etiqueta com o nome de cada casco** (gate V28H.R).
   *
   * Uma por cadeira, criada à primeira vez que faz falta e reposicionada
   * depois - nunca recriada. É uma das duas coisas que dizem de quem é uma
   * nave desde que a cor dos efeitos de carta deixou de o dizer; a outra é a
   * barra de vida logo por baixo dela.
   */
  private readonly nameTags = new Map<PvpParticipantId, Phaser.GameObjects.Text>();
  /** O anel de gelo/escudo, um por cadeira, escondido em vez de destruído. */
  private readonly cardAuras = new Map<PvpParticipantId, Phaser.GameObjects.Arc>();
  /**
   * **As composições das duas cartas que desenham no casco** (gate V28H.O).
   *
   * Uma por cadeira, e as MESMAS classes que a caça usa - não uma segunda
   * versão delas. Os rectângulos que aqui estavam foram reprovados pelo dono
   * exactamente por serem rectângulos, e a arte aprovada é a que `card-fx.ts`
   * desenha: um círculo mágico com anéis e símbolos, uma serra de várias
   * lâminas com furos transparentes, e uma lança com corona, faíscas e
   * dissipação.
   *
   * Criadas na primeira vez que um casco precisa delas: uma partida inteira sem
   * estas cartas não paga um único objecto por elas.
   */
  /**
   * **O Bastião de cada casco protegido** (gate V28H.U).
   *
   * A MESMA classe da caça, como o círculo e a lança: estrelas a orbitar e a
   * destacar-se, e o casco a cintilar em branco, dourado e néon. Aqui esteve um
   * anel, partilhado com o gelo, e o dono reprovou-o.
   *
   * Uma por cadeira e criada só quando faz falta: uma partida inteira sem esta
   * carta não paga um único objecto por ela.
   */
  private readonly cardBastions = new Map<PvpParticipantId, BastionFx>();
  /**
   * **A propulsão e o rasto de cada casco** (gate V28H.V).
   *
   * Uma por cadeira, criada só quando faz falta: as duas cartas que a acendem -
   * o Arranque e a Sobremarcha - podem não estar equipadas de todo, e uma
   * partida inteira sem elas não paga um único objecto por elas.
   */
  private readonly cardMovement = new Map<PvpParticipantId, MovementFx>();
  /**
   * **Os sinais de debuff de cada casco** (gate V28H.W).
   *
   * Uma por cadeira, criada só quando faz falta: uma partida inteira sem estas
   * três cartas não paga um único objecto por elas.
   */
  private readonly cardStatus = new Map<PvpParticipantId, StatusFx>();
  /** E o pulso de hack de quem LANÇA o Malware, que é do lançador. */
  private readonly malwareCasts = new Map<PvpParticipantId, MalwareCastFx>();
  /** E o pulso da Marca da Caveira, um por cadeira (gate V28H.X). */
  private readonly markCasts = new Map<PvpParticipantId, MarkCastFx>();
  /**
   * **A palavra INVISÍVEL sobre um casco encoberto** (gate V28H.V).
   *
   * Pedida pelo dono: a silhueta translúcida diz que alguma coisa mudou, e não
   * diz o QUÊ - podia ser uma nave a desligar-se, um efeito qualquer, um erro de
   * desenho. A palavra fecha essa dúvida enquanto o efeito durar, e desaparece
   * com ele.
   *
   * Uma por cadeira, escondida em vez de destruída: refazer a textura de um
   * texto por activação é o trabalho que este cache existe para evitar.
   */
  private readonly cloakTags = new Map<PvpParticipantId, Phaser.GameObjects.Text>();
  /** Quanto a palavra sobe ACIMA do nome, para as duas não se sobreporem. */
  private static readonly CLOAK_TAG_LIFT = 15;
  private readonly cardCircles = new Map<PvpParticipantId, MagicCircleFx>();
  private readonly cardLances = new Map<PvpParticipantId, SolarLanceFx>();
  /**
   * Como cada invocação foi construída: com o sprite, ou com a forma.
   *
   * A arte do chefe chega depois do primeiro quadro em que o drone existe, e
   * sem isto ele ficaria uma forma para sempre - ou seria refeito a cada
   * quadro depois de ela chegar, que é pior.
   */
  private readonly summonBuiltAs = new Map<number, 'sprite' | 'shape'>();
  /**
   * As invocações desenhadas, por id.
   *
   * Por ID e não por cadeira: um jogador pode ter mais do que uma ao mesmo
   * tempo, e a identidade de uma entidade é o que a sala lhe deu.
   */
  private readonly summonSprites = new Map<number, Phaser.GameObjects.Container>();
  /** The ring under the cursor while a destination is being chosen. Pooled. */
  private targetRing: Phaser.GameObjects.Arc | null = null;
  /** What the canvas cursor currently is, so it is only set when it changes. */
  private canvasCursor = false;
  /**
   * What draws the runes, or `null` before an arena has been built.
   *
   * ## What it replaced, and why nothing stands in for it
   *
   * There used to be a `padMarkers` array here: one `Arc` per pad, created with
   * the arena, sized to the pickup radius and only ever recoloured - dark when
   * the pad was empty, tinted when something grew on it. That made a rune a
   * COLOUR IN AN AREA, which is the language of a booster, and it left a disc
   * on the floor at every moment of a match including the ones where the pad
   * held nothing.
   *
   * It is gone, not hidden: an empty pad now draws zero objects, and the
   * renderer below only ever exists while the room says a rune does. Nothing
   * here keeps a reserved object, an invisible placeholder or a pooled circle
   * for it - `e2e/pvp-runes-visual/legacy-pad.spec.ts` refuses all three, by
   * geometry and by pixels.
   */
  private runeRenderer: PvpRuneRenderer | null = null;
  /**
   * One team mark per participant, drawn ONCE and afterwards only moved.
   *
   * `Graphics` rather than a sprite: the three shapes are outlines, and baking a
   * texture for each role at each drawn size would be nine textures for
   * something a stroke describes exactly. It is redrawn only when the role or
   * the size changes - at most twice in a duel - so no frame allocates here.
   */
  private readonly identities = new Map<PvpParticipantId, Phaser.GameObjects.Graphics>();
  /** What each mark was last DRAWN from, so an unchanged one is left alone. */
  private readonly identityBuiltFrom = new Map<PvpParticipantId, string>();
  /**
   * One rune indicator per participant, and a SECOND object rather than more
   * strokes on the team mark.
   *
   * They mean different things and they live different lives: a mark is created
   * with a participant and dies with them, and an indicator appears when the
   * room says an effect started and goes when the room says it stopped. Sharing
   * one `Graphics` would mean redrawing the identity every time a rune expired,
   * and any mistake in that redraw would take out the cue that says who is on
   * whose side - which is the one this arena cannot afford to lose.
   *
   * Created on the first frame a participant actually carries something, so six
   * ships that never pick anything up cost nothing at all.
   */
  private readonly runeMarks = new Map<PvpParticipantId, Phaser.GameObjects.Graphics>();
  /** Which rune, at which size, each indicator was last drawn for. */
  private readonly runeMarkBuiltFrom = new Map<PvpParticipantId, string>();
  /**
   * One sprite per shot: the hunt's neon dash, in its owner's cannon colour.
   *
   * It used to be a pair of `Rectangle`s - a tinted halo and a white core - and
   * the pair existed only because a tint multiplies, so one rectangle could not
   * be both saturated and white. `ensureDashTexture` bakes the colour in, which
   * is precisely why it exists, so the layering arrives in one object and the
   * duel draws the same bolt the hunt does.
   */
  private readonly shots = new Map<number, Phaser.GameObjects.Image>();
  /**
   * Um sprite por míssil vivo, pela identidade que a autoridade lhe deu.
   *
   * Um mapa próprio e não uma entrada em {@link PvpScene.shots}: são duas listas
   * no fio e duas entidades no jogo, e misturá-las faria a reconciliação de uma
   * apagar os sprites da outra.
   */
  private readonly missiles = new Map<number, Phaser.GameObjects.Image>();
  /**
   * A propulsão de cada míssil vivo, presa à traseira real dele.
   *
   * Um sprite e não um emitter: o contrato proíbe um emitter por míssil, e
   * trezentos deles seriam trezentos sistemas de partículas. Sai no mesmo
   * instante em que o corpo sai.
   */
  private readonly missileFlames = new Map<number, Phaser.GameObjects.Image>();
  /** Os sprites de chama livres. Um pool partilhado, nunca um por míssil. */
  private readonly missileFlamePool: Phaser.GameObjects.Image[] = [];
  /** As marcas da esteira, num pool que nunca aloca dentro de um quadro. */
  private readonly missileTrailPool: Phaser.GameObjects.Image[] = [];
  private readonly missileTrailLive: {
    sprite: Phaser.GameObjects.Image;
    remainingMs: number;
  }[] = [];
  /** Milissegundos até à próxima marca, por míssil. */
  private readonly missileTrailCooldown = new Map<number, number>();
  /** Last drawn position per shot, so a heading comes from real movement. */
  private readonly shotTrail = new Map<number, { x: number; y: number }>();
  /**
   * The heading each shot was drawn at LAST frame, and its wake's countdown.
   *
   * The streak marks where the shot has been, so it is emitted along the
   * previous heading rather than this one - the same reasoning `HuntScene` uses
   * for its own trail. Cleared with the projectiles, so no direction can be
   * carried across a world the arena does not follow from.
   */
  private readonly shotWake = new Map<number, { heading: number; countdown: number }>();
  /**
   * ONE emitter for every wake in the arena.
   *
   * Created on the first frame a shot actually needs one and destroyed with the
   * duel - never one per projectile, and never per frame. It is a `ParticleEmitter`
   * on this scene's display list, which is why `closeView` destroys it: the
   * lifecycle census counts zero duel objects after a close, and an emitter left
   * behind is a duel object.
   *
   * The TEXTURE it draws is `projectile-streak`, generated by
   * `game/projectile-art.ts` and shared with the hunt. This scene must never
   * destroy it, and neither may it destroy the `projectile-dash-*` textures the
   * shots wear: they are cached in Phaser's texture manager for the life of the
   * tab, by colour, and both modes draw from the same cache.
   */
  private trailEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  /**
   * The scale every shot is drawn at, derived once from the arena's own radius.
   *
   * Recomputed with the welcome and never per shot: `projectileRadius` is a
   * property of the room, so ninety shots share one answer.
   */
  private dashGauge: PvpDashGauge | null = null;
  /**
   * Seats whose appearance could not be resolved, reported ONCE each.
   *
   * A missing profile is a real state and a frame is sixty a second: without
   * this, one participant the welcome has not described yet would fill the
   * console with the same sentence until it did. Cleared with the duel.
   */
  private readonly unresolvedShotOwners = new Set<PvpParticipantId>();

  /**
   * O rato mexeu-se em qualquer sítio da janela, e não só sobre a tela.
   *
   * Converte as coordenadas da página para as da TELA - a caixa dela dá o
   * desvio e a escala, e as duas mudam com o tamanho da janela - e entrega-as
   * ao mesmo caminho que o ponteiro do Phaser usa.
   */
  private readonly onWindowPointerMove = (event: PointerEvent) => {
    if (!this.acceptingCommands) {
      return;
    }
    const canvas = this.game.canvas;
    if (!canvas) {
      return;
    }
    const box = canvas.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) {
      return;
    }
    this.aimAtScreen(
      ((event.clientX - box.left) * this.scale.width) / box.width,
      ((event.clientY - box.top) * this.scale.height) / box.height,
    );
  };

  private readonly onBlur = () => this.clearIntent();
  private readonly onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      this.clearIntent();
    }
  };

  constructor(options: PvpSceneOptions) {
    /*
     * ACTIVE from boot, drawing nothing until it is told to.
     *
     * Starting inactive would mean calling `scene.start` later, which is scene
     * management this deliberately avoids: a dormant scene costs one `update`
     * that returns on its first line and cannot disturb the arena behind it.
     */
    super({ key: PvpScene.KEY, active: true });
    this.options = options;
  }

  preload(): void {
    /*
     * Its own texture key, from the same manifest.
     *
     * Not the hunt's `ship`, which is whichever hull that player has equipped -
     * a duel must look the same from both sides, and reaching into another
     * scene's key would also make this one depend on the hunt having preloaded.
     */
    try {
      const asset = findStaticAsset(this.options.manifest, HULL_ASSET_ID);
      this.load.image(HULL_TEXTURE, assetUrl(this.options.manifest, asset));
    } catch (error) {
      // A missing hull is a broken build, not a reason to take the tab down.
      // The scene falls back to a drawn shape and says so once.
      this.reportError('open', error);
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');

    this.unsubscribers.push(
      this.options.bridge.onGameMessage('pvp:open', ({ seat, ruleset }) => {
        this.guard('open', () => {
          this.seat = seat;
          this.opened = true;
          /* Reconstruido a cada abertura: as texturas ja existem, portanto isto
           * nao volta a desenhar nada - so cria os sprites desta arena. */
          this.cycloneCuts?.destroy();
          this.cycloneCuts = new CycloneCutSystem(this as never);
          /* E o das explosões, pela mesma regra e no mesmo instante. */
          this.atomicBlasts?.destroy();
          this.atomicBlasts = this.buildBlastSystem();
          /* E o das bombas de carta, pela mesma regra e no mesmo instante. */
          this.cardBlasts?.destroy();
          this.cardBlasts = new CardBlastFx(this, Phaser.BlendModes.ADD, DEPTH.projectile);
          /* E o do míssil, pela mesma regra e no mesmo instante. */
          this.missileBlasts?.destroy();
          this.missileBlasts = this.buildMissileBlastSystem();
          /* E o das marcas, pela mesma regra e no mesmo instante (V28G.M). */
          /*
           * E o das SERRAS, pela mesma fronteira exata (gate V28G.P).
           *
           * Reconstruído com a cena, como os outros: os sprites pertencem a uma
           * arena, e a arena anterior já não existe.
           */
          this.sawBlades?.destroy();
          this.sawBlades = new SawBladeSystem(this as never, DEPTH.bar - 2);
          this.poisonMarks?.destroy();
          /* E o das serras, pela mesma razão exata (gate V28G.P). */
          this.sawBlades = new SawBladeSystem(this as never, DEPTH.bar - 2);
          this.poisonMarks = new PoisonMarkSystem(this as never, DEPTH.bar);
          /* E o do sangramento, pela mesma regra e no mesmo instante (V28G.N). */
          this.bleedMarks?.destroy();
          this.bleedMarks = new BleedMarkSystem(this, DEPTH.bar, 'bleed-pvp');
          this.bleedBursts?.destroy();
          this.bleedBursts = new BleedBurstSystem(
            this as never,
            DEPTH.bar,
            BLEED_BURST_POOL_CEILING,
            bleedAudioFrom(this.sound as { readonly context?: unknown }),
            (finale) => this.showBleedDamage(finale.x, finale.y, finale.damage),
          );
          /* E os dois da Reflexão, pela mesma regra e no mesmo instante (V28G.O). */
          this.buildReflectSystems();
          /* E sem `+VALOR` nenhum de uma partida anterior. */
          this.destroyHealTexts();
          this.destroyBleedTexts();
          /*
           * Which VFX profile a skin will compile with, decided HERE and never
           * again for the life of this match.
           *
           * A ruleset does not change once a room exists, so a renderer built
           * on the first frame is built on the right budget and no later frame
           * can move it. Deriving it from the roster instead would answer "a
           * duel" while a team match filled and then rebuild every renderer as
           * the other five arrived.
           */
          this.skinModality = ruleset === 'team_3v3' ? 'pvp3x3' : 'pvp1x1';
          /*
           * A new duel is not the old duel's failure.
           *
           * Cleared here rather than on close so that closing a frozen arena
           * and opening another one recovers, while a player who simply stays
           * on the failed one is not shown a scene that quietly resumed
           * throwing.
           */
          this.failed = false;
          this.correction.reset();
          this.prediction.reset();
          this.lastSampleAt = null;
          this.cadence.reset();
          this.intent.clear();
          this.zoom = PVP_ZOOM_DEFAULT;
          this.enableInput();
        });
      }),
      this.options.bridge.onGameMessage('pvp:arm-teleport', () => {
        /*
         * Opened from the HUD button, which is the primary way in - the key is
         * a shortcut for players who already have a hand on the keyboard.
         * Arming grants nothing and costs nothing, so this needs no guard
         * beyond the scene being open.
         */
        this.guard('open', () => {
          if (this.opened) {
            this.intent.armTeleport();
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:activate-card', ({ slot, commandId }) => {
        /*
         * A ORDEM de ligar uma carta (gate V28H.J).
         *
         * Arma o próximo frame de comando e mais nada: não liga a carta, não
         * decide se ela pode, e não toca no arrefecimento. A SALA responde a
         * essas três, e a cena nem sequer as pergunta.
         */
        this.guard('open', () => {
          if (this.opened) {
            this.intent.requestCardActivation(slot, commandId);
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:board-summon', ({ commandId }) => {
        /*
         * A ordem de PILOTAR (gate V28H.X): arma o próximo frame e mais nada.
         *
         * A sala é que decide se há uma estrutura encostada, se ela já tem
         * piloto, e se este casco pode entrar. A cena nem sequer pergunta.
         */
        this.guard('open', () => {
          if (this.opened) {
            this.intent.requestBoard(commandId);
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:release-card', ({ slot, commandId }) => {
        /*
         * A ordem de SOLTAR uma carga (gate V28H.N).
         *
         * A Lança Solar carrega enquanto a tecla está premida, e é o gémeo
         * exacto da mensagem acima: arma o próximo frame de comando e mais nada.
         * A sala decide se havia carga para soltar.
         */
        this.guard('open', () => {
          if (this.opened) {
            this.intent.requestCardRelease(slot, commandId);
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:detonate-bleed', () => {
        /*
         * Premido no botão do HUD, que é a única forma de entrar (gate V28G.N).
         *
         * Arma o próximo frame de comando e mais nada: não dispara o canhão, não
         * substitui controlo nenhum, não usa a soltura do rato e não rouba tecla
         * nenhuma. Sem marcas, a sala faz um `no-op` - e responder aqui seria uma
         * segunda casa para uma regra que já tem dono.
         */
        this.guard('open', () => {
          if (this.opened) {
            this.intent.requestDetonateBleed();
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:intent-sent', ({ seq, intent }) => {
        // Recorded, not applied: the prediction replays it only while the room
        // has not acknowledged it.
        this.prediction.record(seq, intent);
      }),
      this.options.bridge.onGameMessage('pvp:proc', ({ cuts }) => {
        /* `art`, que e o rotulo que o contrato de erro ja tem para um passo de
         * desenho - um rotulo novo atravessaria `pvp:scene-error`. */
        this.guard('art', () => {
          if (!this.opened || !this.cycloneCuts) {
            return;
          }
          /*
           * As curas desta entrega, somadas por `(atacante, tique)`.
           *
           * A chave é essa e não `(atacante, alvo, tique)`: o jogador quer saber
           * quanto o seu casco recuperou, e de qual adversário cada fração saiu
           * não muda decisão nenhuma. Dois alvos no mesmo tique entram no mesmo
           * total; dois tiques são dois números.
           *
           * Em MILLI inteiro, para a soma ser exata. Local à entrega e nunca
           * guardado: a sala esvazia a sua caixa de saída de uma vez por
           * publicação, portanto todos os factos de um tique chegam juntos.
           */
          const healed: ConfirmedHeal[] = [];
          for (const cut of cuts) {
            /*
             * Despachado pelo EFEITO, nunca pela joia. Sem este ramo uma cura
             * desenharia um corte: os dois viajam no mesmo canal, e o canal
             * existe para carregar mais do que um efeito.
             */
            if (cut.procId === CYCLONE_CUT_EFFECT) {
              /*
               * A posicao AUTORITATIVA, convertida para pixeis e mais nada. Nao
               * se procura o sprite do alvo: um corte letal pode ter acabado de
               * o remover, e seria o golpe mais importante de ver a nao
               * aparecer.
               */
              this.cycloneCuts.spawn({
                id: cut.id,
                x: pvpWorldUnits(cut.x),
                y: pvpWorldUnits(cut.y),
                /*
                 * O casco de REFERÊNCIA, porque este payload não nomeia alvo.
                 *
                 * Um corte acontece num PONTO e a sala não diz sobre quem - o
                 * campo não existe de propósito. Dimensioná-lo pelo casco de
                 * alguém exigiria adivinhar de quem, e adivinhar é pior do que
                 * uma proporção fixa.
                 */
                diameter: this.referenceHullDiameter(),
              });
            } else if (cut.procId === LIFESTEAL_HEAL_EFFECT && cut.healedMilli !== undefined) {
              healed.push({
                attacker: cut.attacker,
                tick: cut.tick,
                healedMilli: cut.healedMilli,
              });
            } else if (
              cut.procId === VITAL_HEAL_EFFECT &&
              cut.healedMilli !== undefined &&
              /*
               * **Zero não desenha nada** (gate V28G.U).
               *
               * A autoridade não anuncia uma cura de zero - ela sai antes do
               * evento quando o clamp da vida máxima não deixou passar nada - e
               * esta linha é a segunda vez que isso é dito, do lado que desenha.
               *
               * Sem ela, um zero que chegasse por outro caminho acenderia um
               * feixe SEM número: `healNumbersFrom` já descarta um total não
               * positivo, portanto o `+TOTAL` não apareceria e o feixe apareceria
               * sozinho - a pior das duas metades.
               */
              cut.healedMilli > 0
            ) {
              /*
               * **A CURA DE SUPORTE, e o SENTIDO é o inverso do sifão**
               * (gate V28G.U).
               *
               * No Roubo de Vida o curado é o `attacker`; aqui é o `target`, e é
               * por isso que o canal passou a entregar os dois ids. O número
               * verde aparece sobre quem RECEBEU a vida, porque é a barra dele
               * que sobe.
               */
              healed.push({
                attacker: cut.target,
                tick: cut.tick,
                healedMilli: cut.healedMilli,
              });
              this.drawVitalHeal(cut.attacker, cut.target, cut.healedMilli);
            }
          }
          /* A agregação é PARTILHADA com a caça: uma função, dois modos. */
          for (const number of healNumbersFrom(healed)) {
            this.showHeal(number.attacker, number.milli);
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:blast', ({ blasts }) => {
        /* O mesmo rótulo de erro do corte: é um passo de DESENHO. */
        this.guard('art', () => {
          if (!this.opened || !this.atomicBlasts) {
            return;
          }
          for (const blast of blasts) {
            /*
             * **A explosão do MÍSSIL** (gate V28G.L).
             *
             * Despachada pelo mesmo canal e separada pelo EFEITO, que é a única
             * coisa que as distingue. O raio que o evento traz é ZERO, e é a
             * verdade - o míssil não tem área. O tamanho DESENHADO vem daqui,
             * derivado do corpo do míssil, e não pode ser confundido com um raio
             * de dano porque não existe nenhum para representar.
             */
            if (blast.effectId === MISSILE_EFFECT) {
              this.missileBlasts?.spawn({
                id: blast.id,
                x: pvpWorldUnits(blast.x),
                y: pvpWorldUnits(blast.y),
                radius: pvpWorldUnits(PVP_MISSILE_BODY_RADIUS_MU) * 2 * MISSILE_BLAST_RADIUS_FACTOR,
              });
              /*
               * E o FLASH, sobre o casco que REALMENTE levou - no máximo um, e
               * nenhum quando a explosão foi contra uma parede, o limite ou o fim
               * de vida.
               */
              for (const seat of blast.hit) {
                this.flashHull(seat as PvpParticipantId);
              }
              continue;
            }
            /*
             * **As duas explosões de CARTA** (gate V28H.U), com arte própria.
             *
             * Despachadas pelo EFEITO como as outras duas, e nunca pelo raio: o
             * Catolé abre seis vezes mais do que a Bomba Atômica, e escolher a
             * arte pelo tamanho seria trocá-la no dia em que alguém afinasse um
             * número de equilíbrio.
             *
             * O raio desenhado é o MECÂNICO que vem no evento - o mesmo que
             * decidiu quem levou - portanto a onda diz a verdade sobre o que
             * apanhou, e a Esfera de uma carga curta abre menos do que a de uma
             * carga cheia.
             */
            if (
              blast.effectId === FIREBALL_BLAST_EFFECT ||
              blast.effectId === CATOLE_BLAST_EFFECT
            ) {
              const kind = blast.effectId === CATOLE_BLAST_EFFECT ? 'catole' : 'fireball';
              this.cardBlasts?.burst(
                {
                  id: blast.id,
                  kind,
                  x: pvpWorldUnits(blast.x),
                  y: pvpWorldUnits(blast.y),
                  radius: pvpWorldUnits(blast.radius),
                },
                this.time.now,
              );
              /*
               * **E o TREMOR, só do Catolé** (gate V28H.U): curto, e o mesmo que
               * a caça dá. A Esfera não treme - ela é uma bola de fogo, e um
               * ecrã a abanar a cada carga solta seria um ecrã sempre a abanar.
               */
              if (kind === 'catole') {
                this.cameras.main.shake(CATOLE_SHAKE_MS, CATOLE_SHAKE_INTENSITY);
              }
              for (const seat of blast.hit) {
                this.flashHull(seat as PvpParticipantId);
              }
              continue;
            }
            if (blast.effectId !== ATOMIC_BLAST_EFFECT) {
              /*
               * Despachado pelo EFEITO, nunca pela joia - e um efeito que esta
               * cena não conhece é IGNORADO em vez de desenhado como o que ela
               * conhece. Um canal que carrega mais do que um facto tem de o
               * dizer, e este diz.
               */
              continue;
            }
            /*
             * O centro e o RAIO vêm da autoridade, convertidos para unidades do
             * mundo e mais nada.
             *
             * O raio não é reconstruído a partir de uma constante deste bundle:
             * um cliente com o seu próprio raio desenharia um círculo que
             * deixaria de dizer a verdade no dia em que o do servidor mudasse, e
             * o §5 exige que ele represente o raio mecânico real.
             */
            this.atomicBlasts.spawn({
              id: blast.id,
              x: pvpWorldUnits(blast.x),
              y: pvpWorldUnits(blast.y),
              radius: pvpWorldUnits(blast.radius),
            });
            /*
             * E o FLASH, sobre os cascos que REALMENTE levaram.
             *
             * A lista vem do servidor - quem levou dano, e não quem estava por
             * perto - portanto uma nave que já estava sem vida não pisca. É o
             * §9: "flash breve sobre as naves realmente atingidas".
             */
            for (const seat of blast.hit) {
              this.flashHull(seat as PvpParticipantId);
            }
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:reflect', ({ reflects }) => {
        /* O mesmo rótulo de erro dos outros três: é um passo de DESENHO. */
        this.guard('art', () => {
          if (!this.opened) {
            return;
          }
          for (const reflect of reflects) {
            if (reflect.stage === 'l') {
              /*
               * **O número, e só no estágio em que o payload ATERROU.**
               *
               * O §7 é explícito para o Míssil e para a Bomba: `REFLECT` aparece
               * quando o novo payload finalmente causa dano, e não no
               * relançamento. Aqui isso não é uma condição sobre o tipo de
               * payload - é o estágio, que a autoridade já separou.
               *
               * As coordenadas são as do DESTINATÁRIO no tique em que o golpe
               * caiu, e vêm no evento: lê-las do estado daria as do tique de
               * publicação, que num frame que junta dois tiques é outro sítio.
               */
              this.reflectNumbers?.print({
                /*
                 * **Convertidas, como as dos outros três canais** (V28G.O.2).
                 *
                 * O evento transporta MILLI-UNIDADES - `recipient.x` é o mesmo
                 * campo que `pvp:proc` publica e que esta cena já converte - e
                 * `print` trabalha em unidades de mundo: ele soma `combatTextSpread`
                 * em píxeis e subtrai um deslocamento em píxeis. Sem isto, a
                 * composição nascia mil vezes fora do enquadramento.
                 */
                x: pvpWorldUnits(reflect.tx),
                y: pvpWorldUnits(reflect.ty),
                /*
                 * **E o VALOR também vinha na unidade errada** (V28G.O.2).
                 *
                 * `d` é milli-vida no fio, a ligação guarda-o em milli e o
                 * controlador entrega-o em milli - mas `print` documenta
                 * `amount` como pontos inteiros e passa-o a `formatCombatNumber`.
                 * Um golpe de 16 aparecia como «16 mil».
                 *
                 * É o mesmo erro das coordenadas, no mesmo canal, e ficou
                 * escondido pela mesma razão: o número era desenhado fora da
                 * arena. O número da DETONAÇÃO, dez linhas abaixo, sempre
                 * converteu - é o contraste que o nomeia.
                 *
                 * O formatador NÃO muda: o contrato do gate V28G.O manda usar o
                 * de combate, e usá-lo com a unidade errada era cumprir metade.
                 */
                amount: (reflect.damage ?? 0) / Number(PVP_HEALTH_MILLI_PER_UNIT),
              });
              continue;
            }
            /*
             * **O BRILHO, na silhueta de quem intercetou.**
             *
             * A textura e a transformação saem do casco que a cena já desenha -
             * é isso que faz o brilho ter a forma da nave em vez de uma forma
             * inventada. Um assento que este cliente nunca desenhou não tem
             * casco, e aí não desenhar é a única resposta honesta.
             */
            const hull = this.hulls.get(reflect.reflector as PvpParticipantId);
            if (hull) {
              this.reflectFlash?.glow({
                id: reflect.reflector,
                textureKey: hull.texture.key,
                x: hull.x,
                y: hull.y,
                rotation: hull.rotation,
                width: hull.displayWidth,
                height: hull.displayHeight,
              });
            }
            /*
             * **O FEIXE, e SÓ para um tiro comum.**
             *
             * É o único payload cujo redirecionamento não tem entidade nenhuma a
             * viajar: o §5 manda que o projétil termine no contacto, que nenhum
             * projétil de retorno nasça, e que o dano caia no atacante
             * imediatamente. O feixe é o que representa esse "imediatamente".
             *
             * O Míssil e a Bomba partem MESMO da nave refletora - são entidades,
             * e a cena já as desenha a voar - portanto um feixe por cima delas
             * seria desenhar a mesma viagem duas vezes. O Ciclone, o Veneno e o
             * Sangramento aparecem no atacante pela sua própria apresentação
             * aprovada, que é o que o §7 manda reutilizar.
             *
             * As duas posições vêm do EVENTO, pela mesma razão que as do número.
             */
            if (reflect.payload === CANNON_SHOT_PAYLOAD) {
              this.reflectFlash?.beam({
                id: `${reflect.reflector}:${reflect.target}`,
                /*
                 * **Convertidas, pela mesma razão e pelo mesmo helper.**
                 *
                 * `beam` escreve `strip.x = shot.fromX` num objeto de desenho, e
                 * mede o comprimento entre os dois pontos. Cruas, o segmento tinha
                 * mil vezes o tamanho da arena e nascia fora dela.
                 *
                 * O BRILHO acima não passa por aqui de propósito: ele lê
                 * `hull.x` do sprite que esta cena já desenhou, portanto já está
                 * em unidades de mundo. Convertê-lo seria a segunda metade do
                 * mesmo erro, virada ao contrário.
                 */
                fromX: pvpWorldUnits(reflect.x),
                fromY: pvpWorldUnits(reflect.y),
                toX: pvpWorldUnits(reflect.tx),
                toY: pvpWorldUnits(reflect.ty),
              });
            }
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:bleed', ({ bursts }) => {
        /* O mesmo rótulo de erro do corte e da explosão: é um passo de DESENHO. */
        this.guard('art', () => {
          if (!this.opened || !this.bleedBursts) {
            return;
          }
          for (const burst of bursts) {
            const at = this.hullPositions.get(burst.target as PvpParticipantId);
            if (at === undefined) {
              /*
               * Este casco NUNCA foi desenhado nesta arena.
               *
               * O mapa guarda a última posição de cada assento e não é limpo
               * entre quadros, portanto um alvo que a detonação acabou de MATAR
               * continua aqui, no sítio exato onde morreu - que é onde as garras
               * têm de abrir. Chegar a este `continue` quer dizer que a sala
               * anunciou um golpe sobre um casco que este cliente nunca viu, e
               * aí não desenhar é a única resposta honesta.
               *
               * O caso do golpe que mata era invisível por outra razão, do lado
               * do SERVIDOR: no tique terminal a sala devolvia só o quadro de
               * fim e o evento nunca saía. Ver a nota em `PvpRoom.pump`.
               */
              continue;
            }
            this.bleedBursts.spawn(
              {
                key: burst.key,
                x: at.x,
                /*
                 * O CASCO, e não a faixa das marcas: o §7 diz que elas convergem
                 * para o casco e rompem lá. As posições de onde elas vêm são
                 * calculadas pelo sistema, em coordenadas locais deste ponto.
                 */
                y: at.y,
                diameter: this.hullDiameterOf(burst.target),
                marks: burst.marks,
                /*
                 * A semente sai da CHAVE autoritativa - `(autor, tique, alvo)` -
                 * e nunca de `Math.random()`: dois clientes a ver o mesmo duelo
                 * têm de ver os mesmos fragmentos, e o §7 exige que a variação
                 * derive da identidade ou do tique.
                 */
                seed: hashSeed(burst.key),
                /*
                 * O valor que a AUTORIDADE aplicou, guardado com o efeito.
                 *
                 * Não é impresso aqui: o dono pediu que o total apareça **só no
                 * fim**, e o fim é o corte, que abre depois de todas as garras.
                 * Quem o desenha é o `onFinale` do sistema.
                 */
                damage: burst.damage,
              },
              this.time.now,
            );
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:authoritative', ({ state }) => {
        this.guard('adopt', () => {
          if (!this.opened) {
            return;
          }
          if (state.arena && !this.arena) {
            this.arena = pvpWorldArena(state.arena);
            /*
             * **O tamanho de CADA casco, um por cadeira** (gate V28G.R).
             *
             * Era um número só para a arena inteira, lido de `arena.maxHealth` -
             * a referência normalizada da sala. Isso estava certo enquanto todos
             * os cascos tinham a mesma vida, e deixou de estar: a vida máxima é
             * uma propriedade de cada casco, e a boas-vindas já a declara por
             * participante em `combatProfile`.
             *
             * Lido UMA vez, aqui, onde a boas-vindas aterra. Não por quadro: uma
             * escala que acompanhasse a vida ATUAL encolheria um jogador a
             * perder até ele ser um alvo mais difícil.
             *
             * Esta cena não pergunta que joia produziu a vida, não lê nível e
             * não lê inventário. Ela recebe uma vida máxima autoritativa e
             * chama a única curva que existe.
             */
            this.hullScales.clear();
            for (const [participantId, profile] of Object.entries(state.arena.combatProfile)) {
              if (profile === undefined) {
                continue;
              }
              this.hullScales.set(participantId, pvpHullScale(pvpHullOf(profile)));
            }

            /*
             * And the size every SHOT is drawn at, from the same welcome.
             *
             * `projectileRadius` is the room's collision radius, so one answer
             * serves ninety bolts. Cleared on close rather than kept, because
             * the next duel's welcome is the only thing allowed to set it.
             */
            this.dashGauge = pvpDashGauge(
              this.arena.projectileRadius,
              this.referenceHullDiameter(),
            );
            this.buildArena(this.arena);
          }
          /*
           * The buffer, not the display list.
           *
           * `update` samples; this only records. Drawing here would tie the
           * frame rate to however often the room happens to send, which is
           * exactly what interpolation exists to decouple.
           */
          this.view.push(state, this.time.now);
          /*
           * The room's word about THIS ship, straight to the prediction.
           *
           * `ack` is what makes the replay honest: everything at or below it
           * has already been folded into the position above, and replaying it
           * again would move the ship twice.
           */
          const mine = this.seat ? state.ships.find((ship) => ship.s === this.seat) : undefined;
          if (mine) {
            /*
             * The room's own speed for this hull, not a locally guessed one.
             *
             * Speed is the only thing that changes how far a tick travels, and
             * the frame that carries the position also carries how many ticks
             * of it are left. Reading it here - rather than from a rune the
             * client thinks it picked up - keeps the replay integrating exactly
             * what the room integrated, including the tick the effect expires.
             */
            const mineEffects = this.seat
              ? state.effects.find((entry) => entry.seat === this.seat)
              : undefined;
            this.prediction.acceptAuthoritative(
              { x: mine.x, y: mine.y },
              state.ack,
              state.correction,
              {
                /*
                 * THIS hull's speed, from the room, through the same function
                 * the room uses.
                 *
                 * Two things had to be true for a prediction to track. The
                 * formula is shared - two inlined copies of one expression is
                 * exactly how the prediction came to replay movement at a speed
                 * the room was not using - and the hull is now the SERVER's,
                 * not `PVP_DEFAULT_COMBAT_PROFILE` out of this bundle. A client
                 * that assumed 10 000 while the room integrated 10 600 would
                 * fall behind by six hundredths of every tick, for ever.
                 *
                 * The local participant's own profile, never a teammate's and
                 * never an opponent's: this replays the inputs THIS browser
                 * sent, for the hull it is driving.
                 */
                speedBps: pvpEffectiveSpeedBps(
                  this.arena?.combatProfile[this.seat ?? ''] ?? PVP_DEFAULT_COMBAT_PROFILE,
                  (mineEffects?.speedTicks ?? 0) > 0 ? PVP_RUNE_SPEED_BPS : null,
                ),
                /*
                 * E o RAIO deste casco, pela mesma porta (gate V28G.R).
                 *
                 * `pvpAdvanceShip` trava contra as paredes e os pilares por
                 * ele, e a sala trava por ele. Um cliente que previsse com o
                 * raio da nave de referência encostaria uma nave grande onde a
                 * sala não a encosta - e seria corrigido a cada tique.
                 *
                 * Do MESMO perfil de onde a velocidade sai, e pela mesma
                 * função que a sala usa. Nunca uma segunda curva escrita aqui.
                 */
                hullRadiusMu: pvpHullRadiusMu(
                  pvpHullOf(this.arena?.combatProfile[this.seat ?? '']),
                ),
                /*
                 * **E os OUTROS cascos, que este já não atravessa** (gate
                 * V28H.K).
                 *
                 * Os obstáculos fixos saíram da arena e as naves tomaram-lhes o
                 * lugar, portanto `pvpAdvanceShip` trava contra um casco onde
                 * travava contra um pilar. Sem esta lista o cliente empurrava a
                 * nave para dentro do adversário e a sala corrigia-a no quadro
                 * seguinte - todos os quadros, enquanto os dois se tocassem.
                 *
                 * Do MESMO quadro de onde a posição sai, com o raio lido do
                 * mesmo perfil e pela mesma função: nunca uma segunda curva
                 * escrita aqui. Sem o próprio casco - ninguém se bloqueia a si -
                 * e sem os mortos, porque um destroço não é parede.
                 */
                blockers: state.ships
                  /* `hp` é uma string decimal em milli: comparada como número, e nunca truncada. */
                  .filter((ship) => ship.s !== this.seat && BigInt(ship.hp) > 0n)
                  .map((ship) => ({
                    x: ship.x,
                    y: ship.y,
                    radius: pvpHullRadiusMu(pvpHullOf(this.arena?.combatProfile[ship.s])),
                  })),
                /*
                 * The room's own tick, which is what turns "the hull moved a
                 * long way" into "the hull moved further than three ticks
                 * allow". The room publishes at the client's send cadence
                 * rather than at its own tick - measured in
                 * `room-publish-rate.test.ts` - so a frame carrying two ticks
                 * is the normal case rather than the exception, and a client
                 * without this number has to guess which of the two it is.
                 */
                tick: state.tick,
                /* A charge spent is a teleport, and a teleport is allowed to jump. */
                teleportCharges: mineEffects?.teleportCharges,
                /*
                 * O EMPURRÃO, dito pela sala (gate V28G.J).
                 *
                 * Enquanto durar, o preditor deixa de replicar as intenções
                 * deste browser: a sala ignora-as, portanto reproduzi-las seria
                 * empurrar a nave localmente contra o bloqueio e ser corrigido
                 * de volta a cada frame.
                 */
                blocked: (mine.kb ?? 0) > 0,
              },
            );
          }
          // Commands are only ever sent while the room says it is running.
          this.acceptingCommands = state.phase === 'live' && state.matchState === 'running';
          if (!this.acceptingCommands) {
            // Reconnecting, ready, ended: nothing is held any more.
            this.clearIntent();
          }
        });
      }),
      this.options.bridge.onGameMessage('pvp:close', () => {
        this.guard('close', () => {
          this.closeView();
          /*
           * O sistema do corte e LARGADO ao fechar a arena, e nao apenas ao
           * desligar a cena.
           *
           * A cena sobrevive a um fecho - ela fica a espera do proximo
           * `pvp:open` - portanto guardar o pool aqui deixava sprites vivos numa
           * cena sem mundo. A medicao no navegador apanhou-o: dezoito objetos
           * do corte continuavam na lista de exibicao depois de fechar. Um pool
           * existe para nao alocar DENTRO de uma partida, e nao para atravessar
           * o intervalo entre duas.
           */
          this.cycloneCuts?.destroy();
          this.cycloneCuts = null;
          /*
           * E as marcas, pela mesma razão exata: a cena sobrevive a um fecho, e
           * um objeto gráfico guardado ficaria vivo numa cena sem mundo.
           */
          /* E as lâminas: um sprite por serra, todos deste sistema (V28G.P). */
          this.sawBlades?.destroy();
          this.sawBlades = null;
          this.poisonMarks?.destroy();
          this.poisonMarks = null;
          this.bleedMarks?.destroy();
          this.bleedMarks = null;
          this.bleedBursts?.destroy();
          this.bleedBursts = null;
          /* E os dois da Reflexão, pela mesma razão exata (gate V28G.O). */
          this.destroyReflectSystems();
          this.destroyHealTexts();
          this.destroyBleedTexts();
        });
      }),
    );

    /*
     * Letting go when the scene goes.
     *
     * A subscription outlives the object that made it unless something takes it
     * back, and React's StrictMode mounts twice in development - so a first
     * scene is destroyed while still listening, and the next message reaches a
     * dead one. The mining ring learned this the hard way; this scene inherits
     * the answer rather than the lesson.
     */
    /* Um por cena, criado antes de qualquer evento poder chegar. */
    this.cycloneCuts = new CycloneCutSystem(this as never);
    this.atomicBlasts = this.buildBlastSystem();
    /* E o das bombas de carta, pela mesma fronteira exacta (gate V28H.U). */
    this.cardBlasts = new CardBlastFx(this, Phaser.BlendModes.ADD, DEPTH.projectile);
    this.missileBlasts = this.buildMissileBlastSystem();
    this.poisonMarks = new PoisonMarkSystem(this as never, DEPTH.bar);
    this.bleedMarks = new BleedMarkSystem(this, DEPTH.bar, 'bleed-pvp');
    this.bleedBursts = new BleedBurstSystem(
      this as never,
      DEPTH.bar,
      BLEED_BURST_POOL_CEILING,
      bleedAudioFrom(this.sound as { readonly context?: unknown }),
      (finale) => this.showBleedDamage(finale.x, finale.y, finale.damage),
    );
    this.buildReflectSystems();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      for (const unsubscribe of this.unsubscribers) {
        unsubscribe();
      }
      this.unsubscribers.length = 0;
      this.closeView();
      /* Sprites e tweens do corte sao deste sistema e morrem com a cena. */
      this.cycloneCuts?.destroy();
      this.cycloneCuts = null;
      /* Idem para a explosão. */
      this.atomicBlasts?.destroy();
      this.atomicBlasts = null;
      /* E as bombas de carta, com os corpos, os anéis e as brasas (V28H.U). */
      this.cardBlasts?.destroy();
      this.cardBlasts = null;
      /* E o fogo do míssil, com os seus sprites, tweens e esteira (V28G.L). */
      this.missileBlasts?.destroy();
      this.missileBlasts = null;
      /* E as marcas: um objeto gráfico por casco, todos deste sistema (V28G.M). */
      /* E as lâminas, pela mesma razão exata (gate V28G.P). */
      this.sawBlades?.destroy();
      this.sawBlades = null;
      this.poisonMarks?.destroy();
      this.poisonMarks = null;
      this.bleedMarks?.destroy();
      this.bleedMarks = null;
      this.bleedBursts?.destroy();
      this.bleedBursts = null;
      /* E o brilho, o feixe e as composições `REFLECT` (gate V28G.O). */
      this.destroyReflectSystems();
      this.dropMissiles();
      for (const sprite of [...this.missileTrailPool, ...this.missileFlamePool]) {
        sprite.destroy();
      }
      this.missileTrailPool.length = 0;
      this.missileFlamePool.length = 0;
      /* E os textos de cura, pela mesma regra. */
      this.destroyHealTexts();
      this.destroyBleedTexts();
    });

    /*
     * Every subscription above is now installed, and saying so is what lets a
     * reloaded page get its arena back.
     *
     * `PvpFeature` wraps the host that builds this game, so it mounts FIRST and
     * Phaser boots afterwards. Anything the feature published from a mount
     * effect - `pvp:open`, on a resume - therefore arrived before this scene
     * existed and was dropped on the floor. Measured: the room rejoined, the
     * HUD came back with both ships at full health, and the arena behind it
     * never opened.
     */
    this.options.bridge.sendToUi('pvp:scene-ready', {});
  }

  /**
   * The frame, behind a boundary that refuses to fail silently.
   *
   * ## The failure this exists for
   *
   * `update` used to run unguarded, and an exception in it does not stop
   * Phaser: the renderer goes on drawing the display list exactly as it was, so
   * the arena FREEZES with everything still on screen and nothing at all says
   * why. It was found by the six-participant benchmark, where a fixture missing
   * one field threw on the first line of `drawShips` every frame for a whole
   * commit - and the only symptom that reached a person was an object count two
   * away from a stale number.
   *
   * The bridge handlers have had `guard` for exactly this reason since the
   * mining ring taught it. The frame did not, and the frame is the one that runs
   * sixty times a second.
   *
   * ## What the boundary does
   *
   * Reports ONCE, then stops updating. It does not retry, because a scene that
   * threw on one frame will throw on the next and sixty reports a second is a
   * console nobody can read. It does not tear the arena down either: React owns
   * leaving a duel, and the player's way out - the panel, the hunt behind it -
   * is exactly what must keep working.
   *
   * The stack goes to the console, where a developer needs it. What crosses the
   * bridge is the class and the message and nothing else: a frame carries a
   * match id, and an error is not a place to put one.
   */
  override update(): void {
    if (this.failed) {
      /*
       * Frozen, deliberately, and said once.
       *
       * The alternative was to keep calling a method that is known to throw,
       * which costs a stack trace per frame and changes nothing.
       */
      return;
    }
    try {
      this.frame();
    } catch (error) {
      this.failed = true;
      /*
       * The console gets the whole thing, including the stack.
       *
       * `reportError` deliberately carries only a message across the bridge, so
       * without this a developer would have the sentence and none of the
       * evidence. It is `console.error` rather than a rethrow because a rethrow
       * here is what the boundary exists to stop.
       */
      console.error('pvp scene failed during update; the arena is frozen', error);
      this.reportError('update', error);
    }
  }

  /**
   * One frame's work. The dormant path is the first line.
   *
   * A closed duel costs one comparison per frame: no sampling, no diffing, no
   * camera work and no input. The hunt behind it is untouched.
   */
  private frame(): void {
    if (!this.opened || !this.arena) {
      return;
    }
    const frame = this.view.sample(this.time.now);
    if (!frame) {
      return;
    }
    if (this.correction.observe(frame.correction)) {
      /*
       * The world was REPLACED rather than advanced.
       *
       * Everything drawn describes a world this one does not follow from, so
       * every shot and every remembered position goes. The new snapshot is then
       * applied whole, in this same frame - never a tween between two worlds
       * that were never continuous.
       */
      this.dropProjectiles();
      /*
       * E nenhum corte sobrevive a costura: ele descrevia um golpe de um mundo
       * que foi substituido, e deixa-lo a desvanecer seria mostrar uma linha
       * temporal descartada.
       */
      this.cycloneCuts?.clear();
      /*
       * E nenhuma marca sobrevive à costura, pela mesma razão: ela descrevia um
       * casco de um mundo que foi substituído. O frame que fez a costura traz os
       * stacks que a sala tem AGORA - incluindo os mesmos, se eram esses - e o
       * próximo quadro desenha-os.
       */
      /*
       * E as lâminas: a órbita que estava no ecrã era a de uma sala que acabou.
       * Uma sala nova traz a sua fase, e o primeiro frame dela desenha-a.
       */
      this.sawBlades?.clear();
      this.poisonMarks?.clear();
      this.bleedMarks?.clear();
      this.bleedBursts?.clear();
      /*
       * E nenhum brilho, feixe ou `REFLECT` sobrevive à costura (gate V28G.O):
       * eles descreviam uma interceção de um mundo que foi substituído, e as
       * posições que carregavam eram as desse mundo.
       */
      this.reflectFlash?.clear();
      this.reflectNumbers?.clear();
      /* E nenhuma cura, pela mesma razão exata (gate V28G.U). */
      this.vitalHeals?.clear();
      /*
       * E nenhum `+VALOR` a subir sobrevive a ela, pela mesma razão: ele
       * descrevia uma cura de um mundo que foi substituído. A vida correta vem
       * no frame que fez a costura; um número a flutuar sobre ela seria a
       * afirmação de uma linha temporal descartada.
       */
      this.clearHealTexts();
      this.clearBleedTexts();
      /*
       * And no rune residue survives the seam.
       *
       * Stated here, beside the shots and the skin trails, even though
       * `sync` would reach the same conclusion from the same correction number
       * a few lines below: a discontinuity is a thing this scene DOES, and
       * three of the four things that have to happen at one would be a list
       * with a gap in it. Dropping residue twice costs nothing; forgetting it
       * once leaves motes hanging over a world that was replaced.
       */
      this.runeRenderer?.discontinuity();
      /*
       * And no skin trail keeps crossing the seam either.
       *
       * A wake says "this ship came from over there", and after a correction it
       * did not: the two worlds are not continuous. Same reasoning as the shots
       * above, and the renderer's own `onDiscontinuity` contract is what the
       * descriptor states for this exact moment.
       */
      this.clearSkinResidue();
      /*
       * And no rune keeps burning across the seam.
       *
       * `drawShips` re-derives every indicator from the new snapshot a few lines
       * below, so this changes what is on screen only in the case that matters:
       * a participant the replaced world had under an effect and the new one
       * does not carry at all.
       */
      this.hideRuneMarks();
    }
    /*
     * Real elapsed time, so convergence is identical at 30, 60 and 144 fps.
     *
     * A fraction per frame - which is what the camera used to do - converges
     * twice as fast on a 120 Hz monitor as on a 60 Hz one, and leaves a
     * permanent lag proportional to speed on both.
     */
    const now = this.time.now;
    const deltaMs = this.lastSampleAt === null ? 0 : Math.max(0, now - this.lastSampleAt);
    this.lastSampleAt = now;
    // `peek`, not `current`: a prediction must not spend the teleport request
    // that the network layer has not put on the wire yet.
    const local = this.prediction.sample(deltaMs, this.intent.peek());

    /*
     * Who this browser is, resolved ONCE per frame and shared by everything
     * that has to answer "is this mine, my side's, or theirs".
     *
     * Hulls and projectiles used to work it out separately, from the same
     * assumption - that a participant's team is its own id - which is true in a
     * 1x1 and is a guess anywhere else. One identity, read from the roster the
     * room just sent, is what stops the two ever disagreeing.
     */
    const me = pvpLocalIdentity(frame, this.seat);

    this.drawShips(frame, local, deltaMs, me);
    /* E as invocações, depois dos cascos: elas pertencem a um deles. */
    this.syncSummons(frame, local, this.localAimHeading());
    this.drawProjectiles(frame);
    /* Os mísseis têm a sua lista e o seu diff (gate V28G.L). */
    this.drawMissiles(frame, deltaMs);
    this.followCamera(local, frame);
    /*
     * The runes are drawn AFTER the camera, and with the same delta everything
     * else here uses.
     *
     * After, because the level of detail is a function of the drawn size, and
     * the drawn size is `radius × zoom` - reading the zoom before `followCamera`
     * has applied it would pick a level for the previous frame. Same delta,
     * because two clocks in one frame is how an animation ends up half a frame
     * away from the thing it belongs to.
     */
    this.runeRenderer?.update(deltaMs, this.cameras.main.zoom);
    this.publishIntent();
  }

  /* ------------------------------------------------------------------ art */

  /**
   * Asks for a texture the build did not preload, once.
   *
   * `preload()` runs before the welcome exists, so neither hull nor barrel is
   * known then - which hull a seat flies is the server's answer and it has not
   * been given yet. Textures are therefore fetched when the profile arrives and
   * the sprite is swapped when the load lands, exactly as `HuntScene` does for
   * equipment changed mid-run. A texture that fails to arrive leaves the
   * placeholder hull rather than a blank frame.
   */
  private requestTexture(key: string, assetId: string): void {
    if (this.textures.exists(key) || this.pendingTextures.has(key)) {
      return;
    }
    try {
      const asset = findStaticAsset(this.options.manifest, assetId);
      this.pendingTextures.add(key);
      /*
       * Through the game-wide registry (gate V28I.CF.3): the hunt and the open
       * world load the same cannon and hull keys, and a second loader for a key
       * another scene is already fetching was what put "Texture key already in
       * use" on the console. `pendingTextures` now only means "this scene is
       * already waiting", so a hull drawn every frame registers one listener.
       */
      requestSharedTexture(this, key, assetUrl(this.options.manifest, asset), (loaded) => {
        this.pendingTextures.delete(key);
        if (loaded) {
          // The sprites were built against whatever was available at the time,
          // so they are dropped and rebuilt now that the real art is here.
          this.builtFrom.clear();
        }
      });
    } catch (error) {
      this.pendingTextures.delete(key);
      // A missing asset is a broken build, not a reason to end a duel.
      this.reportError('art', error);
    }
  }

  /** Everything a seat's profile needs on the GPU, requested together. */
  private requestArtFor(appearance: PvpAppearance): void {
    const hull = pvpShipArt(appearance);
    this.requestTexture(hull.key, hull.assetId);
    const cannon = pvpCannonArt(appearance);
    if (cannon) {
      this.requestTexture(cannon.key, cannon.assetId);
    }
  }

  /**
   * Builds the hull and its battery for one seat, from the server's profile.
   *
   * Idempotent by profile: a duel calls this every frame and it does nothing
   * unless the answer would differ. Rebuilding sprites at 60 Hz would allocate
   * during a frame, which is the one thing the arena is not allowed to do.
   */
  private buildShipArt(seat: PvpParticipantId, appearance: PvpAppearance): void {
    const hullArt = pvpShipArt(appearance);
    const cannonArt = pvpCannonArt(appearance);
    const hullKey = this.textures.exists(hullArt.key) ? hullArt.key : HULL_TEXTURE;
    const signature = `${hullKey}/${cannonArt?.key ?? 'none'}/${appearance.level}/${this.hullScaleOf(seat)}`;
    if (this.builtFrom.get(seat) === signature) {
      return;
    }
    this.builtFrom.set(seat, signature);

    const previousHull = this.hulls.get(seat);
    previousHull?.destroy();
    /*
     * The ship, in the colours it was DRAWN in.
     *
     * `clearTint` on a sprite that was never tinted is a no-op, and it is here
     * on purpose: it is the line a future change has to delete on purpose in
     * order to repaint a hull, rather than a silence that could be filled by
     * accident. The art is a PNG somebody made; a renderer that multiplies a
     * colour through it is showing a ship nobody drew, and at 3x it showed six
     * monochrome silhouettes where six different ships were meant to be.
     *
     * Whose ship it is, and what it is carrying, are said by the two marks
     * around it - `drawIdentity` and `drawRuneIndicator` - which is why nothing
     * is lost by leaving the texture alone.
     */
    const hull = this.add.image(0, 0, hullKey).setDepth(DEPTH.ship);
    hull.clearTint();
    /*
     * Sized from the SERVER's radius times the SERVER's health, and from the
     * texture's own pixels - never from whatever resolution the art shipped at.
     *
     * The scale is presentation only. `arena.shipRadius` is still the collision
     * radius and a hull drawn at three times its art still collides as one; the
     * arena's corridors are proved wide enough for the drawing rather than for
     * the hitbox, which is why the two are allowed to differ.
     */
    const diameter = this.hullDiameterOf(seat);
    hull.setDisplaySize(diameter, diameter);
    this.hulls.set(seat, hull);

    for (const sprite of this.cannons.get(seat) ?? []) {
      sprite.destroy();
    }
    const size = PVP_CANNON_WORLD_UNITS * this.hullScaleOf(seat);
    const barrelKey = cannonArt && this.textures.exists(cannonArt.key) ? cannonArt.key : null;
    /*
     * One sprite per mount, and the count comes OUT of the formation.
     *
     * The origin is the barrel's own butt end so a cannon rotates about where
     * it is bolted rather than about its middle, which is what keeps a swivel
     * reading as a swivel.
     */
    const mountCount = barrelKey === null ? 0 : pvpCannonMounts(appearance).length;
    this.cannons.set(
      seat,
      barrelKey === null
        ? []
        : // Untinted, for the same reason the hull is: a cannon's rarity is drawn
          // INTO its art, and a team colour multiplied over it threw that away.
          Array.from({ length: mountCount }, () => this.buildBarrel(barrelKey, size, DEPTH.cannon)),
    );

    /*
     * And LAST, the skin - which is why the battery above cannot depend on it.
     *
     * The count came out of `pvpCannonMounts(appearance)`, the size out of
     * `PVP_CANNON_WORLD_UNITS * visualScale`, and neither line has read
     * anything a skin could write. A skin arriving after both is a skin that
     * cannot have changed either.
     */
    this.attachSkin(seat, hull);
  }

  /** One barrel sprite, in the colours it was drawn in. Used by both batteries. */
  private buildBarrel(key: string, size: number, depth: number): Phaser.GameObjects.Image {
    const sprite = this.add.image(0, 0, key).setDepth(depth);
    sprite.clearTint();
    sprite.setDisplaySize(size, size).setOrigin(0.5, 0.5);
    return sprite;
  }

  /* ---------------------------------------------------------------- arena */

  private buildArena(arena: PvpWorldArena): void {
    /*
     * The one wake emitter, built with the arena rather than with the first
     * shot that needs it.
     *
     * Deliberately eager. Building it lazily would save one object in a duel
     * where nobody ever fires - which is not a duel - and would cost something
     * far more valuable: the display list would gain an object at an instant
     * decided by a particle cadence, so "how many objects does one shot add"
     * would have two answers depending on which frame it was asked on. Ninety
     * bolts share it, it emits nothing until told to, and it is destroyed with
     * the duel.
     */
    this.buildTrailEmitter();
    /*
     * The space outside the walls, drawn first and behind everything.
     *
     * The camera may now look past the arena so the local ship can sit in the
     * middle of the screen even with its back to a wall. What it looks at has
     * to be unmistakably NOT the map: a flat, near-black field with no stars,
     * no grid and no obstacles. Nothing is ever placed here, the room clamps
     * every hull to the arena, and this rectangle has no body of any kind.
     */
    const margin = pvpVisualMargin(this.scale.width, this.scale.height, PVP_ZOOM_MIN);
    this.outside = this.add
      .rectangle(
        arena.width / 2,
        arena.height / 2,
        arena.width + margin.x * 2,
        arena.height + margin.y * 2,
        0x000003,
      )
      .setDepth(DEPTH.floor - 1);
    this.floor = this.add
      .rectangle(arena.width / 2, arena.height / 2, arena.width, arena.height, 0x04040a)
      .setDepth(DEPTH.floor);
    /*
     * **O céu e a poeira** (gate V28H.K).
     *
     * A arena passou a 24 000 x 15 000 e uma caixa quase preta cinco vezes mais
     * larga do que o ecrã não diz a ninguém que está a andar. Dois `TileSprite`
     * presos à câmara dão a paralaxe, um terceiro do tamanho da arena dá o chão,
     * e o número de objectos é três independentemente do tamanho da caixa.
     *
     * Por cima do `floor` e por baixo de tudo o resto: o céu é fundo, e nada que
     * o jogador tenha de ler passa a estar mais perto dele.
     */
    this.arenaVfx.buildBackground(
      this.scale.width,
      this.scale.height,
      arena.width,
      arena.height,
      DEPTH.floor,
    );
    this.border = this.add
      .rectangle(arena.width / 2, arena.height / 2, arena.width, arena.height)
      /*
       * Thicker and brighter than it was, because it now has a job it did not
       * have: it is the line between the playable arena and the dark outside.
       * A player must be able to see where they can no longer go.
       */
      .setStrokeStyle(6, 0x4fb6e0)
      .setFillStyle()
      .setDepth(DEPTH.obstacle);

    /*
     * Exactly the obstacles the welcome carried.
     *
     * Not one more, and none moved: cover that stops no shots is worse than no
     * cover, because a player will stand behind it.
     */
    /*
     * The boosters, drawn as floor rather than as cover.
     *
     * Flat, dim and BELOW the obstacles - they are not collision, a shot passes
     * over one and a hull is not stopped by it, so anything that read like a
     * wall would be a lie about the arena. Their positions come from the same
     * shared constant the room pushes with, so the two cannot disagree.
     *
     * **Três camadas desde o gate V28H.K**, que é o que o contrato do catálogo
     * pediu quando mandou melhorar as estruturas de impulso: a chapa diz ONDE,
     * os galões a escorrer dizem PARA ONDE, e a luz na saída diz até onde. A
     * seta parada de antes dizia só a primeira coisa, e num mapa 25x maior isso
     * já não chega.
     */
    for (const booster of PVP_BOOSTERS) {
      const pieces = this.arenaVfx.buildBooster(
        {
          x: pvpWorldUnits(booster.x),
          y: pvpWorldUnits(booster.y),
          w: pvpWorldUnits(booster.w),
          h: pvpWorldUnits(booster.h),
        },
        { x: booster.pushX, y: booster.pushY },
        DEPTH.booster,
      );
      for (const piece of pieces) {
        this.obstacles.push(piece as unknown as Phaser.GameObjects.Rectangle);
      }
    }

    /*
     * The runes, which are objects on the floor rather than a colour in it.
     *
     * Nothing is drawn here: the renderer draws a pad only while the room says
     * something is standing on it, and an empty pad is empty. The pickup radius
     * is handed over once, converted, because every fraction an appearance
     * declares is a fraction OF IT - which is what makes one appearance survive
     * the whole zoom range without knowing what a pixel is.
     */
    this.runeRenderer = new PvpRuneRenderer(this, pvpWorldUnits(PVP_RUNE_PICKUP_RADIUS_MU));

    for (const obstacle of arena.obstacles) {
      this.obstacles.push(
        this.add
          .rectangle(
            obstacle.x + obstacle.w / 2,
            obstacle.y + obstacle.h / 2,
            obstacle.w,
            obstacle.h,
            0x1b2436,
          )
          .setStrokeStyle(2, 0x3d5473)
          .setDepth(DEPTH.obstacle),
      );
    }

    const camera = this.cameras.main;
    this.applyCameraBounds(arena);
    camera.setZoom(this.zoom);
    const home = pvpCameraHome(arena);
    camera.centerOn(home.x, home.y);
  }

  /**
   * The box the CAMERA may move in, recomputed from the CURRENT viewport.
   *
   * Not the box a ship may move in - that is the room's, it is 3200x2000, and
   * nothing here can widen it. This is how far the view may look past the walls
   * so the local hull can stay centred with its back to one.
   *
   * Recomputed on resize and on zoom because both change what the viewport can
   * see, and a margin computed for the old one would clamp the camera again at
   * exactly the moment the player noticed.
   */
  private applyCameraBounds(arena: PvpWorldArena): void {
    const bounds = pvpCameraBounds(arena, this.scale.width, this.scale.height, PVP_ZOOM_MIN);
    this.cameras.main.setBounds(bounds.x, bounds.y, bounds.width, bounds.height);
  }

  /* ---------------------------------------------------------------- ships */

  private drawShips(
    frame: PvpRenderFrame,
    local: PvpPredictedPoint | null,
    deltaMs: number,
    me: PvpIdentity | null,
  ): void {
    const arena = this.arena;
    if (!arena) {
      return;
    }
    /*
     * Where this player is pointing right now, from the pointer itself.
     *
     * `null` while nothing has been aimed yet, and while the scene is not
     * accepting commands - on blur, on a hidden tab and after an ending the
     * barrel holds its last confirmed orientation rather than snapping to a
     * stale cursor position.
     */
    // Drawing reads without consuming; only the command frame spends it.
    const held = this.intent.peek();
    const localAim = this.acceptingCommands ? pvpHeading(held.ax, held.ay) : null;
    /*
     * What the LOCAL hull is being asked to do, from the keys held right now.
     *
     * The player own intent rather than the room echo of it, because a ship
     * that turned a round trip after its own keypress feels like a ship being
     * driven by somebody else. It decides a picture and nothing else, so being
     * early costs nothing.
     */
    const localTurn = this.acceptingCommands ? pvpHullTargetFromMovement(held.mx, held.my) : null;
    /*
     * What every participant is WEARING, checked here rather than where the
     * welcome lands.
     *
     * The arena object is captured once, when the first authoritative state
     * arrives - but its `appearance` record is not a copy. A team match's
     * connection FILLS that same record as chairs are admitted, so a client
     * that only looked at the message would learn the first arrival's skin and
     * never the other five. The frame loop is the one place that sees the
     * record as it stands now, and the check below is a comparison that returns
     * on its second line unless the server's answer actually changed.
     */
    this.syncAuthoritativeSkins(arena);
    /*
     * The room's word on what is standing on each pad, handed over whole.
     *
     * The renderer works out the rest from it: a pad that gained a kind raises
     * an artefact, one that lost a kind plays the pickup, and a correction
     * number that moved is a discontinuity. Which of those happened is not a
     * decision this scene has to make twice.
     */
    this.runeRenderer?.sync(frame.runes, frame.correction);
    this.drawTargeting();
    this.publishBoardOffer(frame);

    /* Vazia antes do ciclo: o que ficasse de um quadro seria uma esfera a arder
     * sobre um casco que já soltou a tecla. */
    this.chargingSpheres.length = 0;
    /*
     * **Quem está dentro de uma estrutura**, lido do frame (gate V28H.X).
     *
     * `pl` é quem pilota, e é a sala que o diz. O conjunto é reconstruído a
     * cada quadro em vez de guardado: um casco que saiu volta a desenhar-se no
     * primeiro quadro em que a estrutura já não o nomeia, sem ninguém ter de se
     * lembrar de o repor - e é assim que "reaparecer quando a habilidade acabar
     * ou o helicóptero for destruído" fica cumprido pelas duas pontas.
     */
    const piloting = new Set<string>();
    for (const summon of frame.summons ?? []) {
      if (summon.pl !== undefined) {
        piloting.add(summon.pl);
      }
    }
    for (const ship of frame.ships) {
      /*
       * The local hull is drawn where the PREDICTION says, and the opponent
       * where the room said.
       *
       * The prediction is this client's guess about a position it will be told;
       * it is never written back into the snapshot, never shown for the other
       * seat, and loses to the room's frame the moment they disagree.
       */
      /*
       * **E enquanto a SALA o empurra, não há nada a prever** (gate V28H.V).
       *
       * O Arranque continuava a ler-se como um teleporte, e a causa estava
       * aqui. Durante um empurrão - o do Arranque ou o de uma bomba - a sala
       * ignora as teclas, e o preditor deixa de integrar: ele fica parado e é
       * puxado até à verdade por uma correcção exponencial. A verdade chega à
       * cadência do servidor, aos saltos de um tique inteiro, portanto o casco
       * local avançava por sacões de 97 500 mu enquanto o do ADVERSÁRIO - que
       * passa pelo buffer de interpolação - deslizava.
       *
       * Com o empurrão a correr, o casco local passa pelo mesmo caminho de toda
       * a gente: a posição interpolada entre dois instantâneos. Não se perde
       * resposta nenhuma, porque não há resposta a dar - o que este cliente
       * carregar no teclado não move nada até o empurrão acabar.
       */
      /*
       * **Quem está DENTRO do Leviatã não é desenhado** (gate V28H.X).
       *
       * O casco do piloto viaja com a estrutura - a sala põe-no lá - e desenhar
       * as duas coisas no mesmo ponto punha uma nave a flutuar sobre o
       * helicóptero. "As naves dos jogadores devem sumir quando entrarem na
       * nave e reaparecer quando a habilidade acabar ou o helicóptero for
       * destruído": as duas metades saem de graça, porque isto é uma função do
       * frame - o piloto desaparece no tique em que `pl` o nomeia, e volta no
       * primeiro tique em que a estrutura já não o nomeia, seja porque o tempo
       * acabou ou porque ela morreu.
       */
      if (piloting.has(ship.seat)) {
        this.hideShip(ship.seat);
        /*
         * **O casco escondido continua a ser POSICIONADO** (gate V28H.X).
         *
         * É de onde sai a origem da mira - `localShipPosition` lê a posição do
         * sprite do casco - e escondê-lo sem o mover deixava-a presa no sítio
         * onde o piloto entrou. À medida que o helicóptero se afastava, o
         * vector rato-menos-origem apontava cada vez mais para o passado: o
         * dono viu-o como "a nave para de virar para o rato".
         *
         * Escondido e no sítio certo é a única combinação que não mente.
         */
        const seated = ship.seat === this.seat && local !== null ? local : ship;
        const at = { x: pvpWorldUnits(seated.x), y: pvpWorldUnits(seated.y) };
        this.hulls.get(ship.seat)?.setPosition(at.x, at.y);
        /*
         * **A posição continua a ser registada**, e é a câmara que depende
         * disso: ela segue o casco DESENHADO, e um casco escondido que também
         * deixasse de dizer onde está deixava-a sem alvo - foi exactamente o
         * que aconteceu, e o dono viu-o ("a câmara não segue o helicóptero").
         *
         * A sala põe o piloto no sítio da estrutura, portanto seguir este ponto
         * é seguir o helicóptero. E é a PREVISÃO deste cliente que fica
         * registada quando o piloto é ele: a estrutura é desenhada com a mesma
         * previsão, e uma câmara que seguisse a autoridade enquanto o desenho
         * segue a previsão deslizaria meio quadro atrás dela.
         */
        this.hullPositions.set(ship.seat, at);
        continue;
      }
      const pushed = ship.knockbackTicks > 0;
      const predicted = ship.seat === this.seat && !pushed ? local : null;
      const x = pvpWorldUnits(predicted ? predicted.x : ship.x);
      const y = pvpWorldUnits(predicted ? predicted.y : ship.y);
      /* Onde este casco ficou, para um `+VALOR` de cura poder nascer sobre ele. */
      this.hullPositions.set(ship.seat, { x, y });
      /*
       * Three roles, not two.
       *
       * With one opponent the middle is unreachable; with teams a team-mate has
       * to read as "with me" at a glance and still never be mistaken for the
       * ship the camera follows.
       *
       * The side is the frame's, not this scene's: `ship.team` is what the
       * authority said, and a renderer that worked one out from an id would be
       * inventing an alliance.
       *
       * It no longer decides a COLOUR for the ship. The role reaches the team
       * mark below and the projectiles, and the art itself is left as drawn.
       */
      const role = pvpShipRole(ship, me);

      /*
       * The ship the SERVER said this seat is flying.
       *
       * `null` only until the welcome names it, which is the one moment a
       * client legitimately does not know; the fallback profile is a real
       * appearance rather than a placeholder, so nothing below has a special
       * case for it.
       */
      const appearance = arena.appearance[ship.seat] ?? PVP_FALLBACK_APPEARANCE;
      this.requestArtFor(appearance);
      this.buildShipArt(ship.seat, appearance);

      const hull = this.hulls.get(ship.seat);
      if (!hull) {
        continue;
      }
      /*
       * **E o MANTO baixa a opacidade do casco** (gate V28H.V).
       *
       * Quem não é da equipa nunca chega aqui com este casco: a sala omite-o do
       * frame inteiro, e é isso que o esconde. Isto é para o DONO - "deve
       * continuar vendo uma silhueta translúcida para controlar a própria nave"
       * - e para os aliados, que continuam a vê-lo por decisão já tomada.
       *
       * A desconexão ganha, e é deliberado: uma nave que largou o teclado é a
       * informação mais urgente das duas.
       */
      const cloaked = cardVisualFlags(ship.cv).cloaked;
      hull.setPosition(x, y).setAlpha(ship.connected ? (cloaked ? CLOAK_HULL_ALPHA : 1) : 0.45);
      /*
       * The HULL turns towards where it is TRAVELLING.
       *
       * The local seat reads the keys. The opponent reads their displacement
       * between drawn positions - never their aim, or a player strafing while
       * shooting behind themselves would be drawn flying backwards.
       *
       * Computed BEFORE the barrels, because the hardpoints are bolted to the
       * hull and move with it. The hull decides where a cannon IS; the cursor
       * decides only which way it points.
       */
      const effects = frame.effects.find((entry) => entry.seat === ship.seat);
      const previous = this.lastDrawnAt.get(ship.seat) ?? null;
      const target =
        ship.seat === this.seat ? localTurn : pvpHullTargetFromMotion(previous, { x, y });
      const heading = pvpTurnHull(this.hullHeading.get(ship.seat) ?? 0, target, deltaMs);
      this.hullHeading.set(ship.seat, heading);
      this.lastDrawnAt.set(ship.seat, { x, y });
      hull.setRotation(pvpHullRotation(heading));

      /*
       * The skin rides the hull that was just placed.
       *
       * After the position and the rotation, before the battery: it is drawing
       * the SAME sprite the lines above moved, so it takes their answer rather
       * than deriving a second one. `visualScale` is the scene's own - the one
       * `pvpHullScale` produced para ESTA cadeira - so a skin cannot be drawn
       * at a size the ship is not.
       *
       * The speed is measured from where this hull was actually DRAWN last
       * frame, not from the authority's velocity: a trail is residue of motion
       * on screen, and a corrected or teleported ship moved on screen without
       * travelling.
       */
      const skin = this.skins.get(ship.seat);
      if (skin) {
        const drawnBefore = this.skinDrawnAt.get(ship.seat);
        const travelled = drawnBefore ? Math.hypot(x - drawnBefore.x, y - drawnBefore.y) : 0;
        this.skinDrawnAt.set(ship.seat, { x, y });
        /*
         * A TELEPORT is a jump, and a jump is not travel.
         *
         * A correction is announced and handled above; a teleport is not - it
         * is an ordinary frame in which a ship is simply somewhere else. Left
         * alone it would produce one enormous speed sample and a wake drawn
         * straight across the arena between two places the ship never crossed.
         *
         * The threshold is stated in HULLS rather than pixels so it means the
         * same thing at every visual scale and every zoom. No ship covers two
         * hull diameters in one frame at duel speeds; a teleport always does.
         */
        const jumped = travelled > this.hullDiameterOf(ship.seat) * 2;
        if (jumped) {
          skin.clearResidue();
        }
        skin.update(
          {
            x,
            y,
            rotation: pvpHullRotation(heading),
            hullWorldUnits: PVP_HULL_WORLD_UNITS,
            visualScale: this.hullScaleOf(ship.seat),
            speed: jumped || deltaMs <= 0 ? 0 : (travelled * 1_000) / deltaMs,
          },
          deltaMs,
        );
      }

      /*
       * Every CANNON turns, independently, and the hull does not follow them.
       *
       * The hull used to be given the aim as well, and the player described the
       * result exactly: "the whole ship spins to follow the cursor". A duel's
       * ship holds its own orientation and swings its guns. The sprites are
       * SIBLINGS of the hull on the scene, never children of it, so a hull
       * rotation cannot drag a barrel with it and there is no local angle to
       * compensate - which is why the rotation below is a world angle.
       *
       * Whose aim each follows differs, and only for the local seat. Aim is
       * felt instantly, and routing the player's own through the server makes
       * the barrel lag the cursor by a round trip plus the playback delay - so
       * the local battery is drawn from the local pointer. It costs nothing to
       * be early, because aim decides no damage here: the room fires, the room
       * says where a projectile is, and this rotation is a picture of a
       * decision already sent.
       *
       * The opponent's is never guessed - exactly what arrived, and never
       * turned towards anybody by itself. There is no auto-aim in a duel and
       * drawing one would promise a shot that does not exist.
       */
      const aim =
        ship.seat === this.seat && localAim !== null ? localAim : pvpHeading(ship.aimX, ship.aimY);
      const barrels = this.cannons.get(ship.seat) ?? [];
      const mounts = pvpCannonMounts(appearance);
      /*
       * ONE angle for the mount and for the sprite, which is what makes the
       * ship's centre the pivot.
       *
       * The mount used to ride the hull's heading while the sprite took the
       * aim. The two are independent here, so the moment they differed the
       * barrel sat at a hardpoint the hull had placed and pointed somewhere
       * else - spinning on its own centre, detached. `pvpCannonPivotAngle` is
       * where that is stated; this is the single call site that has to agree
       * with it.
       */
      const battery = pvpCannonPivotAngle(aim, heading);
      for (const [index, barrel] of barrels.entries()) {
        const mount = mounts[index];
        if (!mount) {
          continue;
        }
        const at = pvpMountInWorld(mount, x, y, battery, this.hullScaleOf(ship.seat));
        /* Os canhões acompanham o casco: uma silhueta com armas opacas não é
         * uma silhueta (gate V28H.V). */
        barrel
          .setPosition(at.x, at.y)
          .setAlpha(
            ship.connected ? (cardVisualFlags(ship.cv).cloaked ? CLOAK_HULL_ALPHA : 1) : 0.45,
          );
        /*
         * The SPRITE rotation, not the pivot angle.
         *
         * They shared a number until gate V27A and should not have: the pivot
         * is a world angle that places the mount, and the art is drawn facing
         * up. Rotating the sprite by the pivot drew every barrel a quarter turn
         * off the shots leaving it.
         */
        barrel.setRotation(pvpCannonSpriteRotation(battery));
      }

      /*
       * **Os dois canhões temporários, quando a sala diz que estão montados.**
       *
       * O facto vem do frame (`sc`) e a posição é derivada aqui: perpendicular
       * à mira, um de cada lado, atrás do centro. A geometria é a mesma que a
       * sala usa para saber de onde saem os tiros - `pvpSuperCannonLateralBps`
       * vive no domínio, e as duas pontas leem a mesma proporção.
       *
       * Ausentes num casco sem eles, e escondidos em vez de destruídos: o pool
       * é de dois por cadeira e sobrevive ao ciclo da habilidade.
       */
      this.syncSuperCannons(ship.seat, ship.sc === 1, x, y, battery);

      /*
       * **Os dois estados públicos das cartas de temporizador** (gate V28H.L).
       *
       * O gelo e o escudo, e nada mais: o que o adversário tem de perceber é
       * porque a nave parou de responder e porque os tiros dele atravessam. Os
       * dois vêm do frame - a sala decidiu - e nenhum deles é derivado aqui.
       *
       * Anéis à volta da arte, e nunca uma tinta sobre ela: o casco continua a
       * ser o casco que o jogador possui, e a cor dele continua a dizer de quem
       * é. É a mesma decisão que os indicadores de runa já tinham tomado.
       */
      this.syncCardAura(ship.seat, ship.fz === 1, x, y);
      /*
       * **E o Bastião, que já não é um anel** (gate V28H.U).
       *
       * `bt` vem do frame e diz que os tiros atravessam este casco: **toda a
       * gente** vê quem está invulnerável, e não só o dono. Começa e acaba com o
       * temporizador autoritativo, porque é ele que põe e tira a bandeira - e
       * uma morte, um abandono, um fim de partida ou uma correcção do mundo
       * fazem o casco sair do frame ou perder a bandeira, e a composição
       * desaparece no mesmo quadro.
       */
      this.syncBastion(ship.seat, ship.bt === 1, x, y);
      /*
       * **O avanço e a propulsão** (gate V28H.V), atrás do casco.
       *
       * O rumo é o do MOVIMENTO - `heading`, o mesmo com que o casco é desenhado
       * - e nunca o da mira: estas duas cartas marcam por onde a nave passou, e
       * não para onde ela olha. Durante um Arranque as duas coincidem, porque o
       * avanço vai na direcção apontada; a andar de lado não coincidem, e é o
       * movimento que tem razão.
       */
      this.syncMovementFx(ship.seat, ship.cv, x, y, heading, deltaMs);
      /* E os sinais que este casco leva em cima, e o hack que ele lança. */
      this.syncStatusFx(ship, x, y);
      /*
       * **A lâmina, o feixe e a carga** (gate V28H.L).
       *
       * Os três vêm do frame - a sala disse que esta carta está a desenhar isto
       * - e a geometria sai das MESMAS proporções que a simulação usa para
       * decidir quem eles apanham. Uma segunda expressão aqui seria um feixe
       * desenhado ao lado do sítio onde ele fere.
       */
      /*
       * **O feixe segue a MIRA, e a mira do SERVIDOR** (gate V28H.P).
       *
       * Duas correcções numa linha, e as duas vinham do mesmo engano: aqui
       * estava `heading`, que é para onde o CASCO aponta - derivado do movimento
       * dele. O feixe saía na direcção em que a nave andava e não na direcção
       * para onde o jogador apontava, e o adversário levava dano sem o feixe lhe
       * tocar, porque a sala cobrava ao longo da mira enquanto o ecrã desenhava
       * ao longo do movimento.
       *
       * E é `pvpHeading(ship.aimX, ship.aimY)` para TODAS as cadeiras, incluindo
       * a local - ao contrário dos canhões, que seguem o ponteiro local para não
       * ficarem atrás do cursor. A diferença é o que está em jogo: um canhão
       * desenhado cedo não decide nada, e um feixe decide dano. Ele tem de ser
       * desenhado onde ele cobra, mesmo que isso custe uma ida e volta.
       */
      this.syncCardVfx(
        ship.seat,
        ship.cv,
        ship.cg,
        ship.cw,
        x,
        y,
        /*
         * Sem mira - um casco parado no primeiro quadro - o feixe não tem
         * direcção nenhuma para seguir, e o casco é a única resposta honesta.
         * A sala nunca cobra nesse instante: sem mira não há feixe.
         */
        pvpHeading(ship.aimX, ship.aimY) ?? heading,
      );

      /*
       * And the ship says what it is carrying, from OUTSIDE its own art.
       *
       * This used to repaint the hull - red for Double Damage, green for Speed -
       * which said the thing loudly and cost two others: the ship stopped being
       * the ship the player owns, and the tint that had been saying whose it was
       * had nothing left to say it with. Both cues now live in their own layer
       * around the art, and neither touches a texture.
       *
       * The heading is passed because one of the two indicators means a
       * DIRECTION. It is the hull's own heading, already computed above from the
       * authority's positions or the local keys - not a second guess at it.
       */
      this.drawRuneIndicator(ship.seat, pvpActiveRuneEffect(effects), x, y, heading);

      /*
       * And the mark that says whose ship it is.
       *
       * Two independent statements about one ship, in two bands outside it: this
       * one is about WHOSE it is and never changes with an effect, the one above
       * is about what it is CARRYING and never changes with a role.
       */
      this.drawIdentity(ship.seat, role, x, y);

      /*
       * Contra o teto DESTE casco, e não contra a referência da arena.
       *
       * `arena.maxHealth` é a vida normalizada por que a escala divide, e nunca
       * foi o denominador de uma barra - o `wire.ts` já o dizia. Enquanto todos
       * os cascos tinham mil os dois números coincidiam; a partir do Coração do
       * Titã não coincidem, e o partilhado mostraria uma nave resistente
       * permanentemente ferida.
       */
      const written = arena.combatProfile[ship.seat]?.maxHealthMilli;
      const ceiling = written === undefined ? BigInt(arena.maxHealthMilli) : BigInt(written);
      this.drawHealth(ship.seat, role, x, y, pvpHealthFraction(ship.hpMilli, ceiling));
      /* E o nome, por cima da barra: as duas dizem de quem é a nave. */
      this.drawNameTag(ship.seat, x, y);
      /* E a palavra INVISÍVEL, por cima do nome, enquanto o Manto durar. */
      this.drawCloakTag(ship.seat, cardVisualFlags(ship.cv).cloaked, x, y);
    }

    /*
     * Whoever is no longer in the frame is no longer on the screen.
     *
     * A participant is DROPPED from the roster when they die, and nothing here
     * used to notice: the loop above only ever created and updated, so a hull,
     * its battery, its bar and now its team mark would all stay
     * exactly where they were last drawn - a ghost ship, at full health, in a
     * fight it has left.
     *
     * It could not happen in a 1x1, where both ships are in every frame until
     * the room says the match is over, which is why it survived this long.
     */
    /*
     * E as esferas a carregar, TODAS de uma vez (gate V28H.U): quem as recebe
     * apaga as que não vêm na lista, e é assim que uma tecla solta apaga a bola.
     */
    this.cardBlasts?.charging(this.chargingSpheres, this.time.now);

    this.retireAbsent(new Set(frame.ships.map((ship) => ship.seat)));

    /*
     * **As marcas corrosivas** (gate V28G.M).
     *
     * Depois de os cascos estarem desenhados, porque a âncora de uma marca é o
     * topo de uma barra de vida - e a barra acabou de ser posta no sítio, na
     * escala visual desta arena. Um segundo cálculo do levantamento seria uma
     * segunda fórmula para a mesma linha.
     *
     * Os stacks vêm do frame AUTORITATIVO e são agrupados por casco. A cena não
     * decide se existem, quanto duram ou quando pulsam: ela desenha o que a sala
     * diz que existe, e o pulso é a contagem de parcelas a subir.
     */
    /*
     * **AS SERRAS, derivadas do estado autoritativo** (gate V28G.P).
     *
     * ## O que a cena recebe, e o que ela nunca recebe
     *
     * `sw` - quantas lâminas - e `sf` - a fase da órbita. Dois inteiros. Nunca o
     * nível, nunca o perfil, nunca o id da joia: uma cena que recebesse um nível
     * seria uma cena a decorar um inventário.
     *
     * ## Porque as posições são calculadas aqui e não viajam
     *
     * Porque são DERIVÁVEIS, exatamente, da fase e do raio - e o cálculo é o
     * mesmo que a simulação faz, na mesma tabela de direções inteiras. Quatro
     * pares de coordenadas por casco seriam oito inteiros vinte vezes por
     * segundo para dizer o que um inteiro já diz, e seriam oito inteiros que um
     * frame podia juntar de dois tiques diferentes.
     *
     * ## A geometria sai do CORPO autoritativo, e não da arte
     *
     * `PVP_SHIP_RADIUS_MU` é o raio que colide, e é dele que a simulação tira a
     * órbita e o tamanho da lâmina. Usá-lo aqui também é o que faz a serra que
     * se vê ser exatamente a serra que corta - que é o que o contrato exige, e
     * que uma escala de arte diferente da do casco quebraria em silêncio.
     */
    const blades: (SawBladeView & { readonly owner: string })[] = [];
    const orbit = pvpWorldUnits(sawOrbitRadius(PVP_SHIP_RADIUS_MU));
    const bladeRadius = pvpWorldUnits(sawBladeRadius(PVP_SHIP_RADIUS_MU));
    for (const ship of frame.ships) {
      const count = ship.sw ?? 0;
      const at = this.hullPositions.get(ship.seat);
      if (count <= 0 || ship.sf === undefined || at === undefined) {
        continue;
      }
      for (let index = 0; index < count; index += 1) {
        const step = sawBladeStep(ship.sf, index, count);
        const direction = sawDirection(step);
        blades.push({
          owner: ship.seat,
          index,
          x: at.x + (direction.x * orbit) / SAW_VECTOR_SCALE,
          y: at.y + (direction.y * orbit) / SAW_VECTOR_SCALE,
          step,
          radius: bladeRadius,
        });
      }
    }
    this.sawBlades?.sync(blades);

    const stacksByTarget = new Map<PvpParticipantId, { id: number; ticks: number }[]>();
    for (const stack of frame.poison) {
      const existing = stacksByTarget.get(stack.q);
      const entry = { id: stack.i, ticks: stack.n };
      if (existing) {
        existing.push(entry);
      } else {
        stacksByTarget.set(stack.q, [entry]);
      }
    }
    const marked: PoisonMarkTarget[] = [];
    for (const ship of frame.ships) {
      const stacks = stacksByTarget.get(ship.seat);
      const at = this.hullPositions.get(ship.seat);
      if (!stacks || at === undefined) {
        continue;
      }
      marked.push({
        key: ship.seat,
        x: at.x,
        /*
         * O TOPO da barra, derivado dela e não afinado: `BAR_LIFT` acompanha a
         * escala visual e a barra tem `BAR_HEIGHT` de altura, portanto isto é a
         * borda de cima dela em qualquer escala e sem um número escolhido à mão.
         * É a mesma derivação que o `+VALOR` da cura já faz.
         */
        y: at.y - (BAR_LIFT * this.hullScaleOf(ship.seat) + BAR_HEIGHT / 2),
        /*
         * O diâmetro REAL do casco desenhado, na mesma fonte que o dimensiona.
         * Zoom e escala visual acompanham sozinhos porque este número já os
         * inclui.
         */
        diameter: this.hullDiameterOf(ship.seat),
        stacks,
      });
    }
    this.poisonMarks?.sync(marked, this.time.now);

    /*
     * **As marcas de sangramento, na faixa DE CIMA** (gate V28G.N).
     *
     * ## Porque a âncora não é a mesma do veneno
     *
     * Porque o §6 exige uma faixa diferente, e "diferente" tem de ser verdade
     * com uma gota e com trinta. A âncora aqui é o topo da barra MAIS a altura
     * que as gotas ocupam - perguntada ao módulo do veneno, e nunca afinada - o
     * que faz as duas faixas não se tocarem em escala nenhuma e faz a de baixo
     * subir sozinha quando o veneno acaba.
     *
     * Nenhum dos dois módulos conhece o outro: a cena é que os empilha, porque a
     * cena é a única coisa que sabe que os dois existem.
     */
    const marksByTarget = new Map<PvpParticipantId, { id: number }[]>();
    for (const mark of frame.bleeding) {
      /*
       * Todas as marcas, de quem quer que sejam.
       *
       * Um casco com marcas de dois atacantes mostra as duas contagens, porque
       * as duas existem sobre ele - o que a autoria decide é quem as PODE
       * rebentar, e isso é o botão, não o desenho.
       */
      const existing = marksByTarget.get(mark.q);
      const entry = { id: mark.i };
      if (existing) {
        existing.push(entry);
      } else {
        marksByTarget.set(mark.q, [entry]);
      }
    }
    const cut: BleedMarkTarget[] = [];
    for (const ship of frame.ships) {
      const marks = marksByTarget.get(ship.seat);
      const at = this.hullPositions.get(ship.seat);
      if (!marks || at === undefined) {
        continue;
      }
      const barTop = at.y - (BAR_LIFT * this.hullScaleOf(ship.seat) + BAR_HEIGHT / 2);
      const diameter = this.hullDiameterOf(ship.seat);
      const poisonCount = stacksByTarget.get(ship.seat)?.length ?? 0;
      cut.push({
        key: ship.seat,
        x: at.x,
        /*
         * O topo da faixa do veneno, DERIVADO dela com os mesmos números que a
         * desenharam - a largura de uma gota e a largura disponível. Zero quando
         * não há veneno nenhum, e então esta faixa começa onde a outra começaria.
         */
        y:
          barTop -
          poisonBandHeight(
            poisonCount,
            Math.max(POISON_MARK_MIN_PX, diameter * POISON_MARK_TARGET_RATIO),
            Math.max(POISON_MARK_MIN_PX, diameter),
          ),
        diameter,
        marks,
      });
    }
    this.bleedMarks?.sync(cut);
    /* E os rebentamentos avançam um quadro, no relógio da CENA. */
    this.bleedBursts?.update(this.time.now);
  }

  /**
   * Draws one participant's team mark, or leaves the existing one alone.
   *
   * Idempotent by role and size: the signature changes at most twice in a duel -
   * once when the profile's scale is known, once if a participant's role could
   * change - so the `clear`/`stroke` pair below runs then and never per frame.
   * Everything a frame does here is `setPosition`, which allocates nothing.
   */
  private drawIdentity(
    seat: PvpParticipantId,
    role: PvpParticipantRole,
    x: number,
    y: number,
  ): void {
    const signature = `${role}/${this.hullScaleOf(seat)}`;
    let mark = this.identities.get(seat);
    if (!mark) {
      mark = this.add.graphics().setDepth(DEPTH.identity);
      this.identities.set(seat, mark);
    }
    if (this.identityBuiltFrom.get(seat) !== signature) {
      this.identityBuiltFrom.set(seat, signature);
      const spec = pvpIdentityMark(role, PVP_HULL_WORLD_UNITS, this.hullScaleOf(seat));
      mark.clear();
      mark.lineStyle(spec.lineWidth, spec.colour, spec.alpha);
      if (spec.shape.kind === 'rings') {
        for (const radius of spec.shape.radii) {
          mark.strokeCircle(0, 0, radius);
        }
      } else if (spec.shape.kind === 'arcs') {
        /*
         * Short arcs on screen axes, and never rotated - the same rule the
         * brackets follow, and for the same reason: a label that turned with
         * the hull would read as a different shape depending on which way a
         * ship happened to be facing.
         */
        const { radius, sweep, angles, tick } = spec.shape;
        for (const angle of angles) {
          mark.beginPath();
          mark.arc(0, 0, radius, angle - sweep, angle + sweep, false);
          mark.strokePath();
        }
        if (tick) {
          /*
           * The local ship's own mark: a small filled wedge below the hull.
           *
           * Filled where everything else here is a stroke, which is what makes
           * "is that mine" one glance rather than a count of arcs - and it sits
           * BELOW, where the health bar (which floats above) can never meet it.
           */
          mark.fillStyle(spec.colour, spec.alpha);
          mark.beginPath();
          mark.moveTo(-tick.halfWidth, tick.radius);
          mark.lineTo(tick.halfWidth, tick.radius);
          mark.lineTo(0, tick.radius + tick.halfWidth * 1.6);
          mark.closePath();
          mark.fillPath();
        }
      } else {
        /*
         * Four corner brackets, in SCREEN axes and never rotated.
         *
         * The mark is a label, not part of the ship: turning it with the hull
         * or with the cannon would make an enemy's bracket read as a different
         * shape depending on which way they happened to be facing, which is
         * exactly the thing a shape-based cue exists to avoid.
         */
        const { radius, arm } = spec.shape;
        for (const sx of [-1, 1]) {
          for (const sy of [-1, 1]) {
            mark.beginPath();
            mark.moveTo(sx * radius, sy * (radius - arm));
            mark.lineTo(sx * radius, sy * radius);
            mark.lineTo(sx * (radius - arm), sy * radius);
            mark.strokePath();
          }
        }
      }
    }
    mark.setPosition(x, y);
  }

  /**
   * Draws what a ship is carrying, outside the ship, or takes it away.
   *
   * ## Where the answer comes from
   *
   * `kind` is `pvpActiveRuneEffect(frame.effects)` and nothing else. There is no
   * timer here, no memory of a pickup, no collision and no animation whose end
   * means anything: the room publishes the remaining ticks, and the frame that
   * stops carrying them is the frame this stops drawing. A local clock reaches
   * exactly one property - the alpha - and only while the authority still says
   * the effect is running.
   *
   * ## What a frame costs
   *
   * A position, a rotation and an alpha. The geometry is rebuilt only when the
   * rune, the drawn size or the CAMERA'S ZOOM changes - the first two at most a
   * handful of times in a duel, the third once per wheel notch - so no frame
   * allocates and no frame issues a `clear`.
   *
   * The zoom is in that list because Double Damage is held to a minimum
   * projected size (`pvpRuneIndicatorScale`), and a mark whose stroke were
   * scaled by `setScale` instead would be redrawn nowhere and would still be a
   * two-pixel line at zoom 0.4. Baking the factor into the geometry is what lets
   * the stroke grow with it.
   */
  /**
   * **Os dois canhões temporários de um casco** (gate V28H.J).
   *
   * ## O que ele lê, e o que ele NÃO decide
   *
   * Lê um facto que a sala publicou. Não sabe o que é um arrefecimento, não sabe
   * que carta está no espaço, e não decide quando eles aparecem: se a sala não
   * disser que estão montados, eles não estão.
   *
   * ## A geometria
   *
   * Perpendicular à MIRA e atrás do centro, na mesma proporção que a sala usa
   * para o ponto de origem dos tiros. As duas pontas leem a mesma constante do
   * domínio, e é isso que impede um jogador de levar tiros de uma arma desenhada
   * noutro sítio.
   */
  /**
   * **O gelo e o escudo: os dois estados públicos das cartas** (gate V28H.L).
   *
   * ## O que cada um diz, e porque tem de ser dito
   *
   * - **congelado** (`fz`): a nave parou e não dispara. Sem uma marca, um
   *   adversário lê isso como um jogador que largou o teclado, e um aliado lê
   *   como um problema de rede;
   * - **inalvejável** (`bt`): os tiros atravessam-na. Sem uma marca, isso
   *   lê-se como o jogo partido - que é a pior coisa que uma defesa pode
   *   parecer.
   *
   * ## Um anel, e nunca uma tinta sobre a arte
   *
   * A mesma decisão que os indicadores de runa tomaram: repintar o casco tirava
   * ao jogador a cor que diz de quem ele é. O anel vive por fora, numa camada
   * própria, e não toca numa textura.
   *
   * ## Pooling
   *
   * Um anel por cadeira, criado à primeira vez que faz falta e depois
   * **escondido** em vez de destruído - o ciclo destas cartas é de segundos, e
   * criar e destruir um objecto a cada activação seria trabalho por activação
   * numa cena que já tem um orçamento. Vão-se embora com a cadeira, em
   * `retireParticipant`.
   */
  /**
   * **O Bastião de um casco**, ou nada (gate V28H.U).
   *
   * Escondido em vez de destruído enquanto a cadeira existir: a carta dura
   * quatro segundos e volta, e o objecto sai de vez com o participante, em
   * {@link retireParticipant}.
   *
   * O raio é o do CASCO desenhado, e é dele que a composição mede tudo - as
   * estrelas nascem à volta da nave que está protegida, e não a uma distância
   * escolhida aqui.
   */
  /**
   * **O rasto do Arranque e os motores da Sobremarcha** (gate V28H.V).
   *
   * Escondidos em vez de destruídos enquanto a cadeira existir: o Arranque dura
   * um quarto de segundo e volta de três em três, e criar oito objectos por
   * activação seria trabalho por activação. Saem de vez com o participante, em
   * {@link retireParticipant}.
   */
  private syncMovementFx(
    seat: PvpParticipantId,
    visuals: readonly ('o' | 'b' | 'l' | 'c' | 'g' | 'd' | 'h' | 'k' | 'w' | 'm')[] | undefined,
    x: number,
    y: number,
    heading: number,
    deltaMs: number,
  ): void {
    const flags = cardVisualFlags(visuals);
    const existing = this.cardMovement.get(seat);
    if (!flags.dash && !flags.haste) {
      existing?.update(null, this.time.now, deltaMs);
      return;
    }
    const fx = existing ?? new MovementFx(this, Phaser.BlendModes.ADD, DEPTH.projectileTrail);
    this.cardMovement.set(seat, fx);
    fx.update(
      {
        x,
        y,
        heading,
        hullRadius: this.hullDiameterOf(seat) / 2,
        haste: flags.haste,
        dash: flags.dash,
        /*
         * A cor é de quem OLHA, como em todos os efeitos de carta desde o gate
         * V28H.R - e o dono pediu-a por nome para a chama: "a cor do fogo muda
         * conforme o utilizador escolhe".
         */
        tint: cardEffectTint(),
      },
      this.time.now,
      /*
       * O tempo do QUADRO, para as riscas de vento andarem por segundo e não
       * por quadro: a mesma rajada percorre o mesmo caminho a 60 e a 144 Hz.
       */
      deltaMs,
    );
  }

  /**
   * **Os sinais de debuff, e o pulso de quem lança o Malware** (gate V28H.W).
   *
   * Tudo o que se desenha aqui vem do FRAME: a sala diz quem está infectado,
   * quanta casca de gelo desenhar, quem está paralisado por inteiro e quem está
   * lento. A cena não decide nenhuma dessas coisas - ela nem sequer sabe que
   * existe um sorteio.
   */
  private syncStatusFx(ship: PvpRenderShip, x: number, y: number): void {
    const hullRadius = this.hullDiameterOf(ship.seat) / 2;
    const status = this.cardStatus.get(ship.seat);
    const anything =
      ship.mw === 1 ||
      ship.pz === 1 ||
      ship.lt === 1 ||
      (ship.ft ?? 0) > 0 ||
      ship.cc === 1 ||
      ship.sk === 1;
    if (!anything && status === undefined) {
      return;
    }
    const fx =
      status ??
      new StatusFx(
        this,
        Phaser.BlendModes.ADD,
        DEPTH.identity,
        DEPTH.bar + 1,
        /*
         * O CHÃO: abaixo do rasto de um tiro e acima de uma pilastra. Um selo
         * deitado na arena tem de passar por baixo de tudo o que voa - a nave, os
         * tiros e os rastos deles - senão ele não está no chão, está na lente.
         */
        DEPTH.projectileTrail - 3,
      );
    this.cardStatus.set(ship.seat, fx);
    fx.update(
      {
        x,
        y,
        hullRadius,
        /* O levantamento do nome e da barra, para a estrela ficar acima deles. */
        lift: BAR_LIFT * this.hullScaleOf(ship.seat) + NAME_TAG_LIFT,
        /* E o que o nome ocupa acima disso, para nada lhe cair em cima. */
        nameHeight: NAME_TAG_HEIGHT,
        malware: ship.mw === 1,
        frostTier: ship.ft ?? 0,
        paralysed: ship.pz === 1,
        slowed: ship.lt === 1,
        attackBroken: ship.cc === 1,
        marked: ship.sk === 1,
      },
      this.time.now,
    );

    /*
     * E o hack, no casco de QUEM LANÇOU. A letra acende enquanto a carta dele
     * corre; o pulso é curto e ignora repetições, portanto ele toca uma vez por
     * lançamento em vez de a cada quadro.
     */
    const visuals = cardVisualFlags(ship.cv);
    /*
     * **E o pulso da MARCA, pelo mesmo caminho** (gate V28H.X): a carta fica
     * acesa os seis segundos do debuff, e o pulso é curto e ignora repetições -
     * portanto ele toca uma vez por lançamento, e não durante seis segundos.
     */
    const marking = visuals.markCast;
    const mark = this.markCasts.get(ship.seat);
    if (marking || mark !== undefined) {
      const stamp = mark ?? new MarkCastFx(this, DEPTH.identity);
      this.markCasts.set(ship.seat, stamp);
      /*
       * O estado da carta, e não uma ordem: é ele que compara com o quadro
       * anterior e arranca no flanco - a letra fica acesa seis segundos, e o
       * pulso é para assinalar o uso.
       */
      stamp.sync(marking, this.time.now);
      stamp.update(x, y, hullRadius, this.time.now);
    }
    const casting = visuals.malwareCast;
    const cast = this.malwareCasts.get(ship.seat);
    if (!casting && cast === undefined) {
      return;
    }
    const pulse = cast ?? new MalwareCastFx(this, DEPTH.identity);
    this.malwareCasts.set(ship.seat, pulse);
    if (casting) {
      pulse.fire(this.time.now);
    }
    pulse.update(x, y, hullRadius, this.time.now);
  }

  private syncBastion(seat: PvpParticipantId, protectedNow: boolean, x: number, y: number): void {
    const existing = this.cardBastions.get(seat);
    if (!protectedNow) {
      existing?.update(null, this.time.now);
      return;
    }
    const fx = existing ?? new BastionFx(this, Phaser.BlendModes.ADD, DEPTH.identity);
    this.cardBastions.set(seat, fx);
    fx.update({ x, y, hullRadius: this.hullDiameterOf(seat) / 2 }, this.time.now);
  }

  private syncCardAura(
    seat: PvpParticipantId,
    frozen: boolean,
    x: number,
    y: number,
    /** Uma cor e um raio para quem o reaproveita. A carga é o único caso. */
    tint?: number,
    radius?: number,
  ): void {
    /*
     * **Só o GELO** (gate V28H.U), e já não o escudo.
     *
     * O `bt` entrava aqui, e as duas cartas partilhavam um anel: a nave
     * congelada e a nave invulnerável liam-se igual, com uma diferença de cor. O
     * Bastião tem agora a sua composição, e este anel voltou a dizer uma coisa
     * só - que é o que um anel consegue dizer bem.
     */
    const wanted = frozen || tint !== undefined;
    const existing = this.cardAuras.get(seat);
    if (!wanted) {
      existing?.setVisible(false);
      return;
    }
    const diameter = this.hullDiameterOf(seat);
    const ring =
      existing ??
      this.add
        .circle(x, y, diameter * 0.62)
        .setStrokeStyle(3, 0xffffff, 0.9)
        .setFillStyle()
        .setDepth(DEPTH.identity);
    this.cardAuras.set(seat, ring);
    ring.setVisible(true);
    ring.setPosition(x, y);
    ring.setRadius(radius ?? diameter * 0.62);
    /*
     * O gelo ganha ao escudo quando os dois correm: parado é a informação mais
     * urgente das duas, e dois anéis sobrepostos no mesmo casco seriam
     * exactamente o que a direcção de arte proíbe.
     */
    ring.setStrokeStyle(3, tint ?? 0x7fd8ff, 0.95);
  }

  /**
   * **A lâmina do Turbilhão e o feixe da Lança Solar** (gate V28H.L).
   *
   * Dois rectângulos por cadeira, criados à primeira vez que fazem falta e
   * depois **escondidos** em vez de destruídos: o ciclo destas cartas é de
   * segundos, e criar e destruir um objecto por activação seria trabalho por
   * activação numa cena que já tem um orçamento.
   *
   * A carga aparece como um anel a apertar - a antecipação que a direcção de
   * arte pede - e é ela que diz ao adversário que alguém está preso a carregar.
   */
  /**
   * **O círculo, a serra e a lança de um casco** (gate V28H.O).
   *
   * ## As mesmas classes da caça, e é o ponto
   *
   * O dono aprovou a apresentação na caça e mandou-a para cá inteira. Reescrevê-la
   * aqui seria garantir que os dois modos divergem - foi exactamente o que
   * aconteceu com a lista de `cv`, que o duelo nunca chegou a publicar.
   *
   * ## De quem é a cor
   *
   * De ninguém: **todos** os efeitos usam a cor que ESTE jogador escolheu
   * (gate V28H.R). Quem é quem diz-se pela barra de vida - verde do meu lado,
   * vermelha do outro - e pelo nickname por cima do casco, que são coisas da
   * NAVE. Uma paleta que só valesse para a própria nave não seria uma paleta.
   *
   * A Lança fica no seu dourado, como na caça: as texturas dela são solares por
   * desenho aprovado, e o selector nunca lhes tocou.
   */
  /**
   * **Onde e quão grande está a esfera de um casco a carregar**, ou `null`.
   *
   * Uma função à parte porque ela é aritmética e não desenho: o raio sai do
   * MESMO caminho que a sala percorre quando a tecla se solta - o alcance em
   * basis points do raio do casco, graduado pela força já cumprida - e a fracção
   * do corpo é a mesma que a caça desenha. Escrita aqui, provável sem um
   * renderizador.
   *
   * A força tem o mesmo CHÃO que a sala aplica: um toque curto não produz uma
   * bola de tamanho zero, produz a mínima. Desenhar sem o chão mostraria uma
   * esfera a nascer do nada e a saltar para o tamanho mínimo no instante do
   * disparo.
   */
  private chargingSphereOf(
    seat: PvpParticipantId,
    chargePercent: number | undefined,
    x: number,
    y: number,
    heading: number,
    hullRadius: number,
  ): { key: string; x: number; y: number; radius: number } | null {
    /*
     * **A aritmética saiu daqui para `card-vfx.ts`** (gate V28I.P.1), sem mudar
     * um número: o mundo aberto carrega a mesma esfera, e uma segunda cópia da
     * mesma curva seria a segunda oportunidade de a bola mentir num dos modos.
     * O que ficou é o que é do DUELO: o alcance desta arena, e a chave.
     */
    const geometry = chargingSphereGeometry({
      chargePercent,
      x,
      y,
      heading,
      hullRadius,
      reachBps: PVP_FIREBALL_REACH_BPS,
      radiusFor: cardSphereRadiusFor,
      bodyFraction: CARD_FIREBALL_BODY_FRACTION,
    });
    return geometry === null ? null : { key: seat, ...geometry };
  }

  private syncCardVfx(
    seat: PvpParticipantId,
    visuals: readonly ('o' | 'b' | 'l' | 'c' | 'g' | 'd' | 'h' | 'k' | 'w' | 'm')[] | undefined,
    chargePercent: number | undefined,
    beamPercent: number | undefined,
    x: number,
    y: number,
    heading: number,
  ): void {
    const flags = cardVisualFlags(visuals);
    const diameter = this.hullDiameterOf(seat);
    const hullRadius = diameter / 2;
    /*
     * **A ESFERA a carregar, à frente da boca** (gate V28H.U).
     *
     * A letra `g` diz que é a Esfera Ígnea que carrega, e não a Lança: as duas
     * desenham coisas diferentes, e antes de a letra existir a cena punha a
     * corona da Lança por cima de uma esfera que ninguém via.
     *
     * O raio é o que ela TERÁ se soltar neste instante - a mesma função que a
     * sala vai usar, sobre o mesmo alcance - portanto a bola que cresce é a bola
     * que sai, e o adversário pode julgar o que vem na direcção dele. Nunca um
     * tamanho de arte escolhido aqui: seria uma promessa que a sala não paga.
     */
    const sphere = flags.sphere
      ? this.chargingSphereOf(seat, chargePercent, x, y, heading, hullRadius)
      : null;
    if (sphere !== null) {
      this.chargingSpheres.push(sphere);
    }

    /* O CÍRCULO e a SERRA, na cor de quem os vê. */
    const circle = this.cardCircles.get(seat);
    if (!flags.circle) {
      circle?.update(null, this.time.now);
    } else {
      const fx = circle ?? new MagicCircleFx(this, Phaser.BlendModes.ADD, DEPTH.identity);
      this.cardCircles.set(seat, fx);
      fx.update(
        {
          x,
          y,
          /*
           * A mesma proporção com que a sala decide quem a serra apanha - o
           * alcance é basis points do raio do casco. Um número escolhido aqui
           * seria um círculo a prometer uma área que não é a que fere.
           */
          radius: bladeOrbitRadius(diameter, CARD_BALANCE.card_blade_storm.reachBps),
          active: flags.blade,
          tint: cardEffectTint(),
        },
        this.time.now,
      );
    }

    /* A LANÇA: a carga, o feixe, e a dissipação depois dele. */
    const lance = this.cardLances.get(seat);
    if (!flags.beam && !flags.charging && lance === undefined) {
      return;
    }
    const fx = lance ?? new SolarLanceFx(this, Phaser.BlendModes.ADD, DEPTH.identity);
    this.cardLances.set(seat, fx);
    /*
     * A BOCA, e não o centro do casco: é de lá que a sala mede o feixe desde o
     * gate V28H.N, e desenhá-lo do centro punha-o por cima da nave.
     */
    const muzzleX = x + Math.cos(heading) * hullRadius;
    const muzzleY = y + Math.sin(heading) * hullRadius;
    const strengthBps = ((beamPercent ?? 100) * 10_000) / 100;
    fx.update(
      {
        charge: flags.charging
          ? { x: muzzleX, y: muzzleY, progress: (chargePercent ?? 0) / 100 }
          : null,
        beam: flags.beam
          ? {
              x: muzzleX,
              y: muzzleY,
              angle: heading,
              length: (hullRadius * PVP_SOLAR_LANCE_REACH_BPS) / 10_000,
              /* A espessura TOTAL, graduada pela carga - a mesma que a sala cobra. */
              width:
                cardBeamWidthFor((hullRadius * PVP_SOLAR_LANCE_WIDTH_BPS) / 10_000, strengthBps) *
                2,
            }
          : null,
        hullRadius,
      },
      this.time.now,
    );
  }

  /**
   * **As invocações desenhadas** (gate V28H.L).
   *
   * ## O Drone usa o sprite do CHEFE DO MAPA 1, e é temporário
   *
   * Decisão do dono, e dita como provisória por ele. O duelo não carregava arte
   * de chefe nenhuma - ela é da caça - portanto ela é pedida com o mesmo
   * carregador preguiçoso que o casco e o canhão já usam, e o drone desenha-se
   * como uma forma até ela chegar. Uma textura que não chegue deixa a forma, e
   * nunca um quadro em branco.
   *
   * ## O Leviatã não tem arte, e por isso é uma FORMA
   *
   * O contrato registou o que ele há-de ser - helicóptero com entradas
   * laterais e setas animadas - e nada disso existe como ficheiro. Inventar um
   * PNG seria inventar arte, que o `CLAUDE.md` proíbe; o que se desenha é o que
   * a simulação garante: um corpo grande, a direcção para onde ele aponta, e
   * **duas entradas laterais marcadas**, que é o que diz a um jogador por onde
   * se entra.
   *
   * ## Pooling
   *
   * Um contentor por ID de invocação. Uma que saia da lista é destruída, e não
   * escondida: ao contrário de uma lâmina, ela não volta - o id é irrepetível.
   */
  private syncSummons(
    frame: PvpRenderFrame,
    local: PvpPredictedPoint | null,
    localAim: number | null,
  ): void {
    const present = new Set<number>();
    for (const summon of frame.summons ?? []) {
      present.add(summon.i);
      const owner = frame.ships.find((ship) => ship.seat === summon.p);
      const diameter = summonDiameter(
        summon.k,
        this.hullDiameterOf(summon.p),
        PVP_HULL_WORLD_UNITS * LEVIATHAN_MIN_HULLS,
      );
      /*
       * **O Drone usa o sprite do CHEFE DO MAPA 1** (gate V28H.P).
       *
       * Decisão do dono, dita como provisória por ele, e ela vale nos DOIS
       * modos - o duelo desenhava uma forma, e ele foi procurar o PNG. A arte é
       * da caça, portanto é pedida com o mesmo carregador preguiçoso que o casco
       * e o canhão já usam; enquanto ela não chega, a forma continua a ser
       * desenhada, e nunca um quadro em branco.
       */
      if (summon.k === 'd') {
        this.requestDroneTexture();
      } else {
        this.requestLeviathanTexture();
      }
      let container = this.summonSprites.get(summon.i);
      const art = summon.k === 'd' ? PVP_DRONE_TEXTURE : PVP_LEVIATHAN_BODY_TEXTURE;
      const wanted = this.textures.exists(art) ? 'sprite' : 'shape';
      if (container !== undefined && this.summonBuiltAs.get(summon.i) !== wanted) {
        /* A arte chegou depois da forma: o contentor é refeito, e uma vez só. */
        container.destroy(true);
        this.summonSprites.delete(summon.i);
        container = undefined;
      }
      if (container === undefined) {
        container = this.buildSummon(summon.k, diameter, String(owner?.team ?? 'a'));
        this.summonSprites.set(summon.i, container);
        this.summonBuiltAs.set(summon.i, wanted);
      }
      /*
       * **Quem PILOTA vê a estrutura responder já** (gate V28H.X).
       *
       * A posição autoritativa chega a 20 Hz e vem interpolada, o que é
       * suficiente para quem OLHA - e não é para quem CONDUZ: entre carregar
       * numa tecla e a sala responder há uma ida e volta, e a arena inteira
       * arrasta-se atrás dela porque a câmara vai agarrada. Era isso que se
       * sentia como movimento pesado.
       *
       * A sala mantém o piloto exactamente onde a estrutura está, portanto a
       * previsão que já existe para o casco local vale para ela também: as duas
       * andam com a mesma velocidade e pelas mesmas regras. Desenhar aqui a
       * previsão é dar ao condutor a resposta imediata que ele tem quando pilota
       * a própria nave, e não inventa movimento nenhum - ela perde para a sala
       * assim que as duas discordarem, como sempre.
       *
       * Para toda a gente MENOS o piloto, continua a valer a interpolação: um
       * adversário nunca vê uma previsão que não é dele.
       */
      const driven = local !== null && summon.pl !== undefined && summon.pl === this.seat;
      const at = driven && local ? local : { x: summon.x, y: summon.y };
      container.setPosition(pvpWorldUnits(at.x), pvpWorldUnits(at.y));
      /*
       * **A estrutura aponta para onde o piloto aponta** (gate V28H.X).
       *
       * A sala copia a mira do piloto para ela a cada tique, portanto o rumo
       * autoritativo já é esse - mas chega uma ida e volta depois, e quem
       * conduz sente o nariz a arrastar-se atrás do rato. Para o piloto LOCAL a
       * mira lida é a dele, agora, exactamente como os canhões da nave dele já
       * fazem; para toda a gente vale o que a sala disse.
       */
      const aimed = driven && localAim !== null ? localAim : Math.atan2(summon.ay, summon.ax);
      container.setRotation(aimed);
      /* A vida chega em basis points: a barra desenha a razão que a sala mandou. */
      setSummonHealth(container, summon.hp);
      /*
       * **As setas de embarque piscam ENQUANTO ninguém entrou** (gate V28H.X).
       *
       * `pl` é quem pilota, e a ausência dele é a estrutura à espera. O campo já
       * viajava desde o gate V28H.L e o duelo nunca o leu - a cena desenhava um
       * Leviatã ocupado exactamente como um vazio, e a única carta do jogo que
       * convida alguém a aproximar-se não convidava ninguém.
       */
      /*
       * **A hélice gira, e só ela** (gate V28H.X).
       *
       * O ângulo vem do relógio da CENA e não de um acumulador: é uma função do
       * tempo, portanto não deriva, não precisa de estado por invocação, e uma
       * aba escondida não a faz acelerar quando volta.
       */
      spinSummonRotor(container, this.time.now);
      setSummonBoardingArrows(container, summon.pl === undefined, this.time.now);
    }
    for (const id of [...this.summonSprites.keys()]) {
      if (present.has(id)) {
        continue;
      }
      this.summonSprites.get(id)?.destroy(true);
      this.summonSprites.delete(id);
    }
  }

  /** O corpo de uma invocação: uma forma, uma direcção e uma barra de vida. */
  /**
   * Pede a folha do chefe do mapa 1, uma vez.
   *
   * Uma folha 3x3 e não uma imagem: é o mesmo ficheiro que a caça carrega, e
   * carregá-lo como imagem daria um drone com os nove quadros lado a lado.
   */
  /**
   * Pede o PNG do Apache Omega, uma vez, e sem parar a cena se ele faltar.
   *
   * A mesma rede do Drone: enquanto a arte não chega desenha-se a forma, e se o
   * ficheiro falhar a chave sai de `pendingTextures` para o pedido poder ser
   * repetido. Uma invocação sem sprite é provisória; uma cena que rebentasse por
   * causa de um ficheiro em falta era uma partida perdida.
   */
  private requestLeviathanTexture(): void {
    requestLeviathanTexture(this, this.options.manifest, this.pendingTextures);
  }

  private requestDroneTexture(): void {
    /*
     * Delegado a `summon-art.ts` no gate V28I.P, sem mudar um número: o mundo
     * aberto pede exatamente a mesma folha, e dois carregadores seriam dois
     * sítios para um deles esquecer a rede de segurança do ficheiro em falta.
     */
    requestDroneTexture(this, this.options.manifest, this.pendingTextures);
  }

  private buildSummon(
    kind: 'd' | 'l',
    diameter: number,
    team: string,
  ): Phaser.GameObjects.Container {
    /*
     * **A arte saiu daqui para `summon-art.ts`** (gate V28I.P), sem mudar uma
     * expressão: o mundo aberto desenha as mesmas duas invocações, e uma segunda
     * cópia de duzentas linhas seria a segunda oportunidade de as duas
     * divergirem. O que ficou é o que é do DUELO — a cor da equipa e as
     * profundidades desta cena.
     */
    return buildSummonBody(this, {
      kind,
      diameter,
      tint: team === 'a' ? 0x63d2ff : 0xff8a6b,
      hullDepth: DEPTH.ship,
      cannonDepth: DEPTH.cannon,
    });
  }

  private syncSuperCannons(
    seat: PvpParticipantId,
    mounted: boolean,
    x: number,
    y: number,
    battery: number,
  ): void {
    const existing = this.superCannons.get(seat) ?? null;
    if (!mounted) {
      for (const sprite of existing ?? []) {
        sprite.setVisible(false);
      }
      return;
    }
    const texture = this.cannons.get(seat)?.[0]?.texture.key ?? null;
    if (texture === null) {
      /* Sem canhão desenhado não há textura a reutilizar, e não se inventa uma. */
      return;
    }
    const sprites =
      existing ??
      (() => {
        /*
         * **O tamanho vem das UNIDADES DE MUNDO, e nunca de `setScale`.**
         *
         * A primeira versão fez `setScale(hullScaleOf(seat) * 2)` e desenhou um
         * canhão do tamanho da arena inteira - visível na primeira bancada. A
         * razão: `hullScaleOf` é uma escala de MUNDO - quantas unidades vale um
         * casco - e não um factor sobre os pixels de uma textura que pode ter
         * mil de altura.
         *
         * O canhão da formação já resolvia isto: mede
         * `PVP_CANNON_WORLD_UNITS x hullScaleOf` e aplica-o com
         * `setDisplaySize`. Estes dois medem o DOBRO disso - a mesma proporção
         * que a caça desenha - e passam pelo mesmo construtor.
         */
        const size = PVP_CANNON_WORLD_UNITS * this.hullScaleOf(seat) * PVP_SUPER_CANNON_ART_SCALE;
        const created = [0, 1].map(() =>
          /*
           * ATRÁS do casco e dos canhões da formação, como na caça: eles montam
           * fora da nave, e uma profundidade menor mantém o casco a ler-se por
           * cima de uma peça temporária.
           */
          this.buildBarrel(texture, size, DEPTH.ship - 1),
        );
        this.superCannons.set(seat, created);
        return created;
      })();
    const forwardX = Math.cos(battery);
    const forwardY = Math.sin(battery);
    const sideX = -forwardY;
    const sideY = forwardX;
    const lateral = (this.hullDiameterOf(seat) / 2) * (PVP_SUPER_CANNONS_LATERAL_BPS / 10_000);
    const back = (this.hullDiameterOf(seat) / 2) * (PVP_SUPER_CANNONS_BACK_BPS / 10_000);
    for (const [index, sprite] of sprites.entries()) {
      const sign = index === 0 ? -1 : 1;
      sprite
        .setVisible(true)
        .setPosition(
          x + sideX * lateral * sign - forwardX * back,
          y + sideY * lateral * sign - forwardY * back,
        )
        /*
         * Sem `setScale`: o tamanho foi decidido quando o sprite nasceu, em
         * unidades de mundo, e reescrevê-lo por quadro seria uma segunda
         * oportunidade de o escrever errado - que foi exactamente o que
         * aconteceu na primeira versão.
         */
        .setRotation(pvpCannonSpriteRotation(battery));
    }
  }

  private drawRuneIndicator(
    seat: PvpParticipantId,
    kind: PvpRuneIndicatorKind | null,
    x: number,
    y: number,
    heading: number,
  ): void {
    const existing = this.runeMarks.get(seat);
    if (kind === null) {
      /*
       * Hidden, not destroyed, and not on the display list's terms.
       *
       * A duel is two minutes and a rune lasts seconds, so a participant picks
       * several up: creating and destroying a `Graphics` each time would
       * allocate inside a frame for something that is about to be needed again.
       * It goes for real when the participant does.
       */
      existing?.setVisible(false);
      return;
    }
    let mark = existing;
    if (!mark) {
      mark = this.add.graphics().setDepth(DEPTH.rune);
      this.runeMarks.set(seat, mark);
    }
    /*
     * The zoom is part of the signature, ROUNDED, and only for the mark that is
     * held to a projected size.
     *
     * Rounded because the wheel moves the zoom in multiplicative notches and a
     * raw float would put a redraw behind every rounding difference; a
     * thousandth is finer than any notch. `pvpRuneTurnsWithHeading` is not the
     * test here - `speed` is deliberately left at the proportions that were
     * approved, so its signature must not move when the camera does.
     */
    const scaled = kind === 'double_damage';
    const zoomKey = scaled ? Math.round(this.zoom * 1_000) : 0;
    const signature = `${kind}/${this.hullScaleOf(seat)}/${zoomKey}`;
    if (this.runeMarkBuiltFrom.get(seat) !== signature) {
      this.runeMarkBuiltFrom.set(seat, signature);
      const spec = pvpRuneIndicator(kind, PVP_HULL_WORLD_UNITS, this.hullScaleOf(seat));
      /*
       * How much bigger the mark has to be drawn for the player to see it from
       * where they are standing. One at every zoom the arena spends most of its
       * time at; more only when the world has been shrunk under it.
       */
      const scale = scaled
        ? pvpRuneIndicatorScale(spec, this.zoom, PVP_RUNE_PROJECTED_MIN, PVP_RUNE_PROJECTED_MAX)
        : 1;
      mark.clear();
      if (spec.shape.kind === 'spikes') {
        /*
         * Four filled points on the diagonals, and nothing joining them.
         *
         * FILLED, which is the whole reason they cannot be confused with an
         * identity mark: all three of those are strokes, and a solid shape
         * against an outline is a difference that survives greyscale, minimum
         * zoom and a colour-blind player. On the diagonals so they never reach
         * the health bar, and never rotated - Double Damage is not a direction.
         *
         * DISCONNECTED, and `scene-model.ts` records the four outlines that were
         * tried and photographed before that was accepted: every closed shape
         * that fits between the team ring and the health bar reads as a square,
         * and a square around a ship is what an enemy already wears. What an
         * outline was meant to buy - being seen at zoom 0.4 - is bought instead
         * by the points being larger, in world units and in projected pixels.
         */
        const { inner, outer, halfWidth, angles } = spec.shape;
        // Filled only: `fillTriangle` does not stroke, so a line style here
        // would be a setting nothing reads.
        mark.fillStyle(spec.colour, 1);
        for (const angle of angles) {
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          // The base sits across the point's own direction, so a spike is a
          // triangle aimed outwards rather than an axis-aligned wedge.
          mark.fillTriangle(
            cos * outer * scale,
            sin * outer * scale,
            (cos * inner - sin * halfWidth) * scale,
            (sin * inner + cos * halfWidth) * scale,
            (cos * inner + sin * halfWidth) * scale,
            (sin * inner - cos * halfWidth) * scale,
          );
        }
      } else {
        /*
         * Two open chevrons behind the ship, apexes leading.
         *
         * Drawn along the local +x axis and turned by `setRotation` below, so
         * "behind" is behind wherever the hull is actually pointing. They sit
         * BEHIND on purpose: the barrels, the aim and the health bar are all
         * forward or above, and a cue that covered any of them would have
         * bought legibility for one thing by spending it on three.
         *
         * Untouched by the projected-size compensation, deliberately: green
         * against a near-black arena was legible at 0.4 in the capture that
         * found the other one wanting, and the approved proportions are not
         * something to change while fixing a different mark.
         */
        const { halfSpan, sweep, backs } = spec.shape;
        mark.lineStyle(spec.lineWidth, spec.colour, 1);
        for (const back of backs) {
          mark.beginPath();
          mark.moveTo(-(back + sweep), -halfSpan);
          mark.lineTo(-back, 0);
          mark.lineTo(-(back + sweep), halfSpan);
          mark.strokePath();
        }
      }
    }
    mark
      .setPosition(x, y)
      /*
       * The one drawn thing in the arena that turns with a hull, and only for
       * the rune whose content IS a direction. A stopped ship keeps the heading
       * it last had - `pvpTurnHull` holds it rather than inventing one - so the
       * chevrons stay where they were instead of spinning to nowhere.
       */
      .setRotation(pvpRuneTurnsWithHeading(kind) ? heading : 0)
      .setAlpha(pvpRunePulseAlpha(kind, this.time.now))
      .setVisible(true);
  }

  /**
   * Takes every indicator off the screen, without forgetting how to draw one.
   *
   * For a world REPLACEMENT. Everything on screen described a world this one
   * does not follow from, so nothing may be assumed to have survived it - and
   * the very next `drawShips` puts back exactly what the new snapshot says is
   * running. The cached geometry is deliberately kept: which shape a rune draws
   * depends on the rune and the drawn size, and a correction changes neither.
   */
  private hideRuneMarks(): void {
    for (const mark of this.runeMarks.values()) {
      mark.setVisible(false);
    }
  }

  /** Everything that belonged to a participant the newest frame did not carry. */
  private retireAbsent(present: ReadonlySet<PvpParticipantId>): void {
    for (const seat of [...this.hulls.keys()]) {
      if (present.has(seat)) {
        continue;
      }
      this.hulls.get(seat)?.destroy();
      this.hulls.delete(seat);
      this.retireParticipant(seat);
    }
    // A mark, an indicator, a bar or a battery whose hull was already gone -
    // belt and braces, because these maps are keyed independently and a partial
    // build is a thing that can happen while art is still loading.
    for (const seat of [...this.identities.keys()]) {
      if (!present.has(seat)) {
        this.retireParticipant(seat);
      }
    }
    for (const seat of [...this.runeMarks.keys()]) {
      if (!present.has(seat)) {
        this.retireParticipant(seat);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  The skin adapter: the whole public surface                         */
  /* ------------------------------------------------------------------ */

  /**
   * Dresses seats, from the visual laboratory. Never called by the product.
   *
   * Every seat is set from what is passed, so a seat left out is UNDRESSED
   * rather than left as it was. A partial apply would leave the arena in a
   * state no caller wrote down, and the laboratory's whole claim is that a
   * configuration is reproducible.
   *
   * There is no `if` by skin id here or below: a plan is a compiled list of
   * layers and this scene cannot tell which skin it came from.
   */
  applySkins(
    assignments: ReadonlyMap<PvpParticipantId, { plan: SkinVisualPlan; textureKey: string } | null>,
  ): void {
    this.skinAssignments.clear();
    for (const [seat, assignment] of assignments) {
      if (assignment && this.textures.exists(assignment.textureKey)) {
        this.skinAssignments.set(seat, assignment);
      }
    }
    /*
     * Rebuild every seat's art from scratch.
     *
     * `buildShipArt` is cached on a signature that deliberately does NOT
     * mention the skin - a skin must not be able to make the battery churn -
     * so a change here would otherwise be invisible to it. Clearing the
     * signature is how the adapter asks for one rebuild without teaching the
     * cache about skins.
     */
    for (const seat of new Set([...this.hulls.keys(), ...this.skins.keys()])) {
      this.releaseSkin(seat);
      this.builtFrom.delete(seat);
    }
  }

  /**
   * Dresses every participant as the SERVER says, and undresses the rest.
   *
   * ## The whole product path, and it starts at the wire
   *
   * `arena.appearance` is the room's own answer, per participant id, frozen
   * when that participant first attached and re-sent on a reconnection. This
   * reads it and nothing else: not the local player's inventory, not the
   * roster's order, not a bridge message invented for decoration, and never a
   * file name or an asset key the sender chose. The wire says a `definitionId`;
   * this build's own validated manifest decides whether that names something it
   * is allowed to draw, which is what keeps a `draft` skin out of a production
   * bundle without a line of its own.
   *
   * ## Keyed by participant, so identity cannot migrate
   *
   * A `PvpParticipantId` is stable across the lobby, the ticket, the socket,
   * the snapshot, a reconnection and every frame. Nothing here is keyed by an
   * array index or an arrival order, so six players reordering cannot swap
   * their ships' art, and two people wearing two copies of ONE skin resolve to
   * one texture and one plan - the picture belongs to the definition.
   *
   * ## Work only when the answer changes
   *
   * Every frame carries every profile. Compiling six plans sixty times a second
   * to learn nothing changed would be an allocation per frame, which the arena
   * does not do. So the ids are compared against what was last applied, and a
   * match returns on the second line.
   */
  private syncAuthoritativeSkins(arena: PvpWorldArena): void {
    let changed = false;
    let described = 0;
    for (const [seat, profile] of Object.entries(arena.appearance)) {
      const worn = profile?.skin ?? null;
      if (worn === null) {
        if (this.authoritativeSkins.has(seat)) {
          changed = true;
        }
        continue;
      }
      described += 1;
      if (this.authoritativeSkins.get(seat) !== worn) {
        changed = true;
      }
    }
    // A participant who left the roster entirely is a change too: their id is
    // no longer described, so the count is what notices them.
    if (!changed && described === this.authoritativeSkins.size) {
      return;
    }

    this.authoritativeSkins.clear();
    for (const [seat, profile] of Object.entries(arena.appearance)) {
      const worn = profile?.skin ?? null;
      if (worn !== null) {
        this.authoritativeSkins.set(seat, worn);
      }
    }
    /*
     * Applied IMMEDIATELY with whatever is already on the GPU, and again when
     * anything else lands.
     *
     * The first pass is what undresses a seat the instant the server stops
     * describing a skin for it - waiting for a fetch to undress somebody would
     * leave a ship wearing what it no longer wears. The second is what dresses
     * a seat once its PNG has arrived.
     */
    this.applyResolvedSkins();
    void this.loadSkinsThenApply();
  }

  /**
   * Writes the assignment map from what the authority says AND what is loaded.
   *
   * A texture that has not arrived yet is simply absent from the map, so that
   * seat keeps its ordinary hull until it has. `applySkins` is the door, so the
   * rebuild, the release and the undressing all behave exactly as they do for
   * the laboratory.
   */
  private applyResolvedSkins(): void {
    const resolved = new Map<
      PvpParticipantId,
      { plan: SkinVisualPlan; textureKey: string } | null
    >();
    for (const seat of new Set([...this.hulls.keys(), ...this.authoritativeSkins.keys()])) {
      const definitionId = this.authoritativeSkins.get(seat);
      const content = definitionId
        ? resolveSkinContent(
            { instanceId: '', definitionId },
            this.skinManifestValue,
            this.skinModality,
          )
        : null;
      resolved.set(
        seat,
        content && this.textures.exists(content.textureKey)
          ? { plan: content.plan, textureKey: content.textureKey }
          : null,
      );
    }
    this.applySkins(resolved);
  }

  /**
   * Fetches the sidecar and every PNG the authority named, then applies again.
   *
   * ## Why the re-check after the load matters
   *
   * Because a load is asynchronous and a match is not. Between asking for a PNG
   * and receiving it, the participant who wanted it can have been replaced,
   * dropped, or had their profile re-read from a snapshot. `applyResolvedSkins`
   * is therefore rebuilt from {@link PvpScene.authoritativeSkins} as it stands
   * WHEN the file lands, never from what was true when it was requested - so a
   * late arrival dresses whoever is actually wearing that skin now, or nobody.
   *
   * A PNG that never arrives leaves the ordinary hull and is reported once.
   * That is the honest outcome: a ship somebody can still see, aim at and shoot.
   */
  private async loadSkinsThenApply(): Promise<void> {
    const manifest = await this.skinManifest();
    this.skinManifestValue = manifest;
    const wanted = new Map<string, string>();
    /*
     * The five painted lights, wanted whenever any skin is (gate V27A).
     *
     * They are not a skin's own art - one core, one halo, one spark and one
     * ring serve every skin, tinted - so they are queued beside the first hull
     * that needs them, and never again.
     */
    for (const light of SKIN_VFX_TEXTURES) {
      if (!this.textures.exists(light.key)) {
        wanted.set(light.key, `${SKIN_VFX_BASE_URL}${light.file}`);
      }
    }
    for (const definitionId of new Set(this.authoritativeSkins.values())) {
      const content = resolveSkinContent(
        { instanceId: '', definitionId },
        manifest,
        this.skinModality,
      );
      if (content && !this.textures.exists(content.textureKey)) {
        wanted.set(content.textureKey, `${manifest?.baseUrl ?? ''}${content.plan.assetFile}`);
      }
    }
    if (wanted.size === 0) {
      // Everything named is either already on the GPU or not drawable here.
      // Re-applied anyway, because the manifest may only just have arrived.
      this.applyResolvedSkins();
      return;
    }
    for (const [key, url] of wanted) {
      this.load.image(key, url);
      /*
       * Per FILE, not only per batch - the same shape `requestArtFor` already
       * uses for hulls and barrels.
       *
       * The loader is shared: a hull texture requested a frame earlier is in
       * the same queue, so `COMPLETE` describes whatever was in flight rather
       * than this request. Listening for the file itself means a skin is worn
       * the moment its own PNG lands, and it means the batch handler below can
       * be about the one thing a batch really settles - which files did NOT
       * arrive.
       */
      this.load.once(`filecomplete-image-${key}`, () => this.applyResolvedSkins());
    }
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      for (const key of wanted.keys()) {
        if (!this.textures.exists(key)) {
          this.reportMissingSkin(key);
        }
      }
      this.applyResolvedSkins();
    });
    this.load.start();
  }

  /** The skin sidecar, fetched at most once per scene. */
  private skinManifest(): Promise<SkinRuntimeManifest | null> {
    this.skinManifestPromise ??= fetchSkinManifest().catch(() => null);
    return this.skinManifestPromise;
  }

  /** Says a skin asset was missing, once per key. Never per frame. */
  private reportMissingSkin(key: string): void {
    if (this.reportedMissingSkins.has(key)) {
      return;
    }
    this.reportedMissingSkins.add(key);
    console.warn(`[skins] asset "${key}" did not load; drawing the ordinary hull.`);
  }

  /** Drops every skin particle in the air. For a teleport or a correction. */
  clearSkinResidue(): void {
    for (const renderer of this.skins.values()) {
      renderer.clearResidue();
    }
    this.skinDrawnAt.clear();
  }

  /** What the skin subsystem costs right now, per seat and in total. */
  skinCensus(): {
    renderers: number;
    emitters: number;
    particles: number;
    worn: { seat: PvpParticipantId; textureKey: string }[];
  } {
    let emitters = 0;
    let particles = 0;
    const worn: { seat: PvpParticipantId; textureKey: string }[] = [];
    for (const [seat, renderer] of this.skins) {
      emitters += renderer.emitterCount();
      particles += renderer.aliveParticles();
      const assignment = this.skinAssignments.get(seat);
      if (assignment) {
        worn.push({ seat, textureKey: assignment.textureKey });
      }
    }
    return { renderers: this.skins.size, emitters, particles, worn };
  }

  /**
   * Attaches the renderer for one seat to the hull that seat currently has.
   *
   * Called from `buildShipArt`, which destroys and recreates a hull whenever
   * the profile or the visual scale changes. A renderer holding the previous
   * sprite would be drawing onto an object Phaser has already destroyed, so the
   * lifetime of a skin renderer is exactly the lifetime of one hull sprite.
   */
  private attachSkin(seat: PvpParticipantId, hull: Phaser.GameObjects.Image): void {
    this.releaseSkin(seat);
    const assignment = this.skinAssignments.get(seat);
    if (!assignment || !this.textures.exists(assignment.textureKey)) {
      return;
    }
    this.skins.set(
      seat,
      new SkinRenderer({
        scene: this,
        plan: assignment.plan,
        hull,
        textureKey: assignment.textureKey,
      }),
    );
  }

  /** Releases one seat's renderer and everything it owns. */
  private releaseSkin(seat: PvpParticipantId): void {
    this.skins.get(seat)?.destroy();
    this.skins.delete(seat);
    this.skinDrawnAt.delete(seat);
  }

  /** Releases every renderer. For `pvp:close` and for `SHUTDOWN`. */
  private releaseAllSkins(): void {
    for (const seat of [...this.skins.keys()]) {
      this.releaseSkin(seat);
    }
  }

  /** Drops every object and every scrap of bookkeeping for one participant. */
  /**
   * **Esconde tudo o que é deste casco, sem destruir nada** (gate V28H.X).
   *
   * Para quem está DENTRO do Leviatã: ele volta a aparecer quando a estrutura o
   * largar, e destruir os objectos obrigaria a reconstruí-los - com a arte a
   * ser pedida outra vez, o skin a ser adoptado outra vez, e um piscar no
   * regresso. Esconder é reversível, e um objecto escondido não custa nada.
   *
   * Tudo o que ACOMPANHA o casco vai junto: a barra, o nome, a marca de equipa,
   * os canhões, a mira e os sinais das cartas. Um nome a flutuar sobre um
   * helicóptero seria pior do que a nave que ele nomeia.
   */
  /**
   * **Diz ao HUD se há uma estrutura vazia encostada** (gate V28H.X).
   *
   * ## Porque é a CENA a responder
   *
   * Porque a pergunta é geométrica - onde está o meu casco, onde está a
   * estrutura, e a que distância se entra - e as três coisas estão aqui. O HUD
   * só decide se desenha o botão.
   *
   * O raio é o MESMO que a sala usa para aceitar a ordem, lido da mesma
   * constante: um botão que aparecesse antes de a sala aceitar seria um botão
   * que não funciona, e um que aparecesse depois seria uma entrada perdida.
   *
   * ## Só quando MUDA
   *
   * Uma mensagem por quadro seria a ponte a pagar por silêncio vinte vezes por
   * segundo. O último valor publicado fica guardado, e só a diferença viaja.
   */
  /**
   * **Para onde o rato local aponta**, em radianos, ou nulo.
   *
   * A mesma leitura que os canhões da nave local usam - a intenção que este
   * cliente tem na mão, e não o eco dela que a sala devolve - e nula quando a
   * cena não está a aceitar comandos, que é quando não há mira para seguir.
   */
  private localAimHeading(): number | null {
    if (!this.acceptingCommands) {
      return null;
    }
    const held = this.intent.peek();
    return pvpHeading(held.ax, held.ay);
  }

  private publishBoardOffer(frame: PvpRenderFrame): void {
    const me = frame.ships.find((ship) => ship.seat === this.seat);
    let available = false;
    if (me !== undefined && me.hpMilli > 0n) {
      /*
       * O raio de entrada, em unidades de MUNDO: a mesma fracção que a sala
       * usa - PVP_LEVIATHAN_ENTRY_BPS sobre o raio do casco - medida sobre o
       * casco desenhado, que é onde o jogador vê a nave dele.
       */
      const reach = (this.hullDiameterOf(me.seat) / 2) * (PVP_LEVIATHAN_ENTRY_BPS / 10_000);
      for (const summon of frame.summons ?? []) {
        if (summon.k !== 'l' || summon.pl !== undefined) {
          continue;
        }
        const dx = pvpWorldUnits(summon.x) - pvpWorldUnits(me.x);
        const dy = pvpWorldUnits(summon.y) - pvpWorldUnits(me.y);
        if (dx * dx + dy * dy <= reach * reach) {
          available = true;
          break;
        }
      }
    }
    if (available === this.boardOffered) {
      return;
    }
    this.boardOffered = available;
    this.options.bridge.sendToUi('pvp:board-offer', { available });
  }

  private hideShip(seat: PvpParticipantId): void {
    this.hulls.get(seat)?.setVisible(false);
    for (const barrel of this.cannons.get(seat) ?? []) {
      barrel.setVisible(false);
    }
    const bar = this.bars.get(seat);
    bar?.back.setVisible(false);
    bar?.fill.setVisible(false);
    this.nameTags.get(seat)?.setVisible(false);
    this.identities.get(seat)?.setVisible(false);
    this.runeMarks.get(seat)?.setVisible(false);
    this.cloakTags.get(seat)?.setVisible(false);
    /*
     * E os sinais das cartas, pelo caminho que cada um já tem para dizer 'nada':
     * um estado nulo. Nenhuma destas classes ganha método novo por causa disto.
     */
    this.cardAuras.get(seat)?.setVisible(false);
    this.cardStatus.get(seat)?.update(null, this.time.now);
    this.cardBastions.get(seat)?.update(null, this.time.now);
    this.cardMovement.get(seat)?.update(null, this.time.now, 0);
  }

  private retireParticipant(seat: PvpParticipantId): void {
    // The skin goes with the ship. One left behind would be an effect layer
    // emitting over an empty patch of arena, on behalf of nobody.
    this.releaseSkin(seat);
    for (const sprite of this.superCannons.get(seat) ?? []) {
      sprite.destroy();
    }
    this.superCannons.delete(seat);
    /* E o anel de gelo/escudo, que é do casco e não do jogo (gate V28H.L). */
    this.cardAuras.get(seat)?.destroy();
    this.cardAuras.delete(seat);
    /* E o círculo e a lança, que são do casco (gates V28H.L e V28H.O). */
    this.cardCircles.get(seat)?.destroy();
    this.cardCircles.delete(seat);
    /* E as estrelas do Bastião, que são do casco (gate V28H.U). */
    this.cardBastions.get(seat)?.destroy();
    this.cardBastions.delete(seat);
    /* E a propulsão e o rasto, pela mesma razão (gate V28H.V). */
    this.cardMovement.get(seat)?.destroy();
    this.cardMovement.delete(seat);
    /* E os sinais de debuff e o pulso do Malware (gate V28H.W). */
    this.cardStatus.get(seat)?.destroy();
    this.cardStatus.delete(seat);
    this.malwareCasts.get(seat)?.destroy();
    this.malwareCasts.delete(seat);
    this.markCasts.get(seat)?.destroy();
    this.markCasts.delete(seat);
    this.cloakTags.get(seat)?.destroy();
    this.cloakTags.delete(seat);
    this.cardLances.get(seat)?.destroy();
    this.cardLances.delete(seat);
    for (const barrel of this.cannons.get(seat) ?? []) {
      barrel.destroy();
    }
    this.cannons.delete(seat);
    const bar = this.bars.get(seat);
    bar?.back.destroy();
    bar?.fill.destroy();
    this.bars.delete(seat);
    /* E a etiqueta, que é do casco como a barra (gate V28H.R). */
    this.nameTags.get(seat)?.destroy();
    this.nameTags.delete(seat);
    this.identities.get(seat)?.destroy();
    this.identities.delete(seat);
    this.identityBuiltFrom.delete(seat);
    // The indicator goes with them too. One that outlived its ship would be a
    // rune burning over an empty patch of arena, on behalf of nobody.
    this.runeMarks.get(seat)?.destroy();
    this.runeMarks.delete(seat);
    this.runeMarkBuiltFrom.delete(seat);
    this.hullHeading.delete(seat);
    this.lastDrawnAt.delete(seat);
    // The signature goes with the sprites it described, or a participant who
    // came back under the same id would find a battery that no longer exists.
    this.builtFrom.delete(seat);
  }

  /**
   * The cursor ring, drawn only while the player is choosing.
   *
   * One pooled circle that follows the pointer, and the canvas takes a
   * `crosshair` cursor with it - the two together are what tells a player the
   * next click means something different. It is presentation and nothing else:
   * being in the mode grants nothing, and the ring makes no claim about whether
   * the point under it is legal. The room decides that, and refuses out loud.
   */
  private drawTargeting(): void {
    const armed = this.intent.isTargeting();
    if (!this.targetRing) {
      this.targetRing = this.add
        /*
         * A cor do teleporte, e não um literal ao lado dela.
         *
         * O anel do cursor existe enquanto o jogador escolhe um destino, que é
         * a única coisa que a runa de teleporte concede. Ele carregava
         * `0x3cd2ff` escrito à mão - o ciano que o teleporte era antes do gate
         * V25B - e teria continuado ciano depois de a runa virar violeta, que é
         * como dois nomes para uma coisa acabam apontando para coisas
         * diferentes.
         */
        .circle(0, 0, PVP_HULL_WORLD_UNITS / 2, PVP_RUNE_TINT.teleport, 0.18)
        .setStrokeStyle(2, PVP_RUNE_TINT.teleport, 0.9)
        .setDepth(DEPTH.bar)
        .setVisible(false);
    }
    if (this.canvasCursor !== armed) {
      this.canvasCursor = armed;
      this.input.setDefaultCursor(armed ? 'crosshair' : 'default');
    }
    if (!armed) {
      this.targetRing.setVisible(false);
      return;
    }
    const pointer = this.input.activePointer;
    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    this.targetRing
      .setPosition(world.x, world.y)
      .setRadius(this.hullDiameterOf(this.seat ?? '') / 2)
      .setVisible(true);
  }

  /**
   * **O nickname por cima do casco** (gate V28H.R).
   *
   * ## Porque um `Text` por cadeira, e não um por quadro
   *
   * Porque são no máximo seis e mudam de posição, nunca de conteúdo: o nome de
   * quem se sentou é fixo dentro de uma partida. Criar um objecto de texto por
   * quadro seria seis alocações por quadro para desenhar a mesma palavra.
   *
   * ## Nunca escalado com o casco
   *
   * Pela mesma razão que a barra de vida não é: uma nave três vezes maior não
   * merece um nome três vezes maior, merece o mesmo nome mais afastado. O
   * afastamento acompanha o casco; o tamanho da letra não.
   *
   * Uma cadeira sem nome não desenha nada - e é um estado real, não um erro.
   */
  /**
   * **INVISÍVEL, por cima da nave, enquanto o Manto durar** (gate V28H.V).
   *
   * Acima da etiqueta do nome, porque as duas dizem coisas diferentes sobre a
   * mesma nave e a que é temporária é a que tem de saltar à vista.
   *
   * Quem não é da equipa nunca chega aqui com este casco - a sala omite-o do
   * frame - portanto esta palavra nunca denuncia ninguém: ela existe para o dono
   * e para os aliados, que já viam a nave.
   *
   * Acaba com o EFEITO e não com a carta: a letra que a acende vem da ranhura
   * autoritativa, e é por isso que ela desaparece no instante em que um tiro
   * quebra o Manto, e não nove segundos depois.
   */
  private drawCloakTag(seat: PvpParticipantId, cloaked: boolean, x: number, y: number): void {
    const existing = this.cloakTags.get(seat);
    if (!cloaked) {
      existing?.setVisible(false);
      return;
    }
    const lift = BAR_LIFT * this.hullScaleOf(seat) + NAME_TAG_LIFT + PvpScene.CLOAK_TAG_LIFT;
    if (!existing) {
      this.cloakTags.set(
        seat,
        this.add
          .text(x, y - lift, 'INVISÍVEL', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '12px',
            /* O mesmo contorno escuro do nome, e pela mesma razão. */
            color: '#bfe9ff',
            stroke: '#04060e',
            strokeThickness: 3,
          })
          .setOrigin(0.5, 1)
          .setDepth(DEPTH.bar),
      );
      return;
    }
    existing.setVisible(true).setPosition(x, y - lift);
  }

  private drawNameTag(seat: PvpParticipantId, x: number, y: number): void {
    const nickname = this.arena?.nickname[seat] ?? null;
    const existing = this.nameTags.get(seat);
    if (!nickname) {
      existing?.setVisible(false);
      return;
    }
    const lift = BAR_LIFT * this.hullScaleOf(seat) + NAME_TAG_LIFT;
    if (!existing) {
      const created = this.add
        .text(x, y - lift, nickname, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#dbe7ff',
          /*
           * Um contorno escuro, e não uma caixa: a arena tem fundo estrelado e
           * naves por baixo, e um nome sem contorno desaparece sobre metade
           * delas. Uma caixa opaca taparia a arena.
           */
          stroke: '#04060e',
          strokeThickness: 3,
        })
        .setOrigin(0.5, 1)
        .setDepth(DEPTH.bar);
      this.nameTags.set(seat, created);
      return;
    }
    existing.setVisible(true).setPosition(x, y - lift);
    /*
     * O texto é reescrito só quando MUDA: um `setText` por quadro refaz a
     * textura do texto, que é o trabalho que este cache existe para evitar.
     */
    if (existing.text !== nickname) {
      existing.setText(nickname);
    }
  }

  private drawHealth(
    seat: PvpParticipantId,
    role: PvpParticipantRole,
    x: number,
    y: number,
    fraction: number,
  ): void {
    // Clear of the hull at every size, while the bar itself keeps one width.
    const lift = BAR_LIFT * this.hullScaleOf(seat);
    let bar = this.bars.get(seat);
    if (!bar) {
      const back = this.add
        .rectangle(x, y - lift, BAR_WIDTH, BAR_HEIGHT, 0x120000, 0.85)
        .setDepth(DEPTH.bar);
      const fill = this.add
        .rectangle(x - BAR_WIDTH / 2, y - lift, BAR_WIDTH, BAR_HEIGHT, PVP_BAR_ENEMY_TINT)
        .setOrigin(0, 0.5)
        .setDepth(DEPTH.bar);
      bar = { back, fill };
      this.bars.set(seat, bar);
    }
    /*
     * **A barra diz DE QUEM É A NAVE** (gate V28H.R).
     *
     * Ela era vermelha em toda a gente, e o comentário que aqui estava dizia
     * porquê: "de quem ela é, diz-se pela cor do casco e por onde a câmara
     * está". Isso valia num duelo. Num 3x3 há cinco outras naves, a cor do
     * casco passou a ser a arte que o jogador possui, e a câmara só aponta a
     * uma - restava um ecrã com seis barras iguais.
     *
     * Verde do MEU lado - a minha e a dos meus - e vermelha do outro. Pintada a
     * cada quadro e não só na criação: um casco pode mudar de papel dentro da
     * vida da cena, e uma barra pintada uma vez só ficaria com o papel do
     * primeiro quadro em que apareceu.
     */
    bar.fill.setFillStyle(role === 'enemy' ? PVP_BAR_ENEMY_TINT : PVP_BAR_FRIENDLY_TINT);
    bar.back.setPosition(x, y - lift);
    bar.fill.setPosition(x - BAR_WIDTH / 2, y - lift);
    // Discrete. A bar that slid would show a ship alive after the room says it
    // is not, and a ship at zero still announces nothing: only `ended` does.
    bar.fill.setDisplaySize(Math.max(0, BAR_WIDTH * fraction), BAR_HEIGHT);
    bar.fill.setVisible(fraction > 0);
  }

  /* ---------------------------------------------------------- projectiles */

  /**
   * The shots, and deliberately WITHOUT the local identity.
   *
   * It used to take one, because a shot was coloured by whose it was on the
   * three-role scale the hulls use - the player's cyan, an ally's green, an
   * enemy's orange. That is now a thing this function cannot express, and the
   * missing parameter is how: Excellent's blue, Perfect's yellow and Anomaly's
   * red belong to the EQUIPMENT, and there is no argument here through which
   * "who is looking" could reach the colour of anybody's fire.
   *
   * Who a shot belongs to is still legible, and from the things that cannot be
   * faked: where it came from, which way it is going, and which hull it left.
   */
  private drawProjectiles(frame: PvpRenderFrame): void {
    const arena = this.arena;
    if (!arena) {
      return;
    }
    const diff = pvpProjectileDiff(this.shots.keys(), frame);

    for (const id of diff.remove) {
      /*
       * The same frame it left the snapshot. No fade, and NO impact.
       *
       * A projectile absent from the newest snapshot is gone: it hit something,
       * it expired, or the match ended, and this renderer cannot tell those
       * apart. Drawing a burst here would be inventing a hit the room never
       * reported. The wake already in the air is left to dissipate on its own
       * clock - it is residue behind a path that really was flown - but the
       * sprite itself goes on this frame, because something that looks like a
       * live shot and can no longer do anything is worse than nothing.
       */
      this.shots.get(id)?.destroy();
      this.shots.delete(id);
      this.shotTrail.delete(id);
      this.shotWake.delete(id);
    }

    /*
     * **As bombas de CARTA saem da fila dos tiros** (gate V28H.U).
     *
     * Elas viajam na mesma lista porque na sala são projécteis - colidem,
     * expiram, são removidas - mas não são desenhadas como tiros: uma tem um
     * corpo de fogo que a carga dimensionou, a outra é uma massa com um anel de
     * guia. `CardBlastFx` é dona delas, e é ela que as apaga quando a sala as
     * deixa de mandar; `this.shots` nunca chega a conhecê-las, portanto o
     * `diff.remove` acima não tem nada para destruir.
     *
     * Uma lista por quadro, e é deliberado: a alternativa era memória de estado
     * por projéctil nesta cena, e a lista é o que a sala acabou de dizer.
     */
    const cardBodies: CardBlastBody[] = [];
    for (const projectile of [...diff.spawn, ...diff.update]) {
      const x = pvpWorldUnits(projectile.x);
      const y = pvpWorldUnits(projectile.y);
      if (projectile.cardBlast !== null) {
        cardBodies.push({
          id: projectile.id,
          kind: projectile.cardBlast,
          x,
          y,
          /*
           * Zero: no duelo o tamanho já é a verdade no instante em que ela
           * nasce - a carga decidiu-o - e inchá-la a caminho fá-la-ia ler-se
           * menor do que aquilo que bate.
           */
          ageMs: 0,
          radius: pvpWorldUnits(projectile.blastRadius ?? 0),
          /*
           * O CORPO. O do Catolé é o que a sala usa para o testar contra
           * paredes - três vezes o da Bomba Atômica - e o da esfera é a mesma
           * fracção que a caça desenha, sobre o raio que esta carga vai abrir.
           */
          bodyRadius:
            projectile.cardBlast === 'catole'
              ? pvpWorldUnits(PVP_CATOLE_BODY_RADIUS_MU)
              : projectile.cardBlast === 'ice_sphere' ||
                  projectile.cardBlast === 'ice_star' ||
                  projectile.cardBlast === 'shock_bolt'
                ? /*
                   * **Os dois dardos de gelo têm um corpo próprio** (V28H.W).
                   *
                   * Eles não trazem raio de explosão nenhum - não rebentam - e
                   * portanto a fracção que serve a Esfera Ígnea daria zero. O
                   * tamanho sai do CASCO da arena, que é a medida que faz um
                   * projéctil ler-se igual em qualquer zoom.
                   */
                  pvpWorldUnits(PVP_SHIP_RADIUS_MU) * ICE_BOLT_HULLS
                : pvpWorldUnits(projectile.blastRadius ?? 0) * CARD_FIREBALL_BODY_FRACTION,
        });
        continue;
      }
      /*
       * **A salva do Leviatã é VERMELHA NÉON** (decisão do dono, gate V28H.X).
       *
       * A cor de um tiro sai da cadeira de quem o deu, e quem dá estes está
       * escondido dentro da estrutura: sem a letra que a sala publica, a salva
       * saía da cor de uma nave que ninguém está a ver. É o mesmo vermelho das
       * luzes da chapa, e a textura do tiro é a mesma - o gerador da caça
       * pinta-a na cor que lhe derem.
       */
      const colour =
        projectile.fromSummon === 'leviathan'
          ? PVP_LEVIATHAN_SHOT_COLOUR
          : this.projectileColourFor(projectile.seat);
      let sprite = this.shots.get(projectile.id);
      if (!sprite) {
        /*
         * The SHOT: the hunt's neon dash, in the colour of the cannon that
         * fired it.
         *
         * `ensureDashTexture` is the hunt's own generator and this is a call to
         * it, not a copy of it: the near-white core, the saturated body, the hot
         * leading section and the two haloes are painted by the same code that
         * paints them in a hunt, from the same layer table, and cached in the
         * same texture manager by colour. So a player's Perfect cannon looks
         * like their Perfect cannon in both modes, and a third one would inherit
         * the artwork by existing.
         *
         * ADD, because a bolt is light - the same blend the hunt gives its own.
         * The origin is the hunt's too: the simulation's point sits just behind
         * the tip, so the bolt trails the position rather than being centred on
         * it. And the depth keeps it under every hull, every
         * marker and every bar: a shot may cross the ships, never hide them.
         */
        if (projectile.bomb) {
          /*
           * **O NÚCLEO ATÔMICO**, e a AUTORIDADE diz qual projétil é.
           *
           * `bomb` vem do frame - o servidor marca-o - e nunca é deduzido pela
           * velocidade entre dois instantâneos: uma dedução dessas erraria em
           * todos os quadros agrupados, que são precisamente aqueles em que o
           * buffer de interpolação existe.
           *
           * `NORMAL` e não `ADD`: ela é um corpo de metal preto, e somar luz
           * tornaria escuro em transparente - o contrário do volume que a
           * textura assou. Pivota no MEIO, porque o ponto da simulação é o
           * centro do corpo e é dele que a proximidade é medida.
           */
          sprite = this.add
            .image(x, y, ensureAtomicBombTexture(this, atomicBombRollFrame(this.time.now)))
            .setBlendMode(Phaser.BlendModes.NORMAL)
            .setOrigin(0.5, 0.5)
            .setDepth(DEPTH.projectile);
          /*
           * O comprimento desenhado: o dobro do raio de corpo mecânico, em
           * unidades do mundo. Derivado da constante da simulação, o que mantém
           * a ordem do §5 - corpo (81,2) < proximidade (113,7) < explosão (702) - sem
           * ninguém a ter de a verificar à mão.
           */
          const span = (pvpWorldUnits(PVP_BOMB_BODY_RADIUS_MU) * 2) / ATOMIC_BOMB_BODY_SPAN;
          sprite.setScale(span, span);
          this.shots.set(projectile.id, sprite);
        } else {
          sprite = this.add
            .image(x, y, ensureDashTexture(this, colour))
            .setBlendMode(Phaser.BlendModes.ADD)
            .setOrigin(DASH_ORIGIN_X, 0.5)
            .setDepth(DEPTH.projectile);
          /*
           * **Os tiros de CADÊNCIA são desenhados maiores** (gate V28H.X).
           *
           * Decisão do dono: o dobro no comprimento e metade a mais na
           * espessura - "aumente a escala de todos os projéteis do jogo 100%
           * para os lados e 50% para cima/baixo (apenas projéteis que usam
           * velocidade de ataque)".
           *
           * Quem entra nessa conta: o tiro de um casco, o dos Super Canhões e a
           * salva do Leviatã, que dispara à velocidade de ataque de quem o
           * pilota. Quem NÃO entra: o Drone, que atira ao ritmo da carta dele, e
           * tudo o que uma habilidade lança - esses têm o seu próprio tamanho e
           * o dono disse para não lhes tocar.
           *
           * Só o DESENHO cresce. O raio de colisão, a velocidade, a cadência e a
           * origem são da sala e não passam por aqui.
           */
          const gauge = this.gauge();
          const cadence = projectile.fromSummon !== 'drone';
          sprite.setScale(
            gauge.scaleX * (cadence ? PVP_CADENCE_SHOT_LENGTH : 1),
            gauge.scaleY * (cadence ? PVP_CADENCE_SHOT_THICKNESS : 1),
          );
          this.shots.set(projectile.id, sprite);
        }
      } else if (projectile.bomb) {
        /*
         * A ROLAGEM: uma troca de textura, e nada mais.
         *
         * `setTexture` reutiliza a textura em cache; não gera nenhuma. O quadro
         * é uma função pura do relógio, portanto seis clientes com o mesmo
         * relógio de apresentação veem a mesma bomba.
         */
        sprite.setTexture(ensureAtomicBombTexture(this, atomicBombRollFrame(this.time.now)));
      } else if (sprite.texture.key !== ensureDashTexture(this, colour)) {
        /*
         * A profile that arrived AFTER the shot did.
         *
         * A frame can legitimately carry a projectile for a seat the welcome has
         * not described yet, and that shot is drawn in the neutral colour rather
         * than hidden. When the appearance lands, the bolt already in the air
         * becomes the cannon's own - the texture is swapped, and nothing else
         * about the sprite moves. `setTexture` on an existing image reuses the
         * cached texture; it does not bake one.
         */
        sprite.setTexture(ensureDashTexture(this, colour));
      }
      /*
       * Pointed along its own velocity, measured between two drawn positions.
       *
       * Never at the other ship. A duel shot does not home, keeps no target,
       * and must not be drawn as though it might.
       *
       * On the very FIRST frame there is no previous position to measure, and a
       * bolt is a directional object: leaving it at zero would draw a shot
       * pointing due right for one frame regardless of where it was fired. The
       * fallback is its owner's authoritative aim, which is what the shot was
       * born travelling along - the room's own number, never a guess about a
       * target.
       */
      const heading =
        pvpProjectileHeading(this.shotTrail.get(projectile.id), { x, y }) ??
        this.firingHeading(frame, projectile.seat);
      if (heading !== null) {
        sprite.setRotation(heading);
        this.emitWake(projectile.id, x, y, heading, colour);
      }
      sprite.setPosition(x, y);
      this.shotTrail.set(projectile.id, { x, y });
    }
    /*
     * E os corpos das cartas, num só passo: uma bomba que já não venha na lista
     * rebentou ou expirou, e a classe apaga-a no mesmo quadro. A explosão vem do
     * EVENTO, e nunca de uma suposição feita a partir de um desaparecimento.
     */
    this.cardBlasts?.update(cardBodies, this.time.now);
  }

  /**
   * The colour a seat's shots are, resolved through the AUTHORITY every time.
   *
   * `projectile.seat` names the owner, the welcome's `appearance` says what that
   * owner is flying, and the catalogue says what colour that cannon's bolts are.
   * Nothing here can reach the roster's order, the local player's team, the
   * shot's presumed target or this browser's inventory - which is the point: a
   * reorder must not repaint anybody's fire, and an enemy's Anomaly red is theirs
   * whether or not the person watching has one.
   */
  private projectileColourFor(seat: PvpParticipantId): number {
    const appearance = this.arena?.appearance[seat] ?? null;
    const colour = pvpProjectileColour(appearance);
    if (pvpProjectileColourIsFallback(colour) && !this.unresolvedShotOwners.has(seat)) {
      this.unresolvedShotOwners.add(seat);
      /*
       * Said once per seat, per duel, and only to the console.
       *
       * Not through `reportError`: a participant whose profile has not arrived
       * is not a broken scene, and a duel must never end because of one. The
       * shot is still drawn, still the right size, still in the right place and
       * still pointing the right way - the inconsistency is recorded and the
       * match carries on.
       */
      console.warn(`pvp: no cannon appearance for seat ${seat}; its shots use the neutral colour`);
    }
    return colour;
  }

  /** The aim the room says a seat is holding, for a shot's first frame only. */
  private firingHeading(frame: PvpRenderFrame, seat: PvpParticipantId): number | null {
    const owner = frame.ships.find((ship) => ship.seat === seat);
    return owner ? pvpHeading(owner.aimX, owner.aimY) : null;
  }

  /**
   * **`+VALOR` sobre o casco curado, e mais nada** (gate V28G.I).
   *
   * ## O mesmo texto de combate que a caça já usa
   *
   * O duelo não tinha família de texto flutuante nenhuma - não recebe eventos de
   * acerto, e a vida vem no frame - portanto esta é a PRIMEIRA, e não uma
   * segunda. As constantes de estilo são as mesmas de `HuntScene`, importadas de
   * `combat-text.ts`, para os dois modos escreverem um número da mesma maneira.
   *
   * ## Sem decoração
   *
   * Sem partícula, sem símbolo, sem ligação ao adversário e sem nada sobre ele.
   * O número nasce junto da nave, sobe um pouco e desaparece.
   *
   * ## O valor é AUTORITATIVO
   *
   * Somado dos eventos, em milli, e é a vida que a sala realmente acrescentou
   * depois do clamp. Não é derivado do dano, da barra nem de percentagem alguma.
   */
  private showHeal(attacker: string, milli: number): void {
    const at = this.hullPositions.get(attacker);
    if (at === undefined) {
      return;
    }
    const update = this.healTally.add(attacker, milli, () => {
      const text = this.healPool.pop() ?? this.createHealText();
      text
        /*
         * ACIMA DA BARRA, e derivado dela em vez de afinado.
         *
         * O duelo desenha uma barra de vida sobre cada casco, a `BAR_LIFT` do
         * centro e a acompanhar a escala visual. Um deslocamento fixo punha o
         * número em cima dela - encontrado pela folha visual, onde `+2,52` saía
         * escrito por cima do vermelho.
         *
         * `origin` é `(0.5, 1)`, portanto isto é o pé do texto: somar a altura
         * da barra ao seu levantamento deixa-o a começar imediatamente acima
         * dela, em qualquer escala e sem um número escolhido à mão.
         *
         * A caça não precisa disto: lá a vida da nave vive no HUD, e não há
         * nada sobre o casco para o número evitar.
         */
        .setPosition(at.x, at.y - (BAR_LIFT * this.hullScaleOf(attacker) + BAR_HEIGHT))
        .setAlpha(1)
        .setScale(0.75)
        .setVisible(true)
        .setActive(true);
      this.liveHealTexts.add(text);
      return text;
    });
    if (update === null) {
      return;
    }
    /*
     * O TEXTO, e mais nada.
     *
     * A duração continua a ser a que começou na primeira cura deste período, a
     * posição é onde ele nasceu, e a escala e o alpha seguem o seu curso. Um
     * casco a curar sem parar teria, de outro modo, um número que nunca
     * desaparece.
     */
    /*
     * A cor, SEMPRE.
     *
     * O pool é partilhado com o número vermelho da detonação, portanto um texto
     * reutilizado pode vir de lá. Sem esta linha, uma cura herdava o carmesim do
     * golpe anterior - e um `+VALOR` vermelho lê-se como dano.
     */
    update.text.setColor(COMBAT_TEXT_HEAL_COLOR);
    update.text.setText(update.label);
    if (!update.created) {
      return;
    }
    this.tweens.add({ targets: update.text, scale: 1, duration: 110, ease: 'Back.easeOut' });
    this.tweens.add({
      targets: update.text,
      y: update.text.y - COMBAT_TEXT_RISE_PX,
      alpha: 0,
      duration: COMBAT_TEXT_LIFE_MS,
      ease: 'Quad.easeOut',
      onComplete: () => {
        /* Só liberta se a entrada ainda for DESTE período - ver `heal-tally.ts`. */
        const expired = this.healTally.expire(attacker, update.generation);
        if (expired !== null) {
          this.recycleHealText(expired);
        }
      },
    });
  }

  /**
   * **O número vermelho de uma detonação** (gate V28G.N).
   *
   * O duelo nunca teve números de dano flutuantes - a vida a descer era o
   * sinal - e ganha um agora, só para isto, porque o dono o pediu. Ele é
   * legítimo por uma razão que o tiro comum não tem: a barra desce no mesmo
   * tique por tiros E por marcas, e sem o número o jogador não sabe quanto a
   * ordem dele valeu.
   *
   * O valor é AUTORITATIVO - vem em `bdt.q[].d` - e nunca calculado aqui.
   * Zero não imprime nada: um alvo que já estava sem vida não levou golpe.
   *
   * Do mesmo pool do `+VALOR`, e não de um segundo: os dois são um texto curto
   * que sobe e desaparece, e dois pools seriam duas coisas para limpar nas
   * mesmas quatro fronteiras - com uma delas a ser esquecida um dia.
   */
  /**
   * O número de uma detonação, em MILLI (gate V28G.S.1).
   *
   * `bleed-mark.ts` transporta um `number` opaco e cada cena decide a unidade:
   * a caça põe lá pontos fracionários, o duelo põe milésimos. Aqui ele volta a
   * `bigint` para o formatador canónico, que escreve `40,4` e nunca `40400` -
   * nem `40.4` com o ponto de outra língua.
   */
  private showBleedDamage(x: number, y: number, damageMilli: number): void {
    if (damageMilli <= 0) {
      return;
    }
    const text = this.bleedTextPool.pop() ?? this.createBleedText();
    text.setText(formatHealthMilli(BigInt(Math.trunc(damageMilli))));
    /*
     * Na posição do CORTE, e não na do casco.
     *
     * O número nasce até dois segundos e meio depois do golpe, e nessa altura o
     * casco pode ter-se movido - ou deixado de existir. O corte está onde a
     * detonação aconteceu, e é lá que a conta dela pertence.
     */
    text.setPosition(x, y - COMBAT_TEXT_HEAL_OFFSET_PX);
    /*
     * Nasce MAIOR e assenta no tamanho final.
     *
     * Um golpe cobrado de uma vez tem de chegar como um golpe, e um número que
     * simplesmente aparece a subir chega como mais um. O pico é curto - a escala
     * volta a um dentro do primeiro terço da vida - portanto o que se lê é um
     * impacto, e não uma animação a decorrer.
     */
    text.setAlpha(1).setScale(1.6).setVisible(true).setActive(true);
    this.liveBleedTexts.add(text);
    this.tweens.add({
      targets: text,
      scale: 1,
      duration: Math.round(COMBAT_TEXT_LIFE_MS / 3),
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: text,
      /* Sobe mais do que um número comum: é maior, e tem mais que atravessar. */
      y: text.y - COMBAT_TEXT_RISE_PX * 1.8,
      alpha: 0,
      duration: COMBAT_TEXT_LIFE_MS,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.tweens.killTweensOf(text);
        this.recycleBleedText(text);
      },
    });
  }

  private createBleedText(): Phaser.GameObjects.Text {
    const text = this.add.text(0, 0, '', COMBAT_TEXT_BLEED_STYLE);
    /* Acima dos números comuns: quando os dois saem no mesmo tique, este manda. */
    text.setOrigin(0.5, 1).setDepth(951);
    return text;
  }

  private recycleBleedText(text: Phaser.GameObjects.Text): void {
    this.liveBleedTexts.delete(text);
    text.setVisible(false).setActive(false).setScale(1);
    this.bleedTextPool.push(text);
  }

  /** Devolve os números de detonação ao pool. As mesmas quatro fronteiras. */
  private clearBleedTexts(): void {
    for (const text of [...this.liveBleedTexts]) {
      this.tweens.killTweensOf(text);
      this.recycleBleedText(text);
    }
  }

  /** Larga os objetos. Só ao fechar a modalidade. */
  private destroyBleedTexts(): void {
    this.clearBleedTexts();
    for (const text of this.bleedTextPool) {
      text.destroy();
    }
    this.bleedTextPool.length = 0;
  }

  private createHealText(): Phaser.GameObjects.Text {
    const text = this.add.text(0, 0, '', {
      ...COMBAT_TEXT_STYLE,
      color: COMBAT_TEXT_HEAL_COLOR,
    });
    text.setOrigin(0.5, 1).setDepth(950);
    return text;
  }

  private recycleHealText(text: Phaser.GameObjects.Text): void {
    this.liveHealTexts.delete(text);
    text.setVisible(false).setActive(false);
    this.healPool.push(text);
  }

  /** Devolve tudo ao pool: uma correção, uma substituição de mundo, um fecho. */
  private clearHealTexts(): void {
    for (const text of [...this.liveHealTexts]) {
      this.tweens.killTweensOf(text);
      this.recycleHealText(text);
    }
    /*
     * E o mapa dos totais esvazia-se.
     *
     * Isto é chamado na abertura da modalidade, no fecho da arena, no fecho da
     * cena e numa correção do mundo - as quatro fronteiras que o duelo tem. Uma
     * entrada que sobrevivesse a uma delas apontaria para um texto já devolvido
     * ao pool, e a cura seguinte somaria ao total de uma partida que acabou.
     */
    this.healTally.takeAll();
  }

  /** Larga os objetos. Só ao fechar a modalidade. */
  private destroyHealTexts(): void {
    this.clearHealTexts();
    for (const text of this.healPool) {
      text.destroy();
    }
    this.healPool.length = 0;
    this.hullPositions.clear();
  }

  /** The scale every shot shares, from the arena's own collision radius. */
  private gauge(): PvpDashGauge {
    this.dashGauge ??= pvpDashGauge(
      this.arena?.projectileRadius ?? 0,
      /*
       * O casco de REFERÊNCIA, e é a decisão (gate V28G.R).
       *
       * O traço de um tiro é medido contra ele para que o desenho de um
       * projétil não mude quando um casco cresce - o `CLAUDE.md` proíbe alterar
       * como um tiro é desenhado, e uma nave maior não é uma razão para o fazer.
       */
      this.referenceHullDiameter(),
    );
    return this.dashGauge;
  }

  /**
   * One streak dropped behind a shot, from the one emitter the arena owns.
   *
   * Along the PREVIOUS heading rather than this one, because the streak marks
   * where the bolt has been. Tinted with the owner's cannon colour so a wake can
   * never say a different gun from the shot making it, and emitted on a cadence
   * so ninety bolts leave a wake rather than a fog.
   *
   * Nothing random decides a position, a direction or a rule here: the emitter's
   * own tiny speed spread is decoration on a particle that has already been
   * placed, and the placement is the authoritative point.
   */
  private emitWake(id: number, x: number, y: number, heading: number, colour: number): void {
    const wake = this.shotWake.get(id);
    if (!wake) {
      // Its first frame: remember the heading, emit nothing. There is no path
      // behind a shot that has not travelled yet.
      this.shotWake.set(id, { heading, countdown: PROJECTILE_TRAIL_EVERY_FRAMES });
      return;
    }
    wake.countdown -= 1;
    if (wake.countdown <= 0) {
      wake.countdown = PROJECTILE_TRAIL_EVERY_FRAMES;
      const emitter = this.trailEmitter;
      if (emitter) {
        emitter.particleTint = colour;
        emitter.particleRotate = Phaser.Math.RadToDeg(wake.heading);
        emitter.emitParticleAt(x, y, 1);
      }
    }
    wake.heading = heading;
  }

  /**
   * The arena's single wake emitter, built once with the arena.
   *
   * Owned by this scene and destroyed by {@link closeView}. Never rebuilt per
   * frame, per shot or per correction: a replaced world kills its PARTICLES,
   * which is a different thing from destroying the emitter that made them. It
   * exists before the first shot does and emits nothing until one asks, so how
   * many objects a projectile adds to the display list has exactly one answer.
   */
  private buildTrailEmitter(): void {
    if (this.trailEmitter) {
      return;
    }
    const gauge = this.gauge();
    /*
     * The wake does NOT take the ship factor, and this is the measurement that
     * decided it.
     *
     * It did, for one build. Photographed at the ceiling, the result was a set
     * of CONTINUOUS COLOURED LINES across the arena: each wake particle three
     * times longer, emitted on a cadence tuned for one, overlapping into a
     * ribbon. The bolts themselves were innocent - at 3x a dash is 112 world
     * units against a 248-unit gap between consecutive shots, so the train
     * still reads as a train - but the residue between them closed it.
     *
     * A continuous line between a cannon and its target is the one thing this
     * project has permanently forbidden: KENOCYBER never converts shots into
     * lasers, at any cadence, and a wake that draws the beam is the same defect
     * wearing a different name. So the tail of the BOLT grows with the ship and
     * the residue behind it does not.
     */
    const streak = pvpStreakScale(gauge);
    this.trailEmitter = this.add.particles(0, 0, ensureStreakTexture(this), {
      // Barely any drift: a streak marks where the shot has been, it does not
      // spray outwards like sparks.
      speed: { min: 0, max: 12 * streak },
      lifespan: { ...PROJECTILE_TRAIL_LIFESPAN_MS },
      scaleX: { start: streak, end: streak * 0.25 },
      scaleY: { start: streak * 0.9, end: streak * 0.35 },
      /*
       * Fainter than the bolt, and gone to nothing.
       *
       * The dash's own body runs to full opacity; residue that matched it would
       * turn ninety wakes into ninety more shots. This is the LOD lever if one
       * is ever needed, and it is the only kind of thing that may be pulled.
       */
      alpha: { ...PROJECTILE_TRAIL_ALPHA },
      blendMode: Phaser.BlendModes.ADD,
      maxAliveParticles: PROJECTILE_TRAIL_MAX,
      emitting: false,
    });
    this.trailEmitter.setDepth(DEPTH.projectileTrail);
  }

  /**
   * Every shot released, and every wake with it.
   *
   * Called on a CORRECTION - a world the drawn one does not follow from - and on
   * close. Both are discontinuities, and a streak that survived one would be a
   * ribbon drawn across a jump the ship never flew. `killAll` removes the
   * particles that are in the air this instant rather than letting them fade,
   * which is the difference between residue and a lie.
   *
   * The EMITTER survives, because a correction is not the end of a duel. It is
   * `closeView` that destroys it, and `closeView` alone.
   */
  /**
   * **Os mísseis do Arsenal Ígneo, desenhados a partir do estado autoritativo.**
   *
   * ## Uma passagem à parte da dos tiros
   *
   * Porque são duas listas no fio e duas entidades no jogo. Reconciliá-los no
   * mesmo laço obrigaria a distinguir um do outro por um campo - a dedução que a
   * Bomba já ensinou a não fazer - e faria a limpeza de uma apagar os sprites da
   * outra.
   *
   * ## O RUMO vem do servidor, e não de dois quadros desenhados
   *
   * Ao contrário de um tiro. Um tiro voa em linha reta e medir o rumo entre duas
   * posições desenhadas dá a resposta certa; um míssil CURVA, e a medição poria o
   * bico ligeiramente ao lado do sítio para onde ele vai mesmo. A direção viaja no
   * frame precisamente para esta diferença.
   *
   * ## O tamanho sai de uma conta só, e é a mesma da Bomba
   *
   * `(raio de corpo x 2) / MISSILE_BODY_SPAN`, com o raio de corpo do míssil -
   * 70 % do da Bomba por derivação em `pvp/balance.ts`. Um único fator nos dois
   * eixos, portanto 70 % do comprimento E 70 % da largura máxima.
   */
  private drawMissiles(frame: PvpRenderFrame, deltaMs: number): void {
    if (!this.arena) {
      return;
    }
    const diff = pvpMissileDiff(this.missiles.keys(), frame);

    for (const id of diff.remove) {
      /*
       * O mesmo quadro em que ele saiu da instantânea. Sem desvanecer, e SEM
       * EXPLOSÃO: um míssil ausente acabou - acertou, expirou, tocou uma parede
       * ou o limite - e esta cena não sabe qual. Desenhar fogo aqui seria inventar
       * um facto; a explosão chega pelo canal `pvp:blast`, quando a autoridade a
       * anuncia.
       */
      this.missiles.get(id)?.destroy();
      this.missiles.delete(id);
      /* A chama apaga-se NO MESMO quadro e volta ao pool. Nunca sobrevive ao
       * corpo: impacto, parede, limite e fim de vida largam-na toda. */
      this.releaseMissileFlame(id);
      this.missileTrailCooldown.delete(id);
    }

    const span = (pvpWorldUnits(PVP_MISSILE_BODY_RADIUS_MU) * 2) / MISSILE_BODY_SPAN;
    const length = pvpWorldUnits(PVP_MISSILE_BODY_RADIUS_MU) * 2;
    const texture = ensureMissileBodyTexture(this);
    /* Um quadro para TODOS: a chama é a mesma imagem partilhada nesta cena. */
    const flameTexture = ensureMissileFlameTexture(this, missileFlameFrame(this.time.now));
    for (const missile of [...diff.spawn, ...diff.update]) {
      const x = pvpWorldUnits(missile.x);
      const y = pvpWorldUnits(missile.y);
      /* A ÚNICA transformação de orientação, partilhada com a caça. */
      const heading = missileRotation(missile.dirX, missile.dirY);
      let sprite = this.missiles.get(missile.id);
      if (!sprite) {
        sprite = this.add
          .image(x, y, texture)
          /*
           * `NORMAL` e nunca `ADD`: o míssil é um objeto manufaturado com corpo
           * branco, e somar luz apagaria a rampa cilíndrica que a textura assou.
           * Pivota no MEIO, porque o ponto da autoridade é o centro do corpo.
           */
          .setBlendMode(Phaser.BlendModes.NORMAL)
          .setOrigin(0.5, 0.5)
          .setDepth(DEPTH.projectile);
        sprite.setScale(span, span);
        this.missiles.set(missile.id, sprite);
        /*
         * **A CHAMA: um sprite do pool, presa à traseira REAL.**
         *
         * `ADD`, porque fogo é luz - ao contrário do corpo, que é um objeto
         * branco e ficaria transparente. Um por míssil vivo, tirado de um pool
         * partilhado: nunca um emitter, e nunca uma alocação dentro de um quadro.
         */
        const flame = this.missileFlamePool.pop() ?? this.createMissileFlame();
        flame.setScale(span * MISSILE_FLAME_SCALE).setVisible(true);
        this.missileFlames.set(missile.id, flame);
      }
      sprite.setTexture(texture);
      sprite.setPosition(x, y);
      sprite.setRotation(heading);
      /*
       * O deslocamento local da tubeira, RODADO pela mesma rotação do corpo.
       *
       * É isso que a mantém sempre atrás: não há ângulo em que ela possa aparecer
       * no nariz ou de lado, porque a posição dela é a do corpo mais um vetor que
       * gira com ele.
       */
      const flame = this.missileFlames.get(missile.id);
      if (flame) {
        const back = MISSILE_FLAME_ANCHOR * length;
        flame.setTexture(flameTexture);
        flame.setRotation(heading);
        flame.setPosition(x + Math.cos(heading) * back, y + Math.sin(heading) * back);
      }
      /*
       * **A ESTEIRA: curta, fina, e nunca uma linha contínua.**
       *
       * Marcas soltas com um tempo de vida próprio. Nenhuma delas dura os nove
       * segundos - a mais longa vive `MISSILE_TRAIL_FADE_MS` - portanto o rasto
       * desenhado é o mesmo no primeiro segundo de voo e no nono.
       */
      const cooldown = (this.missileTrailCooldown.get(missile.id) ?? 0) - deltaMs;
      if (cooldown <= 0) {
        this.missileTrailCooldown.set(missile.id, MISSILE_TRAIL_INTERVAL_MS);
        this.dropMissileTrail(x, y, heading, span);
      } else {
        this.missileTrailCooldown.set(missile.id, cooldown);
      }
    }
    this.ageMissileTrail(deltaMs);
  }

  /** Uma marca de esteira, tirada do pool e devolvida quando apaga. */
  private dropMissileTrail(x: number, y: number, heading: number, scale: number): void {
    const sprite = this.missileTrailPool.pop() ?? this.createMissileTrail();
    sprite
      .setPosition(x, y)
      .setRotation(heading)
      .setScale(scale * MISSILE_TRAIL_SCALE)
      .setAlpha(0.75)
      .setVisible(true);
    this.missileTrailLive.push({ sprite, remainingMs: MISSILE_TRAIL_FADE_MS });
  }

  /** Um sprite de chama novo. Só quando o pool está vazio. */
  private createMissileFlame(): Phaser.GameObjects.Image {
    return (
      this.add
        .image(0, 0, ensureMissileFlameTexture(this, 0))
        .setBlendMode(Phaser.BlendModes.ADD)
        /* A raiz encosta ao lado direito da moldura, que é onde a tubeira está. */
        .setOrigin(1, 0.5)
        .setDepth(DEPTH.projectile)
        .setVisible(false)
    );
  }

  /** Devolve uma chama ao pool. Idempotente: um id sem chama não faz nada. */
  private releaseMissileFlame(id: number): void {
    const flame = this.missileFlames.get(id);
    if (!flame) {
      return;
    }
    flame.setVisible(false);
    this.missileFlamePool.push(flame);
    this.missileFlames.delete(id);
  }

  private createMissileTrail(): Phaser.GameObjects.Image {
    return this.add
      .image(0, 0, ensureMissileTrailTexture(this))
      .setBlendMode(Phaser.BlendModes.ADD)
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH.projectile)
      .setVisible(false);
  }

  /**
   * Envelhece as marcas e devolve ao pool as que apagaram.
   *
   * Sem tweens: são muitas, curtas, e um tween por marca seria um objeto de
   * animação por marca. Uma subtração e uma escrita de alpha fazem o mesmo.
   */
  private ageMissileTrail(deltaMs: number): void {
    let write = 0;
    for (const entry of this.missileTrailLive) {
      entry.remainingMs -= deltaMs;
      if (entry.remainingMs <= 0) {
        entry.sprite.setVisible(false);
        this.missileTrailPool.push(entry.sprite);
        continue;
      }
      entry.sprite.setAlpha(0.75 * (entry.remainingMs / MISSILE_TRAIL_FADE_MS));
      this.missileTrailLive[write] = entry;
      write += 1;
    }
    this.missileTrailLive.length = write;
  }

  /** Larga os sprites dos mísseis e recolhe a esteira. Nunca destrói o pool. */
  private dropMissiles(): void {
    for (const sprite of this.missiles.values()) {
      sprite.destroy();
    }
    this.missiles.clear();
    /* E as chamas, pela mesma razão e no mesmo instante. */
    for (const id of [...this.missileFlames.keys()]) {
      this.releaseMissileFlame(id);
    }
    this.missileTrailCooldown.clear();
    for (const entry of this.missileTrailLive) {
      entry.sprite.setVisible(false);
      this.missileTrailPool.push(entry.sprite);
    }
    this.missileTrailLive.length = 0;
  }

  private dropProjectiles(): void {
    for (const sprite of this.shots.values()) {
      sprite.destroy();
    }
    this.shots.clear();
    this.shotTrail.clear();
    /* E os mísseis, pela mesma razão: pertenciam ao mundo que foi substituído. */
    this.dropMissiles();
    // The remembered directions go too: a heading from a replaced world would
    // aim the next streak along a path that no longer exists.
    this.shotWake.clear();
    this.trailEmitter?.killAll();
  }

  /* --------------------------------------------------------------- camera */

  /**
   * Puts the local ship in the middle of the screen, and leaves it there.
   *
   * ## No second smoothing
   *
   * This used to be `Phaser.Math.Linear(..., 0.12)` per rendered frame, which
   * had two faults at once: a fixed fraction per frame converges at different
   * speeds on different monitors, and it leaves a permanent lag proportional to
   * the ship's speed - so the faster the player moved, the further from the
   * centre they sat.
   *
   * The drawn ship is ALREADY smoothed: `PvpPrediction` converges its own
   * position towards the authority over time. Smoothing the camera on top of
   * that would be a second, independent lag behind a position that is itself
   * catching up. So the camera is placed exactly on the visual position, every
   * frame, and the only thing that can move the ship off centre is the bounds.
   */
  private followCamera(local: PvpPredictedPoint | null, frame: PvpRenderFrame): void {
    /*
     * **A câmara segue o casco que foi DESENHADO** (gate V28H.V).
     *
     * Ela seguia a previsão, e isso partiu-se no dia em que o casco local
     * deixou de ser desenhado a partir dela durante um empurrão: o Arranque
     * avançava, a nave ia com a posição interpolada e a câmara ficava agarrada a
     * uma previsão que não integra - e depois saltava lá para o fim. O dono
     * viu-o: "a nave avança e depois a câmara teleporta para a nave".
     *
     * `hullPositions` tem a posição onde o casco acabou de ser posto, seja ela
     * de onde vier, e é a única resposta que nunca pode discordar do que está no
     * ecrã. A previsão fica como reserva para o quadro em que o casco ainda não
     * foi desenhado.
     */
    const drawn = this.seat === null ? undefined : this.hullPositions.get(this.seat);
    const target = drawn
      ? { x: drawn.x, y: drawn.y }
      : local
        ? { x: pvpWorldUnits(local.x), y: pvpWorldUnits(local.y) }
        : pvpCameraTarget(frame, this.seat);
    if (!target) {
      // The local ship is not in this frame yet. Stay put rather than follow
      // the opponent, which is what "the first ship in the list" would quietly
      // do.
      return;
    }
    const camera = this.cameras.main;
    camera.setZoom(this.zoom);
    /*
     * `centerOn`, and deliberately not `setScroll` with the zoom divided out.
     *
     * Phaser scales around the camera's MIDPOINT, so the world point that lands
     * on screen centre is `scroll + width / 2` whatever the zoom is - which is
     * exactly what `centerOn` sets. Dividing the half-viewport by the zoom
     * looks like the missing correction and is not: it was tried here, and at
     * zoom 0.75 on a 1920 viewport it put the hull 240 px right of centre.
     *
     * What makes that worth writing down: the measurement in
     * `movement.spec.ts` had made the same mistake, so it reported zero error
     * while the screenshot showed the ship plainly off centre. A measurement
     * that shares the code's assumption cannot check it.
     */
    camera.centerOn(target.x, target.y);
    /*
     * **O fundo segue a câmara** (gate V28H.K), e é aqui e não em `frame()`
     * porque é aqui que o scroll acabou de ser decidido: um céu actualizado
     * antes da câmara ficaria um quadro atrasado, e uma paralaxe atrasada lê-se
     * como o fundo a escorregar.
     *
     * `delta` do relógio da CENA, e nunca um tique da sala: isto é decoração, e
     * duas abas com fases diferentes vêem o mesmo jogo.
     */
    this.arenaVfx.update(this.game.loop.delta, camera.scrollX, camera.scrollY);
  }

  /* ---------------------------------------------------------------- input */

  private enableInput(): void {
    this.input.keyboard?.on('keydown', this.onKeyDown, this);
    this.input.keyboard?.on('keyup', this.onKeyUp, this);
    this.input.on('pointermove', this.onPointerMove, this);
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointerup', this.onPointerUp, this);
    this.input.on('wheel', this.onWheel, this);
    /*
     * **E o rato é seguido em toda a JANELA, não só sobre a tela** (V28H.X).
     *
     * A tela do jogo tem o HUD por cima - a barra das cartas, os botões, os
     * marcadores - e um rato que passe sobre eles deixa de existir para o
     * Phaser: os eventos são do elemento que está à frente. A mira congelava
     * onde o cursor tinha saído, e quem estava a apontar via a nave deixar de
     * virar sem nada ter mudado no jogo.
     *
     * O ouvinte da janela apanha as duas coisas, porque o evento sobe até lá
     * venha ele de onde vier. As coordenadas são convertidas para as da tela -
     * a caixa dela dá o desvio e a escala - e daí para diante é o mesmo caminho
     * de sempre.
     *
     * Fora da janela não há evento nenhum para ouvir, e a mira fica onde
     * estava: é o comportamento certo, e a nave mantém o rumo em vez de rodar
     * para um sítio que ninguém escolheu.
     */
    window.addEventListener('pointermove', this.onWindowPointerMove);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('visibilitychange', this.onVisibility);
    // A wider window sees more world, so the margin that keeps the ship centred
    // has to grow with it.
    this.scale.on('resize', this.onResize, this);
  }

  private disableInput(): void {
    this.input.keyboard?.off('keydown', this.onKeyDown, this);
    this.input.keyboard?.off('keyup', this.onKeyUp, this);
    window.removeEventListener('pointermove', this.onWindowPointerMove);
    this.input.off('pointermove', this.onPointerMove, this);
    this.input.off('pointerdown', this.onPointerDown, this);
    this.input.off('pointerup', this.onPointerUp, this);
    this.input.off('wheel', this.onWheel, this);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.scale.off('resize', this.onResize, this);
  }

  private onResize(): void {
    if (this.arena) {
      this.applyCameraBounds(this.arena);
    }
    /*
     * O céu está preso à câmara e é do tamanho do ECRÃ, portanto uma janela que
     * cresce deixaria ver o bordo dele. Ele volta a ser cortado à medida nova.
     */
    this.arenaVfx.resize(this.scale.width, this.scale.height);
  }

  private onKeyDown(event: KeyboardEvent): void {
    // `repeat` is the operating system's autorepeat, not a new press. Reading
    // it would publish the same direction dozens of times a second.
    if (!event.repeat) {
      this.intent.press(event.code);
    }
  }

  private onKeyUp(event: KeyboardEvent): void {
    this.intent.release(event.code);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    this.aimAtScreen(pointer.x, pointer.y);
  }

  /**
   * Aponta para um ponto da TELA, venha ele do Phaser ou da janela.
   *
   * Screen to world through the CAMERA, so zoom and resize cancel out.
   *
   * What survives is a direction, and the room normalises even that - which is
   * why scrolling a wheel cannot change where a player is aiming.
   */
  private aimAtScreen(x: number, y: number): void {
    const mine = this.localShipPosition();
    if (!mine) {
      return;
    }
    const world = this.cameras.main.getWorldPoint(x, y);
    this.intent.aimAt(pvpAimVector(mine.x, mine.y, world.x, world.y));
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (!pointer.leftButtonDown()) {
      return;
    }
    /*
     * A click either CHOOSES a destination or fires. Never both.
     *
     * `chooseTeleport` answers whether it consumed the click, which is what
     * makes that possible: while the target mode is armed the same button picks
     * a place, and the shot that would otherwise go with it is not sent. A
     * teleport that also fired would make choosing an attack.
     *
     * The point is converted to the SERVER's milli-units here, from the camera
     * that is already on screen, so what travels is the place the player
     * actually clicked at whatever zoom they were using.
     */
    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const chosen = this.intent.chooseTeleport({
      x: world.x * PVP_MU_PER_UNIT,
      y: world.y * PVP_MU_PER_UNIT,
    });
    if (chosen) {
      return;
    }
    // Intent only. No projectile is created here, and no damage: a client that
    // spawned its own shot would be drawing one the server never fired.
    this.intent.setFiring(true);
  }

  private onPointerUp(): void {
    this.intent.setFiring(false);
  }

  private onWheel(_pointer: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number): void {
    // Presentation only, and bounded by the constants in `controls.ts` - there
    // is no second copy of the limits in this file.
    this.zoom = zoomAfterWheel(this.zoom, dy);
  }

  private localShipPosition(): { x: number; y: number } | null {
    const hull = this.seat === null ? undefined : this.hulls.get(this.seat);
    return hull ? { x: hull.x, y: hull.y } : null;
  }

  private publishIntent(): void {
    if (!this.acceptingCommands) {
      return;
    }
    /*
     * A cadência é perguntada ANTES de a intenção ser lida (gate V28G.N).
     *
     * `current()` consome as ordens de uma pressão só - detonar, teleportar - e
     * lê-las num quadro que a cadência vai recusar é deitá-las fora. Ver a nota
     * em `PvpIntentCadence.due`.
     */
    if (!this.cadence.due(this.time.now)) {
      return;
    }
    const next = this.intent.current();
    this.cadence.mark(this.time.now);
    // The bridge carries intent. The SEQUENCE belongs to the connection and
    // starts at the server's `ack + 1`; nothing here counts.
    this.options.bridge.sendToUi('pvp:intent', { intent: next });
  }

  /**
   * Forgets everything held, and says so once.
   *
   * Blur, a hidden tab, a lost socket, a reconnection and an ending all leave
   * keys "down" as far as the browser is concerned. Publishing the cleared
   * intent immediately is what stops a ship flying into a wall while its player
   * is looking at something else.
   */
  private clearIntent(): void {
    this.intent.clear();
    this.cadence.reset();
    if (this.acceptingCommands) {
      this.options.bridge.sendToUi('pvp:intent', { intent: PVP_IDLE_INTENT });
      this.cadence.next(PVP_IDLE_INTENT, this.time.now);
    }
  }

  /* --------------------------------------------------------------- teardown */

  /**
   * Everything the duel made, released - and nothing else.
   *
   * The scene, the canvas, the renderer and the hunt all survive. React resumes
   * the hunt through its own existing path; there is nothing here that could.
   */
  private closeView(): void {
    this.clearIntent();
    this.disableInput();
    this.opened = false;
    this.acceptingCommands = false;
    this.seat = null;
    this.arena = null;

    this.dropProjectiles();
    /*
     * And the emitter itself, which `dropProjectiles` deliberately spares.
     *
     * It is a duel object on this scene's display list, so it has to go or the
     * lifecycle census would find one after a close. The TEXTURES it and the
     * bolts were drawing - `projectile-streak` and every `projectile-dash-*` -
     * are NOT destroyed: they are shared with the hunt, cached by colour in the
     * game's texture manager, and re-baking them every duel is exactly the leak
     * that the same census watches for.
     */
    this.trailEmitter?.destroy();
    this.trailEmitter = null;
    // The scale belongs to the arena that stated the radius, and the complaints
    // to the duel that could not resolve a seat.
    this.dashGauge = null;
    this.unresolvedShotOwners.clear();
    /*
     * The skins go BEFORE the hulls they decorate.
     *
     * A renderer holds the hull image it was built around and writes to it on
     * destroy - restoring nothing, but touching it - so releasing it after
     * `hull.destroy()` would be a write to a destroyed object. It also owns
     * emitters of its own, which are duel objects on this scene's display list
     * and would otherwise be exactly the leak the lifecycle census watches for.
     *
     * The ASSIGNMENTS go too, and that was a correction.
     *
     * They were kept at first, on the reasoning that a wardrobe outlives an
     * arena. The performance suite found what that actually meant: the cell
     * after a dressed one reopened already dressed, so "3x3 baseline" measured
     * two skins and reported itself as a baseline. A baseline that is silently
     * not one is the worst thing a performance gate can contain, and a lab that
     * requires its caller to remember to undress is a lab that will eventually
     * publish a wrong number. A close undresses; dressing is always explicit.
     */
    this.releaseAllSkins();
    this.skinAssignments.clear();
    /*
     * And what the AUTHORITY said, which is the other half of undressing.
     *
     * Kept, the next match's first frame would compare its profiles against the
     * previous match's answer, find them equal for anyone wearing the same
     * skin, and skip the apply that was supposed to dress them - so a second
     * duel would open with the assignments cleared above and nothing to put
     * them back. A closed arena remembers nothing about who was in it.
     */
    this.authoritativeSkins.clear();
    for (const hull of this.hulls.values()) {
      hull.destroy();
    }
    this.hulls.clear();
    this.hullHeading.clear();
    this.lastDrawnAt.clear();
    for (const pair of this.superCannons.values()) {
      for (const sprite of pair) {
        sprite.destroy();
      }
    }
    this.superCannons.clear();
    for (const battery of this.cannons.values()) {
      for (const barrel of battery) {
        barrel.destroy();
      }
    }
    this.cannons.clear();
    // The team marks go with the participants they named. A mark left behind
    // would be a ring floating over an empty arena in the next duel.
    for (const mark of this.identities.values()) {
      mark.destroy();
    }
    this.identities.clear();
    this.identityBuiltFrom.clear();
    // And the rune indicators, which belong to the effects of a match that is
    // over. The next duel draws its own from its own authority.
    for (const mark of this.runeMarks.values()) {
      mark.destroy();
    }
    this.runeMarks.clear();
    this.runeMarkBuiltFrom.clear();
    /*
     * And the runes, which belong to the arena that is closing.
     *
     * `destroy` gives back the sprites, the pool behind them, the emitters and
     * the nine baked textures - everything the renderer ever took. It is
     * idempotent, so a close after a failed open is not a second free.
     */
    this.runeRenderer?.destroy();
    this.runeRenderer = null;
    /*
     * **E as composições das cartas, por cadeira** (gate V28H.U).
     *
     * O círculo, a lança, as estrelas do Bastião e o anel de gelo. Elas eram
     * escondidas enquanto a partida corria - o ciclo destas cartas é de segundos
     * - mas uma partida FECHADA não tem cascos, e um objecto por cadeira que
     * sobrevivesse ao fecho seria exactamente o que o censo de ciclo de vida
     * procura. O `SHUTDOWN` da cena passa por aqui.
     */
    for (const fx of this.cardCircles.values()) {
      fx.destroy();
    }
    this.cardCircles.clear();
    for (const fx of this.cardLances.values()) {
      fx.destroy();
    }
    this.cardLances.clear();
    for (const fx of this.cardBastions.values()) {
      fx.destroy();
    }
    this.cardBastions.clear();
    for (const fx of this.cardMovement.values()) {
      fx.destroy();
    }
    this.cardMovement.clear();
    for (const fx of this.cardStatus.values()) {
      fx.destroy();
    }
    this.cardStatus.clear();
    for (const fx of this.malwareCasts.values()) {
      fx.destroy();
    }
    this.malwareCasts.clear();
    /* E os da Marca, pela mesma regra (gate V28H.X). */
    for (const fx of this.markCasts.values()) {
      fx.destroy();
    }
    this.markCasts.clear();
    for (const tag of this.cloakTags.values()) {
      tag.destroy();
    }
    this.cloakTags.clear();
    for (const ring of this.cardAuras.values()) {
      ring.destroy();
    }
    this.cardAuras.clear();
    this.targetRing?.destroy();
    this.targetRing = null;
    if (this.canvasCursor) {
      // The cursor belongs to the page, not to the scene: leaving a crosshair
      // behind would follow the player out of the duel.
      this.input.setDefaultCursor('default');
      this.canvasCursor = false;
    }
    // The signatures go with the sprites they described, or the next duel
    // would decide it had already built a battery that no longer exists.
    this.builtFrom.clear();
    for (const bar of this.bars.values()) {
      bar.back.destroy();
      bar.fill.destroy();
    }
    this.bars.clear();
    for (const obstacle of this.obstacles) {
      obstacle.destroy();
    }
    this.obstacles.length = 0;
    this.floor?.destroy();
    this.floor = null;
    // The backdrop goes with the arena it framed.
    this.outside?.destroy();
    this.outside = null;
    /*
     * O céu e a poeira também, e as referências dos impulsionadores com eles: as
     * PEÇAS deles já foram destruídas acima, com o resto da arena, portanto o
     * que aqui se larga é a lista que as animava. As texturas ficam - são do
     * `Phaser.Game` e servem o duelo seguinte.
     */
    this.arenaVfx.destroy();
    this.border?.destroy();
    this.border = null;

    this.view.reset();
    this.correction.reset();
    this.cadence.reset();
    this.zoom = clampPvpZoom(PVP_ZOOM_DEFAULT);

    const camera = this.cameras.main;
    camera.setZoom(1);
    camera.setScroll(0, 0);
    camera.removeBounds();
  }

  /**
   * Runs a step and turns a failure into a message rather than an exception.
   *
   * The bridge delivers synchronously, so an exception here travels back into
   * whichever React handler published the message and is reported as something
   * else entirely - which is how an arena stops for no stated reason. The same
   * answer `hunt:scene-error` gives.
   */
  private guard(stage: 'open' | 'adopt' | 'close' | 'art', run: () => void): void {
    try {
      run();
    } catch (error) {
      this.reportError(stage, error);
    }
  }

  private reportError(stage: 'open' | 'adopt' | 'close' | 'art' | 'update', error: unknown): void {
    this.options.bridge.sendToUi('pvp:scene-error', {
      stage,
      // The class and the message, never a payload: a frame can carry a match
      // id, and an error is not a place to put one.
      message: error instanceof Error ? error.message : 'unknown',
    });
  }
}

/**
 * Uma semente inteira a partir de uma chave autoritativa (gate V28G.N).
 *
 * FNV-1a de 32 bits sobre a chave `(autor, tique, alvo)`: determinística, sem
 * estado, e igual em qualquer máquina. É o que faz dois clientes a ver o mesmo
 * duelo verem os mesmos fragmentos, e é a razão pela qual `Math.random()` não
 * aparece em lado nenhum deste caminho.
 */
function hashSeed(key: string): number {
  let hash = 0x811c_9dc5;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x0100_0193) >>> 0;
  }
  return hash >>> 0;
}
